import {
	Container,
	Graphics,
	Point,
} from 'pixi.js';
import {
	DEFAULT_WORM_RADIUS,
	SEGMENT_SPACING,
	DEFAULT_SEGMENT_COUNT,
	SkinType,
	PatternType,
	hexToNumber,
	SEGMENT_SPRING_STIFFNESS,
	SEGMENT_DAMPING,
	SEGMENT_MAX_STRETCH,
	// SEGMENT_TURN_RATE - unused, using spring physics instead
	// SEGMENT_POSITION_FOLLOW_SPEED - unused, using spring physics instead
	PATH_HISTORY_LENGTH,
	PATH_SAMPLE_DISTANCE,
	PATH_SMOOTHING_TENSION,
	AccessoryType,
	getAccessoryPosition,
} from '@wooorms/shared';
import { EyeProtectionSettings, EyeProtectionLevel } from '../../ui/EyeProtectionToggle';
import type { Vector2, SegmentData, WormSkinData } from '@wooorms/shared';

/**
 * Eye expression types for emotional worm expressions
 * Based on game state: boosting, score, powerups
 */
export enum EyeExpression {
	/** Standard eyes - default state */
	NORMAL = 'normal',
	/** Angry eyes with eyebrows - when sprinting hard (5+ seconds) */
	ANGRY = 'angry',
	/** Scared wide eyes - when score is low (under 20) */
	SCARED = 'scared',
	/** Happy eyes (curved like ^_^) - when powered up */
	HAPPY = 'happy',
}

/**
 * Thresholds for eye expression changes
 */
const EXPRESSION_THRESHOLDS = {
	/** Sprint duration to trigger angry expression (seconds) */
	ANGRY_SPRINT_DURATION: 5,
	/** Score threshold below which scared expression shows */
	SCARED_SCORE_THRESHOLD: 20,
};

/**
 * Path point for tracking head movement history
 * Used for smooth path-following body movement
 */
interface PathPoint {
	x: number;
	y: number;
	/** Cumulative distance from the start of the path */
	distance: number;
}

/**
 * Circular buffer for path history with O(1) push/pop operations
 * Maintains access pattern where index 0 is newest (head position)
 */
class PathHistoryBuffer {
	private buffer: PathPoint[];
	private head: number = 0; // Points to newest element
	private _size: number = 0;
	private readonly capacity: number;

	constructor(capacity: number) {
		this.capacity = capacity;
		// Pre-allocate buffer with default objects to avoid GC
		this.buffer = new Array(capacity);
		for (let i = 0; i < capacity; i++) {
			this.buffer[i] = { x: 0, y: 0, distance: 0 };
		}
	}

	get length(): number {
		return this._size;
	}

	/**
	 * Add point at the front (newest position)
	 * O(1) operation - replaces O(n) unshift
	 */
	unshift(point: PathPoint): void {
		if (this._size < this.capacity) {
			// Buffer not full - expand from head backwards
			this.head = (this.head - 1 + this.capacity) % this.capacity;
			this._size++;
		} else {
			// Buffer full - move head back, overwriting oldest (tail)
			this.head = (this.head - 1 + this.capacity) % this.capacity;
		}

		// Copy values to avoid GC from object allocation
		const slot = this.buffer[this.head]!;
		slot.x = point.x;
		slot.y = point.y;
		slot.distance = point.distance;
	}

	/**
	 * Remove and return the oldest element
	 * O(1) operation
	 */
	pop(): PathPoint | undefined {
		if (this._size === 0) return undefined;

		const tailIndex = (this.head + this._size - 1) % this.capacity;
		const point = this.buffer[tailIndex]!;
		this._size--;
		return point;
	}

	/**
	 * Push point at the back (oldest position) - used during initialization
	 * O(1) operation
	 */
	push(point: PathPoint): void {
		if (this._size >= this.capacity) {
			// Buffer full, cannot push (would need to drop from head)
			return;
		}

		const tailIndex = (this.head + this._size) % this.capacity;
		const slot = this.buffer[tailIndex]!;
		slot.x = point.x;
		slot.y = point.y;
		slot.distance = point.distance;
		this._size++;
	}

	/**
	 * Get element at logical index (0 = newest/head)
	 * O(1) operation
	 */
	get(index: number): PathPoint | undefined {
		if (index < 0 || index >= this._size) return undefined;
		const actualIndex = (this.head + index) % this.capacity;
		return this.buffer[actualIndex];
	}

	/**
	 * Array-like index accessor for compatibility
	 * Note: This is a getter that returns element at index
	 */
	at(index: number): PathPoint | undefined {
		return this.get(index);
	}

	/**
	 * Clear the buffer
	 */
	clear(): void {
		this.head = 0;
		this._size = 0;
	}
}

// AccessoryType imported from @wooorms/shared
export { AccessoryType } from '@wooorms/shared';

/**
 * Worm renderer configuration
 */
export interface WormRendererConfig {
	/** Base color for the worm */
	color: number;
	/** Number of body segments */
	segmentCount: number;
	/** Radius of the worm body */
	radius: number;
	/** Spacing between segment points */
	segmentSpacing: number;
	/** Whether to show eyes */
	showEyes: boolean;
	/** Eye color */
	eyeColor: number;
	/** Pupil color */
	pupilColor: number;
	/** Skin data for customization */
	skin?: WormSkinData;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: WormRendererConfig = {
	color: 0x4ecdc4,
	segmentCount: DEFAULT_SEGMENT_COUNT,
	radius: DEFAULT_WORM_RADIUS,
	segmentSpacing: SEGMENT_SPACING,
	showEyes: true,
	eyeColor: 0xffffff,
	pupilColor: 0x111111,
};

/**
 * WormRenderer - Renders a worm using MeshRope for smooth body
 */
export class WormRenderer {
	/** Main container for the worm */
	public container: Container;

	/** Configuration */
	private config: WormRendererConfig;

	/** Body points array */
	private bodyPoints: Point[] = [];

	/** Body segments graphics (draws circles for each segment) */
	private bodySegments: Graphics;

	/** Second body segments graphics for double-buffering (prevents flickering) */
	private bodySegmentsBuffer: Graphics;

	/** Which buffer is currently visible (0 or 1) */
	private activeBodyBuffer: 0 | 1 = 0;

	/** Head sprite (decorative overlay) */
	private headGraphics: Graphics;

	/** Left eye sprite */
	private leftEye: Graphics;

	/** Right eye sprite */
	private rightEye: Graphics;

	/** Current head rotation */
	private headRotation: number = 0;

	/** Segment radii from server (score-based scaling) */
	private segmentRadii: number[] = [];

	/** Segment velocities for spring-damper physics (fallback for remote worms) */
	private segmentVelocities: { vx: number; vy: number }[] = [];

	/**
	 * Segment rotations - each segment's facing direction (radians)
	 * For direction-based following, each segment smoothly rotates toward the segment ahead
	 * This creates organic snake-like movement instead of rigid path-following
	 */
	private segmentRotations: number[] = [];

	/**
	 * Path history buffer - stores positions the head has traveled through
	 * Segments are positioned along this path for smooth ribbon-like motion
	 * Uses circular buffer for O(1) push/pop instead of O(n) shift
	 */
	private pathHistory: PathHistoryBuffer = new PathHistoryBuffer(PATH_HISTORY_LENGTH);

	/** Total distance traveled along the path */
	private pathTotalDistance: number = 0;

	/** Whether to use path-following (true for local player, false for remote) */
	private usePathFollowing: boolean = true;

	/** Previous head rotation for turn rate calculation */
	private prevHeadRotation: number = 0;

	/** Current turn rate (radians per second) */
	private currentTurnRate: number = 0;

	/** Delta time from last update (for physics) */
	private lastDt: number = 1 / 60;

	/** Skin data for customization */
	private skinData: WormSkinData | null = null;

	/** Whether this worm is the local player (shows golden aura) */
	private isLocalPlayer: boolean = false;

	/** Golden crown/marker above head for local player */
	private localPlayerMarker: Graphics | null = null;

	/** Golden aura/glow around the worm body for local player */
	private localPlayerAura: Graphics | null = null;

	/** Red glow indicator for enemies with same skin as local player */
	private sameSkinIndicator: Graphics | null = null;

	/** Whether this enemy has the same skin as local player */
	private hasSameSkin: boolean = false;

	/** Whether this worm has spawn protection active (ghost/transparent appearance) */
	private hasSpawnProtection: boolean = false;

	// === BOUNTY SYSTEM (LEADERBOARD RANK) VISUAL EFFECTS ===
	/** Current leaderboard rank (1-5 for bounty, 0 or >5 = no bounty) */
	private leaderboardRank: number = 0;

	/** Bounty crown/star graphics displayed above head */
	private bountyCrown: Graphics | null = null;

	/** Bounty crown colors from shared/constants BOUNTY config */
	private static readonly BOUNTY_GOLD = 0xffd700;      // #1 - Golden crown
	private static readonly BOUNTY_SILVER = 0xc0c0c0;    // #2 - Silver crown
	private static readonly BOUNTY_BRONZE = 0xcd7f32;    // #3 - Bronze crown
	private static readonly BOUNTY_STAR = 0xffff00;      // #4-5 - Yellow star

	// === LEVIATHAN (LEGENDARY) VISUAL EFFECTS ===
	/** Whether this worm is LEVIATHAN (legendary AI) */
	private isLegendary: boolean = false;

	/** Crown graphics for LEVIATHAN */
	private leviathanCrown: Graphics | null = null;

	/** Purple/gold aura glow for LEVIATHAN */
	private leviathanAura: Graphics | null = null;

	/** Dark particle trail for LEVIATHAN */
	private leviathanParticles: Graphics | null = null;

	/** Particle trail data */
	private leviathanParticleData: Array<{
		x: number;
		y: number;
		vx: number;
		vy: number;
		life: number;
		maxLife: number;
		size: number;
		color: number;
	}> = [];

	/** LEVIATHAN color scheme */
	private static readonly LEVIATHAN_PRIMARY = 0x4a0080;    // Dark purple
	private static readonly LEVIATHAN_SECONDARY = 0xffd700;  // Gold
	private static readonly LEVIATHAN_GLOW = 0x6a0dad;       // Purple glow
	private static readonly LEVIATHAN_EYE = 0xff0000;        // Red pupils

	/** Death animation state */
	private deathAnimation: {
		active: boolean;
		elapsed: number;
		duration: number;
		onComplete: (() => void) | null;
	} = {
			active: false,
			elapsed: 0,
			duration: 1000, // 1 second fade-out
			onComplete: null,
		};

	/** Animation time for skin effects (ms) */
	private animationTime: number = 0;

	/** Segment growth progress (0-1) for smooth tail growth animation */
	private segmentGrowthProgress: number[] = [];

	/** Duration for segment growth animation in seconds */
	private readonly SEGMENT_GROWTH_DURATION = 0.25;

	/**
	 * Pending segment growth queue - segments waiting to be added
	 * Used for batch growth (e.g., eating death drops) to prevent visual explosion
	 * Max 3-5 segments added per frame for smooth appearance
	 */
	private pendingGrowth: number = 0;

	/** Maximum segments to add per frame (prevents visual explosion) */
	private readonly MAX_SEGMENTS_PER_FRAME = 4;

	/** Cached segment colors to avoid recalculation every frame */
	private cachedColors: number[] = [];

	/** Animation time when colors were last cached */
	private colorCacheTime: number = 0;

	/** How often to update animated pattern colors (ms) - 20fps instead of 60fps */
	private readonly COLOR_CACHE_INTERVAL: number = 50;

	/**
	 * LOD (Level of Detail) level for this worm
	 * 0 = Closest/Full quality (all effects)
	 * 1 = Medium distance (reduced effects)
	 * 2 = Far distance (minimal effects)
	 */
	private lodLevel: 0 | 1 | 2 = 0;

	// === PERFORMANCE OPTIMIZATION: Dirty flag system ===
	/** Whether body segments need to be redrawn */
	private bodyDirty: boolean = true;

	/** Last head radius - to detect when head/eyes need redrawing */
	private lastHeadRadius: number = 0;

	/** Culling bounds for per-segment visibility (set by setCullingBounds) */
	private cullingBounds: { left: number; right: number; top: number; bottom: number } | null = null;

	/** Time since last body redraw (ms) */
	private timeSinceBodyRedraw: number = 0;

	/** Minimum interval between body redraws (ms) - caps at 30fps for body drawing */
	private readonly MIN_BODY_REDRAW_INTERVAL: number = 33;

	/** Accessory type */
	private accessoryType: AccessoryType = AccessoryType.NONE;

	/** Accessory graphics */
	private accessoryGraphics: Graphics | null = null;

	// === SMART EYE MOVEMENT ===
	/** Current pupil offset (normalized -1 to 1) */
	private pupilOffsetX: number = 0.3;
	private pupilOffsetY: number = 0;

	/** Target pupil offset for smooth interpolation */
	private targetPupilOffsetX: number = 0.3;
	private targetPupilOffsetY: number = 0;

	/** Timer for random look-around behavior (ms) */
	private lookAroundTimer: number = 0;

	/** Next time to change random look direction (ms) */
	private nextLookChange: number = 2000;

	/** Pupil interpolation speed (per second) */
	private readonly PUPIL_LERP_SPEED: number = 8;

	// === PERFORMANCE: Eye animation throttling ===
	/** Frame counter for eye animation throttling */
	private eyeAnimationFrame: number = 0;

	/** How often to update eyes for non-local worms (every N frames) */
	private readonly EYE_ANIMATION_THROTTLE: number = 4;

	// === SPRINT/BOOST VISUAL EFFECTS ===
	/** Whether the worm is currently boosting/sprinting */
	private isBoosting: boolean = false;

	/** Sprint glow graphics (orange glow around front segments) */
	private sprintGlow: Graphics | null = null;

	/** Speed lines graphics (motion trail behind worm) */
	private speedLines: Graphics | null = null;

	/** Speed line particle data for trail effect */
	private speedLineParticles: Array<{
		x: number;
		y: number;
		vx: number;
		vy: number;
		life: number;
		maxLife: number;
		length: number;
		alpha: number;
	}> = [];

	/** Sprint visual intensity (0-1, ramps up over time) */
	private sprintIntensity: number = 0;

	/** Sprint colors for visual effects - subtle white/brightness based */
	private static readonly SPRINT_GLOW_COLOR = 0xffffff;   // White glow (neutral, works with all skins)
	private static readonly SPRINT_LINE_COLOR = 0xcccccc;   // Light gray speed lines

	// === KILL STREAK VISUAL EFFECTS ===
	/** Current kill streak tier ('', 'RAMPAGE', 'UNSTOPPABLE', 'GODLIKE', 'LEGENDARY') */
	private killStreakTier: string = '';

	/** Kill streak flame trail graphics */
	private killStreakFlames: Graphics | null = null;

	/** Kill streak pulsing glow graphics */
	private killStreakGlow: Graphics | null = null;

	/** Kill streak golden aura graphics (for LEGENDARY tier) */
	private killStreakAura: Graphics | null = null;

	/** Kill streak particle storm data */
	private killStreakParticles: Array<{
		x: number;
		y: number;
		vx: number;
		vy: number;
		life: number;
		maxLife: number;
		size: number;
		color: number;
	}> = [];

	/** Kill streak colors */
	private static readonly STREAK_RAMPAGE = 0xff6b35;     // Orange flame
	private static readonly STREAK_UNSTOPPABLE = 0xff4500; // Red-orange
	private static readonly STREAK_GODLIKE = 0xffd700;     // Gold
	private static readonly STREAK_LEGENDARY = 0xff00ff;   // Magenta/purple

	// === EYE EXPRESSION SYSTEM ===
	/** Current eye expression */
	private currentExpression: EyeExpression = EyeExpression.NORMAL;

	/** Target expression (for smooth transitions) */
	private targetExpression: EyeExpression = EyeExpression.NORMAL;

	/** Expression transition progress (0-1) */
	private expressionTransition: number = 1;

	/** Expression transition speed (per second) */
	private readonly EXPRESSION_TRANSITION_SPEED: number = 4;

	/** Left eyebrow graphics (for angry expression) */
	private leftEyebrow: Graphics | null = null;

	/** Right eyebrow graphics (for angry expression) */
	private rightEyebrow: Graphics | null = null;

	/** Game state for expression calculation (stored for debugging/future use) */
	private _expressionState: {
		sprintDuration: number;
		score: number;
		hasPowerup: boolean;
	} = {
			sprintDuration: 0,
			score: 0,
			hasPowerup: false,
		};

	constructor(config: Partial<WormRendererConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };

		// Store skin data
		if (config.skin) {
			this.skinData = config.skin;
			// Update color from skin if not explicitly provided
			if (!config.color && config.skin.primaryColor) {
				this.config.color = hexToNumber(config.skin.primaryColor);
			}
		}

		// Create main container
		this.container = new Container();
		this.container.cullable = true;
		this.container.label = 'worm';
		// Performance: Disable interactive events (we don't need click/hover on worms)
		this.container.eventMode = 'none';
		this.container.interactiveChildren = false;

		// Create graphics for body segments, head and eyes
		// Using double-buffering to prevent flickering (draw to hidden buffer, then swap)
		this.bodySegments = new Graphics();
		this.bodySegmentsBuffer = new Graphics();
		this.bodySegmentsBuffer.visible = false; // Start with buffer hidden
		this.headGraphics = new Graphics();
		this.leftEye = new Graphics();
		this.rightEye = new Graphics();

		// Initialize the worm
		this.init();
	}

	/**
	 * Initialize the worm renderer
	 */
	private init(): void {
		// Initialize body points in a line
		this.bodyPoints = [];
		this.segmentVelocities = [];
		this.segmentRotations = [];
		this.segmentGrowthProgress = [];
		for (let i = 0; i < this.config.segmentCount; i++) {
			// Body extends BEHIND the head (negative x direction for worm facing right)
			this.bodyPoints.push(new Point(-i * this.config.segmentSpacing, 0));
			this.segmentVelocities.push({ vx: 0, vy: 0 });
			// Initialize rotation pointing toward head (facing right = angle 0)
			this.segmentRotations.push(0);
			this.segmentGrowthProgress.push(1.0); // Start fully grown
		}

		// Initialize path history for smooth body following
		this.initializePathHistory();

		// Add body segments graphics (circles drawn from tail to head)
		// Both buffers added - only one visible at a time (double-buffering)
		this.container.addChild(this.bodySegments);
		this.container.addChild(this.bodySegmentsBuffer);

		// Draw head decoration
		this.drawHead();
		this.container.addChild(this.headGraphics);

		// Draw eyes
		if (this.config.showEyes) {
			this.drawEyes();
			this.container.addChild(this.leftEye);
			this.container.addChild(this.rightEye);
		}

		// Initial draw of body segments
		this.drawBodySegments();
	}

	/**
	 * Draw all body segments as circles with enhanced visuals
	 * Drawn from tail to head so head overlaps tail
	 * Uses server-provided radii (score-based scaling)
	 * Applies smooth growth animation for new segments
	 * Uses double-buffering to prevent flickering
	 */
	private drawBodySegments(): void {
		// Double-buffering: draw to the inactive buffer, then swap visibility
		// This prevents flickering caused by clear() being visible before draw completes
		const g = this.activeBodyBuffer === 0 ? this.bodySegmentsBuffer : this.bodySegments;
		g.clear();

		const { radius, color } = this.config;
		const segmentCount = this.bodyPoints.length;

		// Get colors for the segments based on skin
		const colors = this.getSegmentColors();

		// Check if this is an animated/glowing skin
		const isGlowingSkin = this.hasAnimatedPattern();
		const protectionLevel = EyeProtectionSettings.getInstance().getLevel();
		const eyeProtectionEnabled = protectionLevel > EyeProtectionLevel.OFF;

		// === PERFORMANCE OPTIMIZATION: LOD + Segment Count Based Skipping ===
		// Combines distance-based LOD with segment count for aggressive culling
		// Maintains visual quality by increasing segment size to compensate
		let segmentStep = 1;
		let effectiveLOD = this.lodLevel;

		// LOD-based multipliers for segment stepping
		// LOD2 (far) is more aggressive, LOD0 (close) preserves detail
		const lodStepMultiplier = effectiveLOD === 2 ? 2 : effectiveLOD === 1 ? 1.5 : 1;

		if (segmentCount > 200) {
			// Medium worm: base step 2, adjusted by LOD
			segmentStep = Math.round(2 * lodStepMultiplier);
			// Force minimum LOD 1 for animated skins
			if (isGlowingSkin && effectiveLOD < 1) effectiveLOD = 1;
		}
		if (segmentCount > 500) {
			// Large worm: base step 3, adjusted by LOD
			segmentStep = Math.round(3 * lodStepMultiplier);
			// Force LOD 2 for animated skins (simple rendering)
			if (isGlowingSkin) effectiveLOD = 2;
		}
		if (segmentCount > 1000) {
			// Very large worm: base step 5, adjusted by LOD
			segmentStep = Math.round(5 * lodStepMultiplier);
			effectiveLOD = 2; // Always use simplest LOD
		}
		if (segmentCount > 3000) {
			// Huge worm: base step 8, adjusted by LOD
			segmentStep = Math.round(8 * lodStepMultiplier);
			effectiveLOD = 2;
		}
		if (segmentCount > 6000) {
			// Massive worm: base step 12, adjusted by LOD
			segmentStep = Math.round(12 * lodStepMultiplier);
			effectiveLOD = 2;
		}
		if (segmentCount > 10000) {
			// Enormous worm: base step 18, adjusted by LOD
			segmentStep = Math.round(18 * lodStepMultiplier);
			effectiveLOD = 2;
		}
		if (segmentCount > 20000) {
			// Giant worm: base step 30, adjusted by LOD
			segmentStep = Math.round(30 * lodStepMultiplier);
			effectiveLOD = 2;
		}
		if (segmentCount > 35000) {
			// Colossal worm: base step 50, adjusted by LOD
			segmentStep = Math.round(50 * lodStepMultiplier);
			effectiveLOD = 2;
		}
		if (segmentCount > 50000) {
			// Maximum scale: base step 75, adjusted by LOD
			segmentStep = Math.round(75 * lodStepMultiplier);
			effectiveLOD = 2;
		}

		// CRITICAL FIX: Radius multiplier must scale with segment step to prevent gaps!
		// Formula: To fill gap between drawn segments, need 2*radius*mult >= step * spacing
		// With spacing = 1.33*radius: mult >= step * 0.665
		// Use 0.75 for slight overlap to prevent visible gaps
		const radiusMultiplier = segmentStep * 0.75;

		// Draw from tail to head (so head segments render on top)
		for (let i = segmentCount - 1; i >= 1; i -= (i > 20 ? segmentStep : 1)) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			// === PER-SEGMENT CULLING ===
			// Skip segments that are outside the visible viewport (with padding for segment radius)
			if (this.cullingBounds) {
				const segRadius = (this.segmentRadii[i] ?? radius) * radiusMultiplier * 1.5; // Extra padding for effects
				if (point.x + segRadius < this.cullingBounds.left ||
					point.x - segRadius > this.cullingBounds.right ||
					point.y + segRadius < this.cullingBounds.top ||
					point.y - segRadius > this.cullingBounds.bottom) {
					continue; // Skip this segment - it's off-screen
				}
			}

			// Get growth progress for this segment (defaults to 1 if not set)
			const growthProgress = this.segmentGrowthProgress[i] ?? 1.0;

			// Skip segments that haven't started growing yet
			if (growthProgress <= 0) continue;

			// Use server-provided radius (score-based), fallback to config radius with taper
			let baseSegmentRadius: number;
			const serverRadius = this.segmentRadii[i];
			if (serverRadius !== undefined && serverRadius > 0) {
				baseSegmentRadius = serverRadius;
			} else {
				// Fallback: calculate locally (for initial render before server data)
				const progress = i / Math.max(1, segmentCount - 1);
				baseSegmentRadius = radius * (1.0 - progress * 0.3); // 1.0 at head, 0.7 at tail
			}

			// Apply growth progress with easing (ease-out cubic for smooth pop-in)
			const easedGrowth = 1 - Math.pow(1 - growthProgress, 3);
			// Apply radius multiplier for segment skipping compensation
			const segmentRadius = baseSegmentRadius * easedGrowth * radiusMultiplier;

			// Get color for this segment (with sprint color shift if boosting)
			const baseSegmentColor = colors[i % colors.length] ?? color;
			const segmentColor = this.getSprintAdjustedColor(baseSegmentColor, i);

			// Extract RGB components for effects
			const r = (segmentColor >> 16) & 0xff;
			const g_comp = (segmentColor >> 8) & 0xff;
			const b = segmentColor & 0xff;

			// === LOD-BASED RENDERING ===
			// LOD 0 (close): All effects (7 draw calls per segment)
			// LOD 1 (medium): Shadow, body, ring, single highlight (4 draw calls)
			// LOD 2 (far): Shadow and body only (2 draw calls)
			// Note: effectiveLOD may be higher than lodLevel for large worms (performance optimization)

			// 1. Outer glow for glowing skins - LOD 0 only, skip when eye protection enabled
			if (effectiveLOD === 0 && isGlowingSkin && growthProgress >= 0.5 && !eyeProtectionEnabled) {
				const glowAlpha = 0.15 * easedGrowth;
				g.circle(point.x, point.y, segmentRadius * 1.3);
				g.fill({ color: segmentColor, alpha: glowAlpha });
			}

			// 2. Soft shadow (offset down-right) - All LOD levels
			g.circle(point.x + segmentRadius * 0.1, point.y + segmentRadius * 0.15, segmentRadius);
			g.fill({ color: 0x000000, alpha: 0.25 * easedGrowth });

			// 3. Main body - All LOD levels
			const darkerEdge = ((Math.floor(r * 0.7) << 16) | (Math.floor(g_comp * 0.7) << 8) | Math.floor(b * 0.7));
			g.circle(point.x, point.y, segmentRadius);
			g.fill(segmentColor);

			// Skip detail effects for LOD 2 (far distance)
			if (effectiveLOD === 2) continue;

			// 4. Inner darker ring (creates depth) - LOD 0 and 1
			g.circle(point.x, point.y, segmentRadius * 0.85);
			g.stroke({ color: darkerEdge, width: segmentRadius * 0.1, alpha: 0.3 });

			// Calculate direction from this segment to the previous one (toward head)
			const prevPoint = this.bodyPoints[i - 1];
			let dirX = 0, dirY = -1; // Default direction (up)
			if (prevPoint) {
				const dx = prevPoint.x - point.x;
				const dy = prevPoint.y - point.y;
				const length = Math.sqrt(dx * dx + dy * dy);
				if (length > 0.001) {
					dirX = dx / length;
					dirY = dy / length;
				}
			}

			// Perpendicular direction (for side highlights)
			const perpX = -dirY;
			const perpY = dirX;

			// 5. Snake-like highlight - LOD 0: 3 circles, LOD 1: 1 circle
			const highlightOffset = segmentRadius * 0.3;
			const baseHlX = point.x + perpX * highlightOffset;
			const baseHlY = point.y + perpY * highlightOffset;

			if (effectiveLOD === 0) {
				// Full detail: Draw 3 overlapping circles for elongated effect
				const highlightSpread = segmentRadius * 0.5;
				for (let h = -1; h <= 1; h++) {
					const spreadFactor = h * highlightSpread * 0.4;
					const hlX = baseHlX + dirX * spreadFactor;
					const hlY = baseHlY + dirY * spreadFactor;
					const hlRadius = segmentRadius * (0.22 - Math.abs(h) * 0.04);
					const hlAlpha = (0.18 - Math.abs(h) * 0.05) * easedGrowth;
					g.circle(hlX, hlY, hlRadius);
					g.fill({ color: 0xffffff, alpha: hlAlpha });
				}
			} else {
				// LOD 1: Single center highlight
				g.circle(baseHlX, baseHlY, segmentRadius * 0.22);
				g.fill({ color: 0xffffff, alpha: 0.18 * easedGrowth });
			}

			// Skip remaining detail effects for LOD 1
			if (effectiveLOD === 1) continue;

			// 6. Secondary smaller highlight near the front of segment - LOD 0 only
			const smallHlX = perpX * segmentRadius * 0.2 + dirX * segmentRadius * 0.1;
			const smallHlY = perpY * segmentRadius * 0.2 + dirY * segmentRadius * 0.1;
			g.circle(
				point.x + smallHlX,
				point.y + smallHlY,
				segmentRadius * 0.12
			);
			g.fill({ color: 0xffffff, alpha: 0.3 * easedGrowth });

			// 7. Bottom rim light - LOD 0 only
			const rimX = -perpX * segmentRadius * 0.2;
			const rimY = -perpY * segmentRadius * 0.2;
			g.circle(
				point.x + rimX,
				point.y + rimY,
				segmentRadius * 0.15
			);
			g.fill({ color: 0xffffff, alpha: 0.06 * easedGrowth });
		}

		// Double-buffer swap: make the newly drawn buffer visible, hide the old one
		if (this.activeBodyBuffer === 0) {
			this.bodySegmentsBuffer.visible = true;
			this.bodySegments.visible = false;
			this.activeBodyBuffer = 1;
		} else {
			this.bodySegments.visible = true;
			this.bodySegmentsBuffer.visible = false;
			this.activeBodyBuffer = 0;
		}
	}

	/**
	 * Update animation time for skin effects
	 * Call this each frame with delta time in seconds
	 * OPTIMIZED: Uses dirty flag and throttling to reduce draw calls
	 */
	updateSkinAnimation(dt: number): void {
		const dtMs = dt * 1000;
		this.animationTime += dtMs;
		this.timeSinceBodyRedraw += dtMs;

		// Update sprint visual effects (glow, speed lines, color shift)
		// Handle both fade-in (when boosting) and fade-out (when stopped)
		if (this.isBoosting) {
			this.updateSprintEffects(dt);
			// Force redraw when boosting for smooth color transition
			this.bodyDirty = true;
		} else if (this.sprintIntensity > 0) {
			// Fade out sprint effects when not boosting
			this.updateSprintFadeOut(dt);
			// Force redraw during fade-out for smooth color transition
			this.bodyDirty = true;
		}

		// Update dynamic pupil movement
		// Performance: Throttle eye updates for non-local worms (every 4 frames)
		this.eyeAnimationFrame++;
		const shouldUpdateEyes = this.isLocalPlayer ||
			(this.eyeAnimationFrame % this.EYE_ANIMATION_THROTTLE === 0);

		if (this.config.showEyes && shouldUpdateEyes) {
			const prevPupilX = this.pupilOffsetX;
			const prevPupilY = this.pupilOffsetY;
			this.updatePupils(dt * (this.isLocalPlayer ? 1 : this.EYE_ANIMATION_THROTTLE));

			// Update eye expression transitions
			this.updateExpressionTransition(dt * (this.isLocalPlayer ? 1 : this.EYE_ANIMATION_THROTTLE));

			// Redraw eyes if pupil position changed significantly
			const pupilMoved = Math.abs(this.pupilOffsetX - prevPupilX) > 0.01 ||
				Math.abs(this.pupilOffsetY - prevPupilY) > 0.01;
			if (pupilMoved) {
				this.drawEyes();
			}
		}

		// Process growth queue (add queued segments, max 4 per frame)
		this.processGrowthQueue();

		// Update segment growth progress for smooth tail growth
		let hasGrowingSegments = false;
		const growthIncrement = dt / this.SEGMENT_GROWTH_DURATION;
		for (let i = 0; i < this.segmentGrowthProgress.length; i++) {
			const currentProgress = this.segmentGrowthProgress[i] ?? 1.0;
			if (currentProgress < 1.0) {
				this.segmentGrowthProgress[i] = Math.min(1.0, currentProgress + growthIncrement);
				hasGrowingSegments = true;
			}
		}

		// Determine if we should redraw body
		const isAnimatedSkin = this.skinData?.type === SkinType.PATTERN && this.hasAnimatedPattern();
		const segmentCount = this.bodyPoints.length;

		// Calculate the appropriate redraw interval
		let redrawInterval = this.MIN_BODY_REDRAW_INTERVAL; // 30fps baseline for body
		if (isAnimatedSkin) {
			// Animated skins: reduce animation fps based on worm size
			if (segmentCount > 1000) {
				redrawInterval = 150; // ~7fps for very large animated worms
			} else if (segmentCount > 500) {
				redrawInterval = 100; // 10fps for large animated worms
			} else {
				redrawInterval = this.COLOR_CACHE_INTERVAL; // 20fps for normal animated worms
			}
		} else if (segmentCount > 500) {
			// Large non-animated worms: can use lower fps since no animation
			redrawInterval = 50; // 20fps
		}

		// Force redraw if growing segments need immediate visual feedback
		if (hasGrowingSegments) {
			redrawInterval = Math.min(redrawInterval, 33); // At least 30fps during growth
		}

		// Only redraw if dirty and enough time has passed
		if (this.bodyDirty && this.timeSinceBodyRedraw >= redrawInterval) {
			this.drawBodySegments();
			this.bodyDirty = false;
			this.timeSinceBodyRedraw = 0;
		}
	}

	/**
	 * Set culling bounds for per-segment visibility culling
	 * Call this before updateSkinAnimation with the current camera bounds
	 * @param bounds The visible area in world coordinates
	 */
	setCullingBounds(bounds: { left: number; right: number; top: number; bottom: number }): void {
		this.cullingBounds = bounds;
	}

	/**
	 * Force immediate body redraw (use sparingly)
	 * Useful when segment count changes or skin changes
	 */
	forceBodyRedraw(): void {
		this.bodyDirty = true;
		this.timeSinceBodyRedraw = this.MIN_BODY_REDRAW_INTERVAL; // Allow immediate redraw
		this.drawBodySegments();
		this.bodyDirty = false;
		this.timeSinceBodyRedraw = 0;
	}

	/**
	 * Check if the current skin has an animated pattern
	 */
	private hasAnimatedPattern(): boolean {
		if (!this.skinData?.pattern) return false;
		const animatedPatterns = [
			// Original animated patterns
			PatternType.NEON,
			PatternType.ELECTRIC,
			PatternType.METALLIC,
			PatternType.LAVA,
			PatternType.TOXIC,
			PatternType.PLASMA,
			PatternType.HOLOGRAPHIC,
			PatternType.VOID,
			PatternType.CYBER,
			PatternType.CRYSTAL,
			PatternType.GLITCH,
			PatternType.RAINBOW,
			PatternType.GALAXY,
			// NEW: Retro arcade animated patterns
			PatternType.FIRE,
			PatternType.OCEAN,
			PatternType.AURORA,
			PatternType.DISCO,
			PatternType.MATRIX,
			PatternType.PULSE,
			PatternType.SCALES,
			PatternType.LIGHTNING,
			PatternType.CHROMATIC,
			PatternType.EMBER,
			PatternType.HYPNOTIC,
			PatternType.PIXELATE,
		];
		return animatedPatterns.includes(this.skinData.pattern as PatternType);
	}

	/**
	 * Get array of colors for segments based on skin type
	 * Uses caching to reduce expensive color calculations:
	 * - Static patterns: cache indefinitely (until segment count changes)
	 * - Animated patterns: cache for COLOR_CACHE_INTERVAL ms (20fps updates instead of 60fps)
	 */
	private getSegmentColors(): number[] {
		const { color } = this.config;
		const segmentCount = this.bodyPoints.length;

		// Check if cache is valid
		const isAnimated = this.hasAnimatedPattern();
		const cacheValid = this.cachedColors.length === segmentCount &&
			(!isAnimated || (this.animationTime - this.colorCacheTime < this.COLOR_CACHE_INTERVAL));

		if (cacheValid) {
			return this.cachedColors;
		}

		// Update cache time for animated patterns
		if (isAnimated) {
			this.colorCacheTime = this.animationTime;
		}

		// Calculate colors
		let colors: number[];

		if (!this.skinData) {
			// Apply gradient darkening for default color
			colors = this.generateGradientColors(color, segmentCount);
		} else {
			const skin = this.skinData;

			switch (skin.type) {
				case SkinType.COLOR:
					// Apply gradient darkening from head to tail
					colors = this.generateGradientColors(hexToNumber(skin.primaryColor), segmentCount);
					break;

				case SkinType.PATTERN:
					colors = this.getPatternColors(skin, segmentCount);
					break;

				case SkinType.FLAG:
					colors = this.generateFlagColors(skin.colors || [], segmentCount);
					break;

				default:
					colors = [color];
			}
		}

		// Cache and return
		this.cachedColors = colors;
		return colors;
	}

	/**
	 * Generate gradient colors from head (bright) to tail (darker)
	 */
	private generateGradientColors(baseColor: number, segmentCount: number): number[] {
		const colors: number[] = [];
		const r = (baseColor >> 16) & 0xff;
		const g = (baseColor >> 8) & 0xff;
		const b = baseColor & 0xff;

		for (let i = 0; i < segmentCount; i++) {
			const t = i / Math.max(1, segmentCount - 1);
			const factor = 1 - t * 0.4; // Darken towards tail
			const newR = Math.floor(r * factor);
			const newG = Math.floor(g * factor);
			const newB = Math.floor(b * factor);
			colors.push((newR << 16) | (newG << 8) | newB);
		}

		return colors;
	}

	/**
	 * Generate colors for flag skins - distributes flag stripes evenly across the worm body
	 * Each flag color covers an equal portion of segments for a proper flag appearance
	 */
	private generateFlagColors(flagColors: string[], segmentCount: number): number[] {
		if (flagColors.length === 0) {
			return [0xffffff];
		}

		const colors: number[] = [];
		const numStripes = flagColors.length;

		// Convert hex colors to numbers
		const colorValues = flagColors.map(c => hexToNumber(c));

		// Distribute colors evenly across segments with slight gradient darkening
		for (let i = 0; i < segmentCount; i++) {
			// Calculate which stripe this segment belongs to
			const stripeIndex = Math.min(
				Math.floor((i / segmentCount) * numStripes),
				numStripes - 1
			);

			// Get the base color for this stripe
			const baseColor = colorValues[stripeIndex] ?? colorValues[0] ?? 0xffffff;

			// Apply subtle gradient darkening from head to tail for depth
			const t = i / Math.max(1, segmentCount - 1);
			const darkenFactor = 1 - t * 0.25; // Darken slightly towards tail

			const r = Math.floor(((baseColor >> 16) & 0xff) * darkenFactor);
			const g = Math.floor(((baseColor >> 8) & 0xff) * darkenFactor);
			const b = Math.floor((baseColor & 0xff) * darkenFactor);

			colors.push((r << 16) | (g << 8) | b);
		}

		return colors;
	}

	/**
	 * Get colors for pattern-based skins with animation support
	 */
	private getPatternColors(skin: WormSkinData, segmentCount: number): number[] {
		const primary = hexToNumber(skin.primaryColor);
		const secondary = skin.secondaryColor ? hexToNumber(skin.secondaryColor) : 0xffffff;
		const colors: number[] = [];

		for (let i = 0; i < segmentCount; i++) {
			const t = i / Math.max(1, segmentCount - 1);
			const color = this.getAnimatedPatternColor(skin, i, segmentCount, t, primary, secondary);
			colors.push(color);
		}

		return colors;
	}

	/**
	 * Get animated color for a specific segment based on pattern type
	 * When Eye Protection mode is enabled, returns static gradient colors
	 */
	private getAnimatedPatternColor(
		skin: WormSkinData,
		segmentIndex: number,
		_segmentCount: number,
		t: number,
		primary: number,
		secondary: number
	): number {
		const protectionLevel = EyeProtectionSettings.getInstance().getLevel();

		// Strong Eye Protection: use simple static patterns (no animation at all)
		if (protectionLevel === EyeProtectionLevel.STRONG) {
			// Simple alternating or gradient pattern based on original pattern type
			const pattern = skin.pattern;
			// Non-animated patterns stay the same
			if (pattern === PatternType.STRIPED || pattern === PatternType.CHECKER ||
				pattern === PatternType.TIGER || pattern === PatternType.ZEBRA) {
				return segmentIndex % 2 === 0 ? primary : secondary;
			}
			if (pattern === PatternType.SPOTTED || pattern === PatternType.ZIGZAG) {
				return segmentIndex % 3 === 0 ? secondary : primary;
			}
			// All animated patterns become a simple static gradient
			return this.interpolateColors(primary, secondary, t);
		}

		switch (skin.pattern) {
			case PatternType.STRIPED:
			case PatternType.CHECKER:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.SPOTTED:
				return segmentIndex % 3 === 0 ? secondary : primary;

			case PatternType.ZIGZAG:
				return (segmentIndex + Math.floor(segmentIndex / 2)) % 2 === 0 ? primary : secondary;

			case PatternType.TIGER:
				// Thick primary with thin secondary stripes
				return segmentIndex % 3 !== 2 ? primary : secondary;

			case PatternType.ZEBRA:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.RAINBOW:
				// Animated rainbow that flows along the body (slower)
				const rainbowOffset = this.animationTime / 3000;
				return this.getRainbowColor((t + rainbowOffset) % 1);

			case PatternType.GRADIENT:
			case PatternType.SUNSET:
				return this.interpolateColors(primary, secondary, t);

			case PatternType.PLASMA:
				// Swirling plasma effect with multiple overlapping waves (slower)
				const plasma1 = Math.sin(this.animationTime / 500 + segmentIndex * 0.3);
				const plasma2 = Math.sin(this.animationTime / 700 + segmentIndex * 0.5 + 2);
				const plasma3 = Math.sin(this.animationTime / 400 + t * 6);
				const plasmaIntensity = (plasma1 + plasma2 + plasma3) / 3 * 0.5 + 0.5;
				return this.interpolateColors(primary, secondary, plasmaIntensity);

			case PatternType.HOLOGRAPHIC:
				// Shifting rainbow with metallic shimmer (slower)
				const holoHue = (t * 360 + this.animationTime / 20) % 360;
				const holoShimmer = Math.sin(this.animationTime / 400 + segmentIndex * 0.8) * 0.3 + 0.7;
				const holoColor = this.hslToRgb(holoHue, 80, 60 * holoShimmer);
				return (holoColor.r << 16) | (holoColor.g << 8) | holoColor.b;

			case PatternType.VOID:
				// Dark pulsing void with occasional bright sparks (slower)
				const voidPulse = Math.sin(this.animationTime / 800 + segmentIndex * 0.4) * 0.15 + 0.2;
				const voidSpark = Math.sin(this.animationTime / 200 + segmentIndex * 3) > 0.95;
				if (voidSpark) {
					return 0xaa44ff; // Bright purple spark
				}
				return this.applyBrightness(primary, voidPulse);

			case PatternType.CYBER:
				// Neon circuit-like pattern with data flow (slower)
				const cyberFlow = Math.floor((this.animationTime / 250 + segmentIndex) % 8);
				const cyberActive = cyberFlow < 3;
				if (segmentIndex % 4 === 0) {
					// Node points - bright cyan
					return cyberActive ? 0x00ffff : 0x004444;
				}
				// Data flowing between nodes
				const cyberIntensity = cyberActive ? 0.9 : 0.3;
				return this.applyBrightness(primary, cyberIntensity);

			case PatternType.CRYSTAL:
				// Sparkling crystal with light refraction (slower)
				const crystalFacet = (segmentIndex + Math.floor(this.animationTime / 600)) % 5;
				const crystalShine = Math.sin(this.animationTime / 500 + segmentIndex * 1.2) * 0.4 + 0.6;
				if (crystalFacet === 0) {
					// Bright sparkle
					return this.interpolateColors(primary, 0xffffff, crystalShine);
				}
				// Crystal body with subtle color shift
				return this.interpolateColors(primary, secondary, crystalShine * 0.5);

			case PatternType.GLITCH:
				// Digital glitch effect with random color shifts (slower)
				const glitchSeed = Math.floor(this.animationTime / 300);
				const glitchRandom = Math.sin(glitchSeed * segmentIndex * 12.9898) * 43758.5453 % 1;
				if (glitchRandom > 0.85) {
					// Color channel split effect
					const glitchColors = [0xff0000, 0x00ff00, 0x0000ff, 0xff00ff, 0x00ffff];
					return glitchColors[Math.floor(glitchRandom * 5) % glitchColors.length] ?? primary;
				}
				// Base with occasional flicker
				const glitchFlicker = Math.sin(this.animationTime / 120 + segmentIndex) > 0.7 ? 1.2 : 0.8;
				return this.applyBrightness(primary, glitchFlicker);

			case PatternType.NEON:
				return this.applyNeonEffect(primary, t);

			case PatternType.ELECTRIC:
				// Electric pulsing effect (slower)
				const electricPulse = Math.sin(this.animationTime / 350 + segmentIndex) * 0.3 + 0.7;
				if (segmentIndex % 3 === 0) {
					return secondary;
				}
				return this.applyBrightness(primary, electricPulse);

			case PatternType.METALLIC:
				// Metallic shine wave (slower)
				const wave = Math.sin(this.animationTime / 800 + t * 4) * 0.5 + 0.5;
				return this.interpolateColors(primary, secondary, wave);

			case PatternType.GALAXY:
				// Cosmic galaxy with twinkling stars and nebula swirls (slower)
				const galaxySwirl = Math.sin(this.animationTime / 1000 + t * 3) * 0.3 + 0.5;
				const starTwinkle = Math.sin(this.animationTime / 350 + segmentIndex * 2.7) > 0.85;
				if (starTwinkle) {
					return 0xffffff; // Twinkling star
				}
				// Nebula color shift
				return this.interpolateColors(primary, secondary, galaxySwirl);

			case PatternType.FROST:
				// Cool icy gradient with white sparkles
				const frostBase = this.interpolateColors(primary, secondary, t);
				return segmentIndex % 3 === 0 ? 0xffffff : frostBase;

			case PatternType.LAVA:
				// Hot flowing gradient with movement (slower)
				const lavaT = Math.abs((t + Math.sin(this.animationTime / 600 + segmentIndex * 0.5) * 0.2) % 1);
				return this.interpolateColors(primary, secondary, lavaT);

			case PatternType.TOXIC:
				// Glowing toxic pulse (slower)
				const toxicPulse = Math.sin(this.animationTime / 500 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.applyBrightness(primary, toxicPulse);

			case PatternType.CAMO:
				return segmentIndex % 2 === 0 ? primary : secondary;

			// ==========================================
			// NEW: Retro Arcade Animated Patterns
			// ==========================================

			case PatternType.FIRE:
				// Flickering flames that dance along the body
				const fireFlicker = Math.sin(this.animationTime / 100 + segmentIndex * 0.8) * 0.5 +
					Math.sin(this.animationTime / 67 + segmentIndex * 1.3) * 0.3;
				const fireIntensity = Math.max(0.3, Math.min(1, fireFlicker + 0.7));
				// Fire colors: yellow at tips, orange/red at base
				const fireHue = 30 - t * 25; // Yellow (30) to red (5)
				const fireSat = 100;
				const fireLight = 45 + fireIntensity * 20;
				const fireRgb = this.hslToRgb(fireHue, fireSat, fireLight);
				return (fireRgb.r << 16) | (fireRgb.g << 8) | fireRgb.b;

			case PatternType.OCEAN:
				// Wave patterns rippling down the body
				const waveOffset = this.animationTime / 600;
				const wave1 = Math.sin(waveOffset + segmentIndex * 0.4) * 0.5 + 0.5;
				const wave2 = Math.sin(waveOffset * 1.3 + segmentIndex * 0.6 + 2) * 0.3 + 0.5;
				const oceanDepth = (wave1 + wave2) / 2;
				// Blend between deep blue and seafoam/cyan
				const oceanHue = 200 + oceanDepth * 40; // 200 (deep blue) to 240 (cyan)
				const oceanLight = 35 + oceanDepth * 25;
				const oceanRgb = this.hslToRgb(oceanHue, 80, oceanLight);
				return (oceanRgb.r << 16) | (oceanRgb.g << 8) | oceanRgb.b;

			case PatternType.AURORA:
				// Northern lights flowing effect with greens, blues, purples
				const auroraFlow = this.animationTime / 800 + t * 3;
				const auroraWave = Math.sin(auroraFlow) * Math.sin(auroraFlow * 0.7 + 1);
				// Shift between green (120), cyan (180), purple (280)
				const auroraHue = 140 + auroraWave * 80 + Math.sin(segmentIndex * 0.3) * 30;
				const auroraBright = 55 + Math.sin(auroraFlow * 2) * 15;
				const auroraRgb = this.hslToRgb(auroraHue, 85, auroraBright);
				return (auroraRgb.r << 16) | (auroraRgb.g << 8) | auroraRgb.b;

			case PatternType.DISCO:
				// Flashing disco ball segments - rapid color changes
				const discoTime = Math.floor(this.animationTime / 150);
				const discoPhase = (discoTime + segmentIndex) % 6;
				const discoColors = [0xff0088, 0x00ffff, 0xffff00, 0xff00ff, 0x00ff88, 0xff8800];
				const discoFlash = Math.sin(this.animationTime / 100 + segmentIndex) > 0.7;
				if (discoFlash) {
					return 0xffffff; // White flash
				}
				return discoColors[discoPhase] ?? primary;

			case PatternType.MATRIX:
				// Matrix rain digital effect - bright green with data flow
				const matrixFlow = Math.floor(this.animationTime / 200 + segmentIndex * 0.5) % 10;
				const matrixActive = matrixFlow < 4;
				const matrixChar = Math.sin(this.animationTime / 80 + segmentIndex * 2.5) > 0.85;
				if (matrixChar && matrixActive) {
					return 0xffffff; // Bright character flash
				}
				// Green gradient based on activity
				const matrixBright = matrixActive ? 60 + Math.sin(this.animationTime / 150) * 20 : 25;
				const matrixRgb = this.hslToRgb(120, 100, matrixBright);
				return (matrixRgb.r << 16) | (matrixRgb.g << 8) | matrixRgb.b;

			case PatternType.PULSE:
				// Heartbeat-like pulse waves traveling down the body
				const pulseWave = (this.animationTime / 300 + t * 5) % (Math.PI * 2);
				const pulseIntensity = Math.pow(Math.sin(pulseWave), 4); // Sharp pulse shape
				const pulseBright = 0.4 + pulseIntensity * 0.6;
				return this.applyBrightness(primary, pulseBright);

			case PatternType.SCALES:
				// Shimmering scales with light reflection
				const scaleAngle = this.animationTime / 800;
				const scaleReflect = Math.sin(scaleAngle + segmentIndex * 0.5) * 0.5 + 0.5;
				const scaleShimmer = Math.sin(scaleAngle * 3 + segmentIndex) > 0.9 ? 0.3 : 0;
				// Alternating scale pattern with shimmer
				if (segmentIndex % 2 === 0) {
					return this.interpolateColors(primary, 0xffffff, scaleReflect * 0.4 + scaleShimmer);
				}
				return this.interpolateColors(secondary, 0xffffff, scaleReflect * 0.2 + scaleShimmer);

			case PatternType.LIGHTNING:
				// Random lightning bolts flashing along segments
				const lightningTime = Math.floor(this.animationTime / 80);
				const lightningSeed = Math.sin(lightningTime * segmentIndex * 12.9898 + 43758.5453) % 1;
				const lightningStrike = Math.abs(lightningSeed) > 0.92;
				const lightningBranch = Math.abs(Math.sin(lightningTime * segmentIndex * 7.233)) > 0.95;
				if (lightningStrike || lightningBranch) {
					// Bright lightning flash - white/cyan
					return lightningStrike ? 0xffffff : 0x88ffff;
				}
				// Dark stormy background with subtle pulse
				const stormPulse = Math.sin(this.animationTime / 500 + segmentIndex * 0.3) * 0.15 + 0.4;
				return this.applyBrightness(primary, stormPulse);

			case PatternType.CHROMATIC:
				// RGB chromatic aberration effect - color channel separation
				const chromaOffset = this.animationTime / 400;
				const chromaShift = Math.sin(chromaOffset + segmentIndex * 0.4) * 0.5 + 0.5;
				// Cycle through separated RGB channels
				const chromaPhase = Math.floor((chromaOffset + t * 3) * 3) % 3;
				if (chromaPhase === 0) {
					return this.interpolateColors(0xff0000, primary, chromaShift);
				} else if (chromaPhase === 1) {
					return this.interpolateColors(0x00ff00, primary, chromaShift);
				}
				return this.interpolateColors(0x0000ff, primary, chromaShift);

			case PatternType.EMBER:
				// Glowing embers floating up - orange/red particles moving
				const emberFlow = this.animationTime / 400;
				const emberPos = (emberFlow + t + segmentIndex * 0.1) % 1;
				const emberGlow = Math.sin(emberPos * Math.PI); // Brightest in middle
				// Ember color: deep red to bright orange
				const emberHue = 15 + emberGlow * 25;
				const emberBright = 30 + emberGlow * 40;
				// Random sparkle
				const emberSpark = Math.sin(this.animationTime / 100 + segmentIndex * 3.7) > 0.9;
				if (emberSpark) {
					return 0xffff88; // Bright spark
				}
				const emberRgb = this.hslToRgb(emberHue, 100, emberBright);
				return (emberRgb.r << 16) | (emberRgb.g << 8) | emberRgb.b;

			case PatternType.HYPNOTIC:
				// Swirling hypnotic spirals
				const hypnoTime = this.animationTime / 500;
				const hypnoSpiral = Math.sin(hypnoTime + t * 8 + segmentIndex * 0.3);
				const hypnoRing = Math.floor((hypnoTime + t * 4) * 2) % 2;
				// Alternating black/white spiral bands
				if (hypnoRing === 0) {
					const hypnoBright = 0.2 + hypnoSpiral * 0.15;
					return this.applyBrightness(primary, hypnoBright);
				}
				const hypnoBright = 0.8 + hypnoSpiral * 0.2;
				return this.applyBrightness(secondary, hypnoBright);

			case PatternType.PIXELATE:
				// Retro pixel art shifting - blocky color changes
				const pixelTime = Math.floor(this.animationTime / 250);
				const pixelBlock = Math.floor(segmentIndex / 3); // Group segments into pixel blocks
				const pixelPhase = (pixelTime + pixelBlock) % 5;
				const pixelColors = [
					primary,
					secondary,
					this.interpolateColors(primary, secondary, 0.5),
					0xffffff,
					this.applyBrightness(primary, 0.6),
				];
				// Add scanline effect
				const scanline = segmentIndex % 3 === 2 ? 0.85 : 1.0;
				return this.applyBrightness(pixelColors[pixelPhase] ?? primary, scanline);

			default:
				// Apply gradient darkening for unknown patterns
				return this.applyBrightnessToColor(primary, 1 - t * 0.4);
		}
	}

	/**
	 * Get rainbow color based on position (0-1)
	 */
	private getRainbowColor(t: number): number {
		const hue = t * 360;
		const { r, g, b } = this.hslToRgb(hue, 100, 50);
		return (r << 16) | (g << 8) | b;
	}

	/**
	 * Convert HSL to RGB
	 */
	private hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
		s /= 100;
		l /= 100;
		const c = (1 - Math.abs(2 * l - 1)) * s;
		const x = c * (1 - Math.abs((h / 60) % 2 - 1));
		const m = l - c / 2;
		let r = 0, g = 0, b = 0;

		if (h < 60) { r = c; g = x; b = 0; }
		else if (h < 120) { r = x; g = c; b = 0; }
		else if (h < 180) { r = 0; g = c; b = x; }
		else if (h < 240) { r = 0; g = x; b = c; }
		else if (h < 300) { r = x; g = 0; b = c; }
		else { r = c; g = 0; b = x; }

		return {
			r: Math.round((r + m) * 255),
			g: Math.round((g + m) * 255),
			b: Math.round((b + m) * 255),
		};
	}

	/**
	 * Apply neon glow effect
	 */
	private applyNeonEffect(color: number, t: number): number {
		const pulse = Math.sin(this.animationTime / 600 + t * 2) * 0.3 + 0.7;
		return this.applyBrightness(color, pulse);
	}

	/**
	 * Apply brightness factor to a color
	 */
	private applyBrightness(color: number, factor: number): number {
		const r = Math.min(255, Math.floor(((color >> 16) & 0xff) * factor));
		const g = Math.min(255, Math.floor(((color >> 8) & 0xff) * factor));
		const b = Math.min(255, Math.floor((color & 0xff) * factor));
		return (r << 16) | (g << 8) | b;
	}

	/**
	 * Apply brightness to color (alias for consistency)
	 */
	private applyBrightnessToColor(color: number, factor: number): number {
		return this.applyBrightness(color, factor);
	}

	/**
	 * Interpolate between two colors
	 */
	private interpolateColors(color1: number, color2: number, t: number): number {
		const r1 = (color1 >> 16) & 0xff;
		const g1 = (color1 >> 8) & 0xff;
		const b1 = color1 & 0xff;

		const r2 = (color2 >> 16) & 0xff;
		const g2 = (color2 >> 8) & 0xff;
		const b2 = color2 & 0xff;

		const r = Math.floor(r1 + (r2 - r1) * t);
		const g = Math.floor(g1 + (g2 - g1) * t);
		const b = Math.floor(b1 + (b2 - b1) * t);

		return (r << 16) | (g << 8) | b;
	}


	/**
	 * Draw the head decoration
	 * Uses server-provided radius for head (score-based scaling)
	 * Now skin-aware - matches body color/pattern
	 */
	private drawHead(): void {
		// Use server-provided head radius if available, otherwise use config radius
		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;
		const g = this.headGraphics;
		g.clear();

		// Get head color from skin (same as first body segment)
		const colors = this.getSegmentColors();
		const headColor = colors[0] ?? this.config.color;

		// Extract RGB for effects
		const r = (headColor >> 16) & 0xff;
		const gc = (headColor >> 8) & 0xff;
		const b = headColor & 0xff;
		const darkerColor = ((Math.floor(r * 0.7) << 16) | (Math.floor(gc * 0.7) << 8) | Math.floor(b * 0.7));
		const lighterColor = ((Math.min(255, Math.floor(r * 1.2)) << 16) | (Math.min(255, Math.floor(gc * 1.2)) << 8) | Math.min(255, Math.floor(b * 1.2)));

		// Check if this is a glowing skin
		const isGlowingSkin = this.hasAnimatedPattern();
		const protectionLevel = EyeProtectionSettings.getInstance().getLevel();

		// === ENHANCED HEAD RENDERING ===

		// 1. Outer glow for glowing skins - reduced for mild protection, disabled for strong
		if (isGlowingSkin && protectionLevel !== EyeProtectionLevel.STRONG) {
			const glowRadius = protectionLevel === EyeProtectionLevel.MILD ? 1.25 : 1.4;
			const glowAlpha = protectionLevel === EyeProtectionLevel.MILD ? 0.12 : 0.2;
			g.circle(0, 0, headRadius * glowRadius);
			g.fill({ color: headColor, alpha: glowAlpha });
		}

		// 2. Soft shadow (offset down-right)
		g.circle(headRadius * 0.1, headRadius * 0.15, headRadius * 1.1);
		g.fill({ color: 0x000000, alpha: 0.25 });

		// 3. Main head circle
		g.circle(0, 0, headRadius * 1.1);
		g.fill(headColor);

		// 4. Inner darker ring for depth
		g.circle(0, 0, headRadius * 0.95);
		g.stroke({ color: darkerColor, width: headRadius * 0.1, alpha: 0.3 });

		// 5. Main highlight (top-left glossy effect)
		const highlightRadius = headRadius * 0.4;
		g.circle(-headRadius * 0.3, -headRadius * 0.3, highlightRadius);
		g.fill({ color: 0xffffff, alpha: 0.35 });

		// 6. Secondary smaller highlight (adds glossiness)
		g.circle(-headRadius * 0.2, -headRadius * 0.45, highlightRadius * 0.4);
		g.fill({ color: 0xffffff, alpha: 0.5 });

		// 7. Bottom rim light reflection
		g.circle(headRadius * 0.15, headRadius * 0.35, headRadius * 0.25);
		g.fill({ color: lighterColor, alpha: 0.15 });

		// 8. Subtle nose/snout bump highlight (front of head)
		g.ellipse(headRadius * 0.5, 0, headRadius * 0.2, headRadius * 0.3);
		g.fill({ color: lighterColor, alpha: 0.2 });
	}

	/**
	 * Draw the eyes with dynamic pupil position and expression support
	 * Uses server-provided radius for head (score-based scaling)
	 * LEVIATHAN has menacing red pupils with a slit shape
	 * Expressions: Normal, Angry (eyebrows), Scared (wide eyes), Happy (curved eyes)
	 */
	private drawEyes(): void {
		const { eyeColor } = this.config;
		// Use server-provided head radius if available, otherwise use config radius
		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;
		const eyeRadius = headRadius * 0.35;
		const pupilRadius = eyeRadius * 0.5;

		// Pupil can move within 40% of eye radius from center
		const maxPupilMove = eyeRadius * 0.4;
		const pupilX = this.pupilOffsetX * maxPupilMove;
		const pupilY = this.pupilOffsetY * maxPupilMove;

		// LEVIATHAN has special menacing eyes (override expressions)
		if (this.isLegendary) {
			this.drawLeviathanEyes(eyeRadius, pupilRadius, pupilX, pupilY);
			this.hideEyebrows();
			return;
		}

		const pupilColor = this.config.pupilColor;
		const expression = this.currentExpression;

		// Draw eyes based on current expression
		switch (expression) {
			case EyeExpression.ANGRY:
				this.drawAngryEyes(eyeRadius, pupilRadius, pupilX, pupilY, eyeColor, pupilColor);
				break;
			case EyeExpression.SCARED:
				this.drawScaredEyes(eyeRadius, pupilRadius, pupilX, pupilY, eyeColor, pupilColor);
				break;
			case EyeExpression.HAPPY:
				this.drawHappyEyes(eyeRadius, eyeColor, pupilColor);
				break;
			case EyeExpression.NORMAL:
			default:
				this.drawNormalEyes(eyeRadius, pupilRadius, pupilX, pupilY, eyeColor, pupilColor);
				break;
		}
	}

	/**
	 * Draw normal eyes (default expression)
	 */
	private drawNormalEyes(
		eyeRadius: number,
		pupilRadius: number,
		pupilX: number,
		pupilY: number,
		eyeColor: number,
		pupilColor: number
	): void {
		// Hide eyebrows for normal expression
		this.hideEyebrows();

		// Left eye
		this.leftEye.clear();
		this.leftEye.circle(0, 0, eyeRadius);
		this.leftEye.fill(eyeColor);
		this.leftEye.circle(pupilX, pupilY, pupilRadius);
		this.leftEye.fill(pupilColor);

		// Right eye
		this.rightEye.clear();
		this.rightEye.circle(0, 0, eyeRadius);
		this.rightEye.fill(eyeColor);
		this.rightEye.circle(pupilX, pupilY, pupilRadius);
		this.rightEye.fill(pupilColor);
	}

	/**
	 * Draw angry eyes with eyebrows (when sprinting hard 5+ seconds)
	 */
	private drawAngryEyes(
		eyeRadius: number,
		pupilRadius: number,
		pupilX: number,
		pupilY: number,
		eyeColor: number,
		pupilColor: number
	): void {
		// Draw slightly squinted eyes
		this.leftEye.clear();
		// Squinted eye - ellipse instead of circle, slightly flattened
		this.leftEye.ellipse(0, 0, eyeRadius, eyeRadius * 0.75);
		this.leftEye.fill(eyeColor);
		this.leftEye.circle(pupilX, pupilY * 0.8, pupilRadius);
		this.leftEye.fill(pupilColor);

		this.rightEye.clear();
		this.rightEye.ellipse(0, 0, eyeRadius, eyeRadius * 0.75);
		this.rightEye.fill(eyeColor);
		this.rightEye.circle(pupilX, pupilY * 0.8, pupilRadius);
		this.rightEye.fill(pupilColor);

		// Draw eyebrows
		this.drawEyebrows(eyeRadius);
	}

	/**
	 * Draw scared eyes (wide eyes with small pupils when score is low)
	 */
	private drawScaredEyes(
		eyeRadius: number,
		pupilRadius: number,
		pupilX: number,
		pupilY: number,
		eyeColor: number,
		pupilColor: number
	): void {
		// Hide eyebrows for scared expression
		this.hideEyebrows();

		// Wide eyes - larger than normal
		const wideEyeRadius = eyeRadius * 1.2;
		// Small pupils - scared look
		const smallPupilRadius = pupilRadius * 0.6;

		// Left eye - wide and round
		this.leftEye.clear();
		this.leftEye.circle(0, 0, wideEyeRadius);
		this.leftEye.fill(eyeColor);
		// Small pupil, slightly up (looking worried)
		this.leftEye.circle(pupilX * 0.5, pupilY * 0.5 - wideEyeRadius * 0.1, smallPupilRadius);
		this.leftEye.fill(pupilColor);

		// Right eye
		this.rightEye.clear();
		this.rightEye.circle(0, 0, wideEyeRadius);
		this.rightEye.fill(eyeColor);
		this.rightEye.circle(pupilX * 0.5, pupilY * 0.5 - wideEyeRadius * 0.1, smallPupilRadius);
		this.rightEye.fill(pupilColor);
	}

	/**
	 * Draw happy eyes (curved like ^_^ when powered up)
	 */
	private drawHappyEyes(
		eyeRadius: number,
		eyeColor: number,
		pupilColor: number
	): void {
		// Hide eyebrows for happy expression
		this.hideEyebrows();

		// Happy eyes are curved arcs (like ^_^)
		// Draw as thick curved lines instead of circles

		// Left eye - curved arc pointing up
		this.leftEye.clear();
		// Draw a curved "smile" shape for the eye
		const arcWidth = eyeRadius * 1.5;
		const arcHeight = eyeRadius * 0.8;

		// Create a happy curve (arc) - filled crescent shape
		this.leftEye.moveTo(-arcWidth * 0.5, 0);
		this.leftEye.quadraticCurveTo(0, -arcHeight, arcWidth * 0.5, 0);
		this.leftEye.quadraticCurveTo(0, -arcHeight * 0.4, -arcWidth * 0.5, 0);
		this.leftEye.fill(pupilColor);

		// Add a small white highlight for shine
		this.leftEye.circle(-arcWidth * 0.2, -arcHeight * 0.3, eyeRadius * 0.15);
		this.leftEye.fill({ color: eyeColor, alpha: 0.8 });

		// Right eye - same curved arc
		this.rightEye.clear();
		this.rightEye.moveTo(-arcWidth * 0.5, 0);
		this.rightEye.quadraticCurveTo(0, -arcHeight, arcWidth * 0.5, 0);
		this.rightEye.quadraticCurveTo(0, -arcHeight * 0.4, -arcWidth * 0.5, 0);
		this.rightEye.fill(pupilColor);

		// Add a small white highlight for shine
		this.rightEye.circle(-arcWidth * 0.2, -arcHeight * 0.3, eyeRadius * 0.15);
		this.rightEye.fill({ color: eyeColor, alpha: 0.8 });
	}

	/**
	 * Draw angry eyebrows above the eyes
	 */
	private drawEyebrows(eyeRadius: number): void {
		// Create eyebrow graphics if they don't exist
		if (!this.leftEyebrow) {
			this.leftEyebrow = new Graphics();
			this.container.addChild(this.leftEyebrow);
		}
		if (!this.rightEyebrow) {
			this.rightEyebrow = new Graphics();
			this.container.addChild(this.rightEyebrow);
		}

		const browWidth = eyeRadius * 1.4;
		const browThickness = eyeRadius * 0.25;
		const browColor = 0x222222; // Dark gray/black eyebrows

		// Left eyebrow - angled inward (angry look)
		// Position is set in updateHeadAndEyes
		this.leftEyebrow.clear();
		this.leftEyebrow.moveTo(-browWidth * 0.5, browThickness * 0.5);
		this.leftEyebrow.lineTo(browWidth * 0.5, -browThickness * 0.5);
		this.leftEyebrow.lineTo(browWidth * 0.5, browThickness * 0.5);
		this.leftEyebrow.lineTo(-browWidth * 0.5, browThickness * 1.2);
		this.leftEyebrow.closePath();
		this.leftEyebrow.fill(browColor);
		this.leftEyebrow.visible = true;

		// Right eyebrow - mirrored angle
		this.rightEyebrow.clear();
		this.rightEyebrow.moveTo(-browWidth * 0.5, -browThickness * 0.5);
		this.rightEyebrow.lineTo(browWidth * 0.5, browThickness * 0.5);
		this.rightEyebrow.lineTo(browWidth * 0.5, browThickness * 1.2);
		this.rightEyebrow.lineTo(-browWidth * 0.5, browThickness * 0.5);
		this.rightEyebrow.closePath();
		this.rightEyebrow.fill(browColor);
		this.rightEyebrow.visible = true;
	}

	/**
	 * Hide the eyebrows (for non-angry expressions)
	 */
	private hideEyebrows(): void {
		if (this.leftEyebrow) {
			this.leftEyebrow.visible = false;
		}
		if (this.rightEyebrow) {
			this.rightEyebrow.visible = false;
		}
	}

	/**
	 * Draw LEVIATHAN's menacing eyes with red slit pupils
	 */
	private drawLeviathanEyes(eyeRadius: number, pupilRadius: number, pupilX: number, pupilY: number): void {
		const eyeColor = 0xffffdd; // Slightly yellowish white for menacing look

		// Pulsing glow effect
		const pulse = Math.sin(this.animationTime * 0.006) * 0.2 + 0.8;

		// Left eye
		this.leftEye.clear();
		// Outer red glow
		this.leftEye.circle(0, 0, eyeRadius * 1.2);
		this.leftEye.fill({ color: WormRenderer.LEVIATHAN_EYE, alpha: 0.3 * pulse });
		// Main eye white
		this.leftEye.circle(0, 0, eyeRadius);
		this.leftEye.fill(eyeColor);
		// Red iris
		this.leftEye.circle(pupilX, pupilY, pupilRadius * 1.2);
		this.leftEye.fill({ color: 0x8b0000, alpha: 0.8 }); // Dark red iris
		// Slit pupil (vertical ellipse)
		this.leftEye.ellipse(pupilX, pupilY, pupilRadius * 0.25, pupilRadius * 0.9);
		this.leftEye.fill(0x000000);
		// Glowing center
		this.leftEye.ellipse(pupilX, pupilY - pupilRadius * 0.2, pupilRadius * 0.15, pupilRadius * 0.3);
		this.leftEye.fill({ color: WormRenderer.LEVIATHAN_EYE, alpha: 0.6 * pulse });

		// Right eye
		this.rightEye.clear();
		// Outer red glow
		this.rightEye.circle(0, 0, eyeRadius * 1.2);
		this.rightEye.fill({ color: WormRenderer.LEVIATHAN_EYE, alpha: 0.3 * pulse });
		// Main eye white
		this.rightEye.circle(0, 0, eyeRadius);
		this.rightEye.fill(eyeColor);
		// Red iris
		this.rightEye.circle(pupilX, pupilY, pupilRadius * 1.2);
		this.rightEye.fill({ color: 0x8b0000, alpha: 0.8 }); // Dark red iris
		// Slit pupil (vertical ellipse)
		this.rightEye.ellipse(pupilX, pupilY, pupilRadius * 0.25, pupilRadius * 0.9);
		this.rightEye.fill(0x000000);
		// Glowing center
		this.rightEye.ellipse(pupilX, pupilY - pupilRadius * 0.2, pupilRadius * 0.15, pupilRadius * 0.3);
		this.rightEye.fill({ color: WormRenderer.LEVIATHAN_EYE, alpha: 0.6 * pulse });
	}

	/**
	 * Update pupil positions with smooth interpolation and look-around behavior
	 */
	private updatePupils(dt: number): void {
		// Update look-around timer
		this.lookAroundTimer += dt * 1000;

		// Check if it's time to pick a new random look direction
		if (this.lookAroundTimer >= this.nextLookChange) {
			this.lookAroundTimer = 0;
			// Random interval between 1.5-4 seconds for next look change
			this.nextLookChange = 1500 + Math.random() * 2500;

			// 70% chance to look forward (movement direction), 30% to look around
			if (Math.random() < 0.7) {
				// Look in movement direction (forward)
				this.targetPupilOffsetX = 0.5 + Math.random() * 0.3; // 0.5-0.8 forward
				this.targetPupilOffsetY = (Math.random() - 0.5) * 0.3; // slight vertical variance
			} else {
				// Look around randomly
				const lookAngle = Math.random() * Math.PI * 2;
				const lookDist = 0.3 + Math.random() * 0.5; // 0.3-0.8 from center
				this.targetPupilOffsetX = Math.cos(lookAngle) * lookDist;
				this.targetPupilOffsetY = Math.sin(lookAngle) * lookDist;
			}
		}

		// Smoothly interpolate current pupil position toward target
		const lerpFactor = Math.min(1, this.PUPIL_LERP_SPEED * dt);
		this.pupilOffsetX += (this.targetPupilOffsetX - this.pupilOffsetX) * lerpFactor;
		this.pupilOffsetY += (this.targetPupilOffsetY - this.pupilOffsetY) * lerpFactor;

		// Clamp pupil offset to valid range
		const dist = Math.sqrt(this.pupilOffsetX * this.pupilOffsetX + this.pupilOffsetY * this.pupilOffsetY);
		if (dist > 1) {
			this.pupilOffsetX /= dist;
			this.pupilOffsetY /= dist;
		}
	}

	/**
	 * Update the worm from segment data (from server state)
	 */
	updateFromSegments(segments: SegmentData[]): void {
		if (segments.length === 0) return;

		// Mark body as dirty so it gets redrawn with new positions
		this.bodyDirty = true;

		// Update body points and radii from segments
		const pointCount = Math.min(segments.length, this.bodyPoints.length);

		// Resize segmentRadii array if needed
		if (this.segmentRadii.length !== this.bodyPoints.length) {
			this.segmentRadii = new Array(this.bodyPoints.length).fill(this.config.radius);
		}

		for (let i = 0; i < pointCount; i++) {
			const segment = segments[i];
			const point = this.bodyPoints[i];
			if (segment && point) {
				point.x = segment.x;
				point.y = segment.y;
				// Store server-provided radius (score-based)
				this.segmentRadii[i] = segment.radius;
			}
		}

		// Update head position and rotation
		const head = segments[0];
		if (head) {
			this.updateHeadPosition(head.x, head.y);

			// Calculate head rotation from first two segments
			const second = segments[1];
			if (second) {
				const dx = head.x - second.x;
				const dy = head.y - second.y;
				this.headRotation = Math.atan2(dy, dx);
			}
		}

		this.updateHeadAndEyes();
	}

	/**
	 * Update worm with head position (for local control)
	 *
	 * Uses path-following for smooth ribbon-like movement when enabled.
	 * Segments trace the exact path the head traveled through.
	 * Falls back to spring-damper physics for remote worms.
	 */
	updateHeadPosition(headX: number, headY: number): void {
		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		const prevHeadX = headPoint.x;
		const prevHeadY = headPoint.y;

		// Update head position
		headPoint.x = headX;
		headPoint.y = headY;

		// Calculate head rotation from movement
		const moveX = headX - prevHeadX;
		const moveY = headY - prevHeadY;
		const moveDist = Math.sqrt(moveX * moveX + moveY * moveY);

		if (moveDist > 0.01) {
			const newRotation = Math.atan2(moveY, moveX);

			// Calculate turn rate (radians per frame, normalized by dt)
			let rotationDelta = newRotation - this.prevHeadRotation;
			// Normalize to [-PI, PI]
			while (rotationDelta > Math.PI) rotationDelta -= Math.PI * 2;
			while (rotationDelta < -Math.PI) rotationDelta += Math.PI * 2;

			this.currentTurnRate = Math.abs(rotationDelta) / this.lastDt;
			this.headRotation = newRotation;
			this.prevHeadRotation = newRotation;

			// Add head position to path history for path-following
			if (this.usePathFollowing) {
				this.addPathPoint(headX, headY);
			}
		}

		// Update body segments using the appropriate method
		if (this.usePathFollowing && this.pathHistory.length > 2) {
			// Path-following: segments trace the exact path of the head
			this.updateSegmentsAlongPath();
		} else {
			// Fallback: spring-damper physics for remote worms or short paths
			this.updateSegmentPhysics();
		}

		this.updateHeadAndEyes();
	}

	/**
	 * Update segment physics using spring-damper system
	 * Creates natural ribbon-like following behavior
	 */
	private updateSegmentPhysics(): void {
		const dt = this.lastDt;
		// Use dynamic spacing based on current radius for proper overlap at all sizes
		const spacing = this.getDynamicSpacing();

		// Dynamic stiffness based on turn rate
		// When turning sharply, reduce stiffness for ribbon effect
		const turnFactor = Math.min(this.currentTurnRate / 4, 0.6); // 0-0.6 based on turn rate
		const dynamicStiffness = SEGMENT_SPRING_STIFFNESS * (1 - turnFactor * 0.5);
		const dynamicDamping = SEGMENT_DAMPING * (1 - turnFactor * 0.3);

		for (let i = 1; i < this.bodyPoints.length; i++) {
			const current = this.bodyPoints[i];
			const target = this.bodyPoints[i - 1]; // The segment ahead
			const velocity = this.segmentVelocities[i];

			if (!current || !target || !velocity) continue;

			// Calculate direction to target
			const dx = target.x - current.x;
			const dy = target.y - current.y;
			const distance = Math.sqrt(dx * dx + dy * dy);

			if (distance < 0.001) {
				// Segments overlapping - nudge apart using local tail direction
				// Find the direction from the segment ahead of target to target (tail direction)
				let tailAngle = this.headRotation + Math.PI; // Fallback to head direction reversed
				const targetIdx = i - 1;
				if (targetIdx > 0) {
					const prevTarget = this.bodyPoints[targetIdx - 1];
					if (prevTarget) {
						const tdx = target.x - prevTarget.x;
						const tdy = target.y - prevTarget.y;
						const tdist = Math.sqrt(tdx * tdx + tdy * tdy);
						if (tdist > 0.001) {
							// Use the direction the tail is pointing (away from head)
							tailAngle = Math.atan2(tdy, tdx);
						}
					}
				}
				current.x = target.x + Math.cos(tailAngle) * spacing;
				current.y = target.y + Math.sin(tailAngle) * spacing;
				velocity.vx = 0;
				velocity.vy = 0;
				continue;
			}

			// Calculate ideal position (at spacing distance behind target)
			const dirX = dx / distance;
			const dirY = dy / distance;
			const idealX = target.x - dirX * spacing;
			const idealY = target.y - dirY * spacing;

			// Calculate displacement from ideal position
			const dispX = idealX - current.x;
			const dispY = idealY - current.y;
			const dispDist = Math.sqrt(dispX * dispX + dispY * dispY);

			// Check for excessive stretch
			const stretchRatio = distance / spacing;
			if (stretchRatio > SEGMENT_MAX_STRETCH) {
				// Snap back to prevent excessive stretching
				current.x = target.x - dirX * spacing * SEGMENT_MAX_STRETCH;
				current.y = target.y - dirY * spacing * SEGMENT_MAX_STRETCH;
				velocity.vx *= 0.5; // Dampen velocity on snap
				velocity.vy *= 0.5;
				continue;
			}

			// Spring force (pulls toward ideal position)
			const springForceX = dispX * dynamicStiffness;
			const springForceY = dispY * dynamicStiffness;

			// Damping force (reduces oscillation)
			const dampingForceX = -velocity.vx * dynamicDamping;
			const dampingForceY = -velocity.vy * dynamicDamping;

			// Apply forces to velocity
			velocity.vx += (springForceX + dampingForceX) * dt;
			velocity.vy += (springForceY + dampingForceY) * dt;

			// Clamp velocity to prevent instability
			const maxVel = spacing * 20; // Max velocity based on segment spacing
			const velMag = Math.sqrt(velocity.vx * velocity.vx + velocity.vy * velocity.vy);
			if (velMag > maxVel) {
				velocity.vx = (velocity.vx / velMag) * maxVel;
				velocity.vy = (velocity.vy / velMag) * maxVel;
			}

			// Update position
			current.x += velocity.vx * dt;
			current.y += velocity.vy * dt;

			// For segments far from ideal, blend in direct movement for stability
			if (dispDist > spacing * 0.5) {
				const blendFactor = Math.min((dispDist - spacing * 0.5) / spacing, 0.3);
				current.x += dispX * blendFactor;
				current.y += dispY * blendFactor;
			}
		}
	}

	/**
	 * Add points to the path history when the head moves
	 * Interpolates multiple points for large movements to ensure dense sampling
	 * This prevents gaps during sprint by keeping path data continuous
	 */
	private addPathPoint(x: number, y: number): void {
		const lastPoint = this.pathHistory.get(0);

		if (!lastPoint) {
			// First point - just add it
			this.pathHistory.unshift({
				x,
				y,
				distance: this.pathTotalDistance,
			});
			return;
		}

		const dx = x - lastPoint.x;
		const dy = y - lastPoint.y;
		const dist = Math.sqrt(dx * dx + dy * dy);

		// Only add points if moved far enough
		if (dist < PATH_SAMPLE_DISTANCE) {
			return;
		}

		// Calculate how many intermediate points we need
		// During sprint, head might move 50+ pixels per frame - we need multiple samples
		const numPoints = Math.ceil(dist / PATH_SAMPLE_DISTANCE);
		const stepX = dx / numPoints;
		const stepY = dy / numPoints;
		const stepDist = dist / numPoints;

		// Add intermediate points to maintain dense path sampling
		for (let i = 1; i <= numPoints; i++) {
			const px = lastPoint.x + stepX * i;
			const py = lastPoint.y + stepY * i;
			this.pathTotalDistance += stepDist;

			this.pathHistory.unshift({
				x: px,
				y: py,
				distance: this.pathTotalDistance,
			});
		}
	}

	/**
	 * Initialize path history with current segment positions
	 * Called when worm is first created or reset
	 */
	private initializePathHistory(): void {
		this.pathHistory.clear();
		this.pathTotalDistance = 0;

		// Build path from existing segments (head to tail)
		// Also initialize segment rotations based on direction toward segment ahead
		let accumulatedDistance = 0;
		for (let i = 0; i < this.bodyPoints.length; i++) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			if (i > 0) {
				const prev = this.bodyPoints[i - 1];
				if (prev) {
					const dx = point.x - prev.x;
					const dy = point.y - prev.y;
					accumulatedDistance += Math.sqrt(dx * dx + dy * dy);

					// Calculate rotation toward segment ahead (from current to prev)
					const rotDx = prev.x - point.x;
					const rotDy = prev.y - point.y;
					const rotation = Math.atan2(rotDy, rotDx);
					// Ensure segmentRotations array is properly sized
					while (this.segmentRotations.length <= i) {
						this.segmentRotations.push(0);
					}
					this.segmentRotations[i] = rotation;
				}
			} else {
				// Head segment uses headRotation
				while (this.segmentRotations.length <= i) {
					this.segmentRotations.push(0);
				}
				this.segmentRotations[i] = this.headRotation;
			}

			this.pathHistory.push({
				x: point.x,
				y: point.y,
				distance: accumulatedDistance,
			});
		}

		this.pathTotalDistance = accumulatedDistance;
	}

	/**
	 * Catmull-Rom spline interpolation between four points
	 * Creates smooth curves through control points
	 * @param p0 Point before start
	 * @param p1 Start point
	 * @param p2 End point
	 * @param p3 Point after end
	 * @param t Interpolation factor (0-1)
	 * @returns Interpolated position
	 */
	private catmullRomInterpolate(
		p0: PathPoint,
		p1: PathPoint,
		p2: PathPoint,
		p3: PathPoint,
		t: number
	): { x: number; y: number } {
		const tension = PATH_SMOOTHING_TENSION;
		const t2 = t * t;
		const t3 = t2 * t;

		// Catmull-Rom basis functions
		const s = (1 - tension) / 2;

		const b0 = -s * t3 + 2 * s * t2 - s * t;
		const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
		const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
		const b3 = s * t3 - s * t2;

		return {
			x: b0 * p0.x + b1 * p1.x + b2 * p2.x + b3 * p3.x,
			y: b0 * p0.y + b1 * p1.y + b2 * p2.y + b3 * p3.y,
		};
	}

	/**
	 * Get position along the path at a given distance from the head
	 * Uses Catmull-Rom interpolation for smooth curves
	 * @param targetDistance Distance from head along the path
	 * @returns Interpolated position, or null if path too short
	 */
	private getPositionAlongPath(targetDistance: number): { x: number; y: number } | null {
		if (this.pathHistory.length < 2) {
			return null;
		}

		// Find the two path points that bracket the target distance
		let idx = 0;
		while (idx < this.pathHistory.length - 1) {
			const current = this.pathHistory.get(idx);
			const next = this.pathHistory.get(idx + 1);

			if (!current || !next) break;

			// Calculate distances relative to head (pathHistory.get(0))
			const currentDist = this.pathTotalDistance - current.distance;
			const nextDist = this.pathTotalDistance - next.distance;

			if (targetDistance >= currentDist && targetDistance <= nextDist) {
				// Found the segment - interpolate
				const segmentLength = nextDist - currentDist;
				if (segmentLength < 0.001) {
					return { x: current.x, y: current.y };
				}

				const t = (targetDistance - currentDist) / segmentLength;

				// Get surrounding points for Catmull-Rom
				const p0 = this.pathHistory.get(Math.max(0, idx - 1)) ?? current;
				const p1 = current;
				const p2 = next;
				const p3 = this.pathHistory.get(Math.min(this.pathHistory.length - 1, idx + 2)) ?? next;

				return this.catmullRomInterpolate(p0, p1, p2, p3, t);
			}

			idx++;
		}

		// Target distance is beyond our path history
		// Return null to trigger chain-following fallback
		// This prevents segments from clustering at the last point
		return null;
	}

	/**
	 * Update segment positions using TRUE path-following
	 * Each segment is positioned directly on the path the head traveled through.
	 * This creates classic snake/worms game behavior where the body traces
	 * the exact trail of the head - no corner cutting, no loose following.
	 *
	 * Algorithm:
	 * - Segment i is placed at distance (i * spacing) along the recorded path
	 * - Uses Catmull-Rom interpolation for smooth curves between path points
	 * - Falls back to chain-following only when path history is insufficient
	 */
	private updateSegmentsAlongPath(): void {
		// Use dynamic spacing based on current radius for proper overlap at all sizes
		const spacing = this.getDynamicSpacing();
		const segmentCount = this.bodyPoints.length;

		// Ensure segmentRotations array is properly sized
		while (this.segmentRotations.length < segmentCount) {
			const lastRotation = this.segmentRotations.length > 0
				? this.segmentRotations[this.segmentRotations.length - 1]
				: this.headRotation + Math.PI;
			this.segmentRotations.push(lastRotation ?? 0);
		}

		// Update segment 0's rotation to match head rotation
		this.segmentRotations[0] = this.headRotation;

		// Position each segment directly on the path
		for (let i = 1; i < segmentCount; i++) {
			const current = this.bodyPoints[i];
			const target = this.bodyPoints[i - 1]; // Segment ahead

			if (!current || !target) continue;

			// Get the exact position on the path at this segment's distance from head
			const pathPos = this.getPositionAlongPath(i * spacing);

			if (pathPos) {
				// TRUE PATH FOLLOWING: Position segment directly on the path
				current.x = pathPos.x;
				current.y = pathPos.y;

				// Update rotation to face toward the segment ahead
				const dx = target.x - current.x;
				const dy = target.y - current.y;
				if (dx * dx + dy * dy > 0.001) {
					this.segmentRotations[i] = Math.atan2(dy, dx);
				}
			} else {
				// Fallback: Path not long enough yet, use simple chain-following
				// This happens for newly spawned worms or when path history is short
				const dx = target.x - current.x;
				const dy = target.y - current.y;
				const dist = Math.sqrt(dx * dx + dy * dy);

				if (dist > 0.001) {
					// Position at exactly spacing distance behind the target
					const dirX = dx / dist;
					const dirY = dy / dist;
					current.x = target.x - dirX * spacing;
					current.y = target.y - dirY * spacing;
					this.segmentRotations[i] = Math.atan2(dy, dx);
				} else {
					// Segments overlapping (newly spawned at same position)
					// Use tail direction to spread them out properly
					let tailAngle = this.headRotation + Math.PI; // Default: opposite head direction
					const prevIdx = i - 1;
					if (prevIdx > 0) {
						const prevPrev = this.bodyPoints[prevIdx - 1];
						if (prevPrev) {
							// Calculate tail direction from the segment chain
							const tdx = target.x - prevPrev.x;
							const tdy = target.y - prevPrev.y;
							const tdist = Math.sqrt(tdx * tdx + tdy * tdy);
							if (tdist > 0.001) {
								tailAngle = Math.atan2(tdy, tdx);
							}
						}
					}
					// Position segment at spacing distance along tail direction
					current.x = target.x + Math.cos(tailAngle) * spacing;
					current.y = target.y + Math.sin(tailAngle) * spacing;
					this.segmentRotations[i] = tailAngle;
				}
			}
		}
	}

	/**
	 * Enable or disable path-following mode
	 * Path-following is used for local player, spring physics for remote worms
	 */
	setPathFollowing(enabled: boolean): void {
		if (this.usePathFollowing === enabled) return;

		this.usePathFollowing = enabled;

		if (enabled) {
			// Initialize path history from current positions
			this.initializePathHistory();
		}
	}

	/**
	 * Set the delta time for physics calculations
	 * Should be called each frame before updateHeadPosition
	 */
	setDeltaTime(dt: number): void {
		this.lastDt = Math.max(0.001, Math.min(dt, 0.1)); // Clamp to reasonable range
	}

	/**
	 * Update head graphics and eye positions
	 * Uses server-provided radius for head (score-based scaling)
	 * OPTIMIZED: Only redraws graphics when radius changes
	 */
	private updateHeadAndEyes(): void {
		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		const headX = headPoint.x;
		const headY = headPoint.y;
		// Use server-provided head radius if available, otherwise use config radius
		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		// Check if radius changed - only then do we need to redraw graphics
		// EXCEPTION: Animated accessories need to redraw every frame
		const isAnimated = this.isAnimatedAccessory(this.accessoryType);
		const radiusChanged = Math.abs(headRadius - this.lastHeadRadius) > 0.1;
		const shouldRedraw = radiusChanged || isAnimated;

		// Position head (always needed for movement)
		this.headGraphics.position.set(headX, headY);
		this.headGraphics.rotation = this.headRotation;

		// Position eyes relative to head (scale with head radius)
		if (this.config.showEyes) {
			const eyeOffset = headRadius * 0.5;
			const eyeSpread = headRadius * 0.45;

			const cosR = Math.cos(this.headRotation);
			const sinR = Math.sin(this.headRotation);

			// Left eye
			this.leftEye.position.set(
				headX + cosR * eyeOffset - sinR * eyeSpread,
				headY + sinR * eyeOffset + cosR * eyeSpread
			);
			this.leftEye.rotation = this.headRotation;

			// Right eye
			this.rightEye.position.set(
				headX + cosR * eyeOffset + sinR * eyeSpread,
				headY + sinR * eyeOffset - cosR * eyeSpread
			);
			this.rightEye.rotation = this.headRotation;

			// Position eyebrows above eyes (for angry expression)
			if (this.leftEyebrow && this.leftEyebrow.visible) {
				const browOffset = headRadius * 0.65; // Slightly forward of eyes
				const browHeight = headRadius * 0.35; // Above eyes
				// Left eyebrow - above left eye
				this.leftEyebrow.position.set(
					headX + cosR * browOffset - sinR * (eyeSpread + browHeight),
					headY + sinR * browOffset + cosR * (eyeSpread + browHeight)
				);
				this.leftEyebrow.rotation = this.headRotation;
			}
			if (this.rightEyebrow && this.rightEyebrow.visible) {
				const browOffset = headRadius * 0.65;
				const browHeight = headRadius * 0.35;
				// Right eyebrow - above right eye
				this.rightEyebrow.position.set(
					headX + cosR * browOffset + sinR * (eyeSpread + browHeight),
					headY + sinR * browOffset - cosR * (eyeSpread + browHeight)
				);
				this.rightEyebrow.rotation = this.headRotation;
			}
		}

		// Only redraw head/eyes when radius changes (expensive operations)
		if (radiusChanged) {
			this.lastHeadRadius = headRadius;
			this.drawHead();
			if (this.config.showEyes) {
				this.drawEyes();
			}
		}

		// Redraw accessory if needed (animated or radius change)
		if (shouldRedraw && this.accessoryGraphics && this.accessoryType !== AccessoryType.NONE) {
			this.drawAccessory();
		}

		// Mark body as dirty - actual drawing happens in updateSkinAnimation or renderBody
		this.bodyDirty = true;

		// Update accessory position and rotation
		// Accessories rotate with head, plus category-based offset for proper positioning
		if (this.accessoryGraphics && this.accessoryType !== AccessoryType.NONE) {
			this.accessoryGraphics.position.set(headX, headY);
			// Apply category-based rotation offset (face items, hats, etc. have different orientations)
			const categoryPosition = getAccessoryPosition(this.accessoryType);
			this.accessoryGraphics.rotation = this.headRotation + categoryPosition.rotationOffset;

			// Manually call drawAccessory if radius changed (already handled above? No, we suppressed it)
			// Actually, logic above handles redrawing. Here we just position.
			if (radiusChanged) {
				// This block is now redundant because shouldRedraw includes radiusChanged
				// But we keep this check clean.
				this.drawAccessory();
			}
		}

		// Update local player effects (aura and marker)
		this.updateLocalPlayerEffects();

		// Update LEVIATHAN effects (crown, aura, particles)
		if (this.isLegendary) {
			this.updateLeviathanEffects(this.lastDt);
		}

		// Update bounty crown/star (top 5 leaderboard players)
		if (this.leaderboardRank >= 1 && this.leaderboardRank <= 5) {
			this.updateBountyCrown();
		}

		// Update kill streak visual effects (flames, glow, particles)
		if (this.killStreakTier) {
			this.updateKillStreakEffects(this.lastDt);
		}
	}

	/**
	 * Get the head position
	 */
	getHeadPosition(): Vector2 {
		const headPoint = this.bodyPoints[0];
		return {
			x: headPoint?.x ?? 0,
			y: headPoint?.y ?? 0,
		};
	}

	/**
	 * Get the head rotation in radians
	 */
	getHeadRotation(): number {
		return this.headRotation;
	}

	/**
	 * Set a world position for the eyes to look at
	 * The pupils will smoothly move toward this target
	 * @param worldX World X coordinate to look at
	 * @param worldY World Y coordinate to look at
	 * @param priority If true, overrides random look-around behavior temporarily
	 */
	setLookTarget(worldX: number, worldY: number, priority: boolean = false): void {
		const headPos = this.getHeadPosition();
		const dx = worldX - headPos.x;
		const dy = worldY - headPos.y;

		// Convert world direction to local eye space (relative to head rotation)
		const cosR = Math.cos(-this.headRotation);
		const sinR = Math.sin(-this.headRotation);
		const localX = dx * cosR - dy * sinR;
		const localY = dx * sinR + dy * cosR;

		// Normalize to -1 to 1 range, clamped
		const dist = Math.sqrt(localX * localX + localY * localY);
		if (dist > 0.01) {
			this.targetPupilOffsetX = Math.max(-1, Math.min(1, localX / dist));
			this.targetPupilOffsetY = Math.max(-1, Math.min(1, localY / dist));
		}

		// If priority target, reset the look-around timer to prevent immediate override
		if (priority) {
			this.lookAroundTimer = 0;
			this.nextLookChange = 1000 + Math.random() * 1000; // Shorter delay before returning to normal
		}
	}

	/**
	 * Get all body points
	 */
	getBodyPoints(): Point[] {
		return this.bodyPoints;
	}

	/**
	 * Get the number of body segments
	 */
	getSegmentCount(): number {
		return this.bodyPoints.length;
	}

	/**
	 * Get the worm body radius
	 */
	getRadius(): number {
		return this.config.radius;
	}

	/**
	 * Get the head radius (score-based from server or config fallback)
	 */
	getHeadRadius(): number {
		return (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;
	}

	/**
	 * Get dynamic segment spacing based on current head radius
	 * Spacing scales with radius to maintain proper segment overlap at all sizes
	 * Factor of 1.33 ensures ~25% overlap between adjacent segments
	 */
	private getDynamicSpacing(): number {
		const headRadius = this.getHeadRadius();
		// Spacing should be ~1.33x radius for proper overlap (diameter * 0.67)
		// This ensures segments overlap by about 33% of their diameter
		return headRadius * 1.33;
	}

	/**
	 * Set the worm body radius and redraw
	 */
	setRadius(radius: number): void {
		if (this.config.radius === radius) return;
		this.config.radius = radius;
		// Update segment radii
		this.segmentRadii = new Array(this.bodyPoints.length).fill(radius);
		// Update lastHeadRadius to trigger head redraw
		this.lastHeadRadius = 0;
		// Force redraw with new size
		this.drawHead();
		this.forceBodyRedraw();
	}

	/**
	 * Get all segment positions and radii for collision detection
	 */
	getSegmentPositions(): Array<{ x: number; y: number; radius: number }> {
		return this.bodyPoints.map((point, index) => ({
			x: point.x,
			y: point.y,
			radius: this.segmentRadii[index] ?? this.config.radius,
		}));
	}

	/**
	 * Set worm color
	 */
	setColor(color: number): void {
		if (this.config.color === color) return;

		this.config.color = color;

		// Invalidate color cache and force redraw
		this.cachedColors = [];
		this.drawHead();
		this.forceBodyRedraw();
	}

	/**
	 * Set worm skin data
	 */
	setSkin(skin: WormSkinData): void {
		this.skinData = skin;

		// Update color from skin
		if (skin.primaryColor) {
			this.config.color = hexToNumber(skin.primaryColor);
		}

		// Invalidate color cache and force redraw
		this.cachedColors = [];
		this.drawHead();
		this.forceBodyRedraw();
	}

	/**
	 * Get current skin data
	 */
	getSkin(): WormSkinData | null {
		return this.skinData;
	}

	/**
	 * Set accessory type for the worm
	 */
	setAccessory(type: AccessoryType): void {
		if (this.accessoryType === type) return;

		this.accessoryType = type;

		// Remove existing accessory
		if (this.accessoryGraphics) {
			this.container.removeChild(this.accessoryGraphics);
			this.accessoryGraphics.destroy();
			this.accessoryGraphics = null;
		}

		// Create new accessory if not NONE
		if (type !== AccessoryType.NONE) {
			this.accessoryGraphics = new Graphics();
			this.container.addChild(this.accessoryGraphics);
			this.drawAccessory();
		}
	}

	/**
	 * Get current accessory type
	 */
	getAccessory(): AccessoryType {
		return this.accessoryType;
	}

	/**
	 * Draw the accessory based on type
	 */
	private drawAccessory(): void {
		if (!this.accessoryGraphics || this.accessoryType === AccessoryType.NONE) return;

		const g = this.accessoryGraphics;
		g.clear();

		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		switch (this.accessoryType) {
			case AccessoryType.HEADPHONES:
				this.drawHeadphones(g, headRadius);
				break;
			case AccessoryType.PARTY_HAT:
				this.drawPartyHat(g, headRadius);
				break;
			case AccessoryType.CROWN:
				this.drawCrown(g, headRadius);
				break;
			case AccessoryType.SUNGLASSES:
				this.drawSunglasses(g, headRadius);
				break;
			case AccessoryType.BOW_TIE:
				this.drawBowTie(g, headRadius);
				break;
			case AccessoryType.HALO:
				this.drawHalo(g, headRadius);
				break;
			case AccessoryType.DEVIL_HORNS:
				this.drawDevilHorns(g, headRadius);
				break;
			case AccessoryType.BANDANA:
				this.drawBandana(g, headRadius);
				break;
			case AccessoryType.WIZARD_HAT:
				this.drawWizardHat(g, headRadius);
				break;
			case AccessoryType.ANTENNA:
				this.drawAntenna(g, headRadius);
				break;
			case AccessoryType.TOP_HAT:
				this.drawTopHat(g, headRadius);
				break;
			case AccessoryType.FLOWER_CROWN:
				this.drawFlowerCrown(g, headRadius);
				break;
			case AccessoryType.PIRATE_HAT:
				this.drawPirateHat(g, headRadius);
				break;
			case AccessoryType.VIKING_HELMET:
				this.drawVikingHelmet(g, headRadius);
				break;
			case AccessoryType.CAT_EARS:
				this.drawCatEars(g, headRadius);
				break;
			case AccessoryType.BUNNY_EARS:
				this.drawBunnyEars(g, headRadius);
				break;
			case AccessoryType.COWBOY_HAT:
				this.drawCowboyHat(g, headRadius);
				break;
			case AccessoryType.STAR:
				this.drawStarAccessory(g, headRadius);
				break;
			case AccessoryType.NINJA_MASK:
				this.drawNinjaMask(g, headRadius);
				break;
			case AccessoryType.CHEF_HAT:
				this.drawChefHat(g, headRadius);
				break;
			case AccessoryType.HEART:
				this.drawHeartAccessory(g, headRadius);
				break;
			case AccessoryType.PROPELLER_HAT:
				this.drawPropellerHat(g, headRadius);
				break;

			// === EYES GROUP ===
			case AccessoryType.GLASSES_3D:
				this.drawGlasses3D(g, headRadius);
				break;
			case AccessoryType.MONOCLE:
				this.drawMonocle(g, headRadius);
				break;
			case AccessoryType.VR_HEADSET:
				this.drawVRHeadset(g, headRadius);
				break;
			case AccessoryType.SKI_GOGGLES:
				this.drawSkiGoggles(g, headRadius);
				break;
			case AccessoryType.AVIATORS:
				this.drawAviators(g, headRadius);
				break;
			case AccessoryType.CYCLOPS_VISOR:
				this.drawCyclopsVisor(g, headRadius);
				break;
			case AccessoryType.EYE_PATCH:
				this.drawEyePatch(g, headRadius);
				break;
			case AccessoryType.CUCUMBER_SLICES:
				this.drawCucumberSlices(g, headRadius);
				break;
			case AccessoryType.ANIME_EYES:
				this.drawAnimeEyes(g, headRadius);
				break;

			// === MOUTH GROUP ===
			case AccessoryType.VAMPIRE_FANGS:
				this.drawVampireFangs(g, headRadius);
				break;
			case AccessoryType.BUCK_TEETH:
				this.drawBuckTeeth(g, headRadius);
				break;
			case AccessoryType.MUSTACHE:
				this.drawMustache(g, headRadius);
				break;
			case AccessoryType.BEARD:
				this.drawBeard(g, headRadius);
				break;
			case AccessoryType.MEDICAL_MASK:
				this.drawMedicalMask(g, headRadius);
				break;
			case AccessoryType.CLOWN_NOSE:
				this.drawClownNose(g, headRadius);
				break;
			case AccessoryType.BUBBLEGUM:
				this.drawBubblegum(g, headRadius);
				break;
			case AccessoryType.PIPE:
				this.drawPipe(g, headRadius);
				break;
			case AccessoryType.ZIPPER_MOUTH:
				this.drawZipperMouth(g, headRadius);
				break;
			case AccessoryType.DOG_TONGUE:
				this.drawDogTongue(g, headRadius);
				break;
		}
	}

	/**
	 * Draw headphones accessory (faces forward - wraps around head sides)
	 * In local coordinates: +X = front, +Y = right side, -Y = left side
	 */
	private drawHeadphones(g: Graphics, r: number): void {
		// Headband arcs over the BACK of the head
		// Arc from bottom (+Y) to top (-Y) passing through back (-X)
		g.arc(-r * 0.1, 0, r * 1.05, Math.PI / 2, -Math.PI / 2, false);
		g.stroke({ width: r * 0.15, color: 0x333333 });

		// Left ear cup (-Y)
		g.circle(0, -r * 0.9, r * 0.35);
		g.fill(0x222222);
		g.circle(0, -r * 0.9, r * 0.25);
		g.fill(0x444444);

		// Right ear cup (+Y)
		g.circle(0, r * 0.9, r * 0.35);
		g.fill(0x222222);
		g.circle(0, r * 0.9, r * 0.25);
		g.fill(0x444444);
	}

	/**
	 * Draw party hat accessory - festive cone with confetti and pom pom
	 */
	private drawPartyHat(g: Graphics, r: number): void {
		// Hat shadow for depth
		g.moveTo(-r * 0.65, -r * 0.15);
		g.lineTo(0, -r * 2.15);
		g.lineTo(r * 0.65, -r * 0.15);
		g.closePath();
		g.fill(0xcc4488);

		// Main cone body
		g.moveTo(-r * 0.6, -r * 0.2);
		g.lineTo(0, -r * 2.2);
		g.lineTo(r * 0.6, -r * 0.2);
		g.closePath();
		g.fill(0xff69b4);
		g.stroke({ width: 2, color: 0xff1493 });

		// Festive stripes (multiple colors)
		g.moveTo(-r * 0.52, -r * 0.45);
		g.lineTo(r * 0.52, -r * 0.45);
		g.stroke({ width: r * 0.12, color: 0x00ffff });

		g.moveTo(-r * 0.42, -r * 0.8);
		g.lineTo(r * 0.42, -r * 0.8);
		g.stroke({ width: r * 0.1, color: 0xffff00 });

		g.moveTo(-r * 0.28, -r * 1.2);
		g.lineTo(r * 0.28, -r * 1.2);
		g.stroke({ width: r * 0.08, color: 0x00ff88 });

		g.moveTo(-r * 0.15, -r * 1.55);
		g.lineTo(r * 0.15, -r * 1.55);
		g.stroke({ width: r * 0.06, color: 0xff8800 });

		// Confetti dots scattered on hat
		g.circle(-r * 0.35, -r * 0.6, r * 0.05);
		g.fill(0xffff00);
		g.circle(r * 0.25, -r * 0.55, r * 0.04);
		g.fill(0x00ff00);
		g.circle(-r * 0.15, -r * 1.0, r * 0.04);
		g.fill(0xff0000);
		g.circle(r * 0.2, -r * 0.95, r * 0.035);
		g.fill(0x0088ff);
		g.circle(-r * 0.08, -r * 1.4, r * 0.03);
		g.fill(0xff00ff);
		g.circle(r * 0.1, -r * 1.35, r * 0.03);
		g.fill(0xffffff);

		// Elastic band at base
		g.moveTo(-r * 0.6, -r * 0.2);
		g.lineTo(r * 0.6, -r * 0.2);
		g.stroke({ width: r * 0.06, color: 0xffffff });

		// Fluffy pom pom on top (multiple circles for fluffy effect)
		g.circle(0, -r * 2.2, r * 0.22);
		g.fill(0xffffff);
		g.circle(-r * 0.08, -r * 2.28, r * 0.12);
		g.fill(0xffeeee);
		g.circle(r * 0.1, -r * 2.15, r * 0.1);
		g.fill(0xffffff);
		g.circle(0, -r * 2.35, r * 0.08);
		g.fill(0xfff5f5);

		// Pom pom shine
		g.circle(-r * 0.05, -r * 2.25, r * 0.04);
		g.fill({ color: 0xffffff, alpha: 0.8 });
	}

	/**
	 * Draw crown accessory - luxurious royal crown with gems and details
	 */
	private drawCrown(g: Graphics, r: number): void {
		// Crown shadow/depth layer
		g.moveTo(-r * 0.85, -r * 0.25);
		g.lineTo(-r * 0.85, -r * 0.85);
		g.lineTo(-r * 0.53, -r * 0.55);
		g.lineTo(-r * 0.28, -r * 1.05);
		g.lineTo(0, -r * 0.65);
		g.lineTo(r * 0.28, -r * 1.05);
		g.lineTo(r * 0.53, -r * 0.55);
		g.lineTo(r * 0.85, -r * 0.85);
		g.lineTo(r * 0.85, -r * 0.25);
		g.closePath();
		g.fill(0xb8860b);

		// Crown main body
		g.moveTo(-r * 0.8, -r * 0.3);
		g.lineTo(-r * 0.8, -r * 0.9);
		g.lineTo(-r * 0.5, -r * 0.6);
		g.lineTo(-r * 0.25, -r * 1.1);
		g.lineTo(0, -r * 0.7);
		g.lineTo(r * 0.25, -r * 1.1);
		g.lineTo(r * 0.5, -r * 0.6);
		g.lineTo(r * 0.8, -r * 0.9);
		g.lineTo(r * 0.8, -r * 0.3);
		g.closePath();
		g.fill(0xffd700);
		g.stroke({ width: 2, color: 0xdaa520 });

		// Crown band at base (red velvet look)
		g.rect(-r * 0.75, -r * 0.42, r * 1.5, r * 0.12);
		g.fill(0x8b0000);
		g.stroke({ width: 1, color: 0x5c0000 });

		// Gold trim on band
		g.moveTo(-r * 0.75, -r * 0.42);
		g.lineTo(r * 0.75, -r * 0.42);
		g.stroke({ width: 2, color: 0xffd700 });
		g.moveTo(-r * 0.75, -r * 0.3);
		g.lineTo(r * 0.75, -r * 0.3);
		g.stroke({ width: 2, color: 0xffd700 });

		// Center large jewel (ruby with shine)
		g.circle(0, -r * 0.55, r * 0.14);
		g.fill(0xff0044);
		g.stroke({ width: 1, color: 0xcc0033 });
		g.circle(-r * 0.04, -r * 0.59, r * 0.04);
		g.fill({ color: 0xffffff, alpha: 0.6 });

		// Left jewel (sapphire)
		g.circle(-r * 0.45, -r * 0.55, r * 0.1);
		g.fill(0x0055ff);
		g.stroke({ width: 1, color: 0x0044cc });
		g.circle(-r * 0.48, -r * 0.58, r * 0.03);
		g.fill({ color: 0xffffff, alpha: 0.5 });

		// Right jewel (emerald)
		g.circle(r * 0.45, -r * 0.55, r * 0.1);
		g.fill(0x00cc44);
		g.stroke({ width: 1, color: 0x00aa33 });
		g.circle(r * 0.42, -r * 0.58, r * 0.03);
		g.fill({ color: 0xffffff, alpha: 0.5 });

		// Small diamonds on crown points
		g.circle(-r * 0.25, -r * 1.0, r * 0.06);
		g.fill(0xffffff);
		g.circle(0, -r * 0.62, r * 0.05);
		g.fill(0xffffff);
		g.circle(r * 0.25, -r * 1.0, r * 0.06);
		g.fill(0xffffff);

		// Gold highlights
		g.moveTo(-r * 0.6, -r * 0.75);
		g.lineTo(-r * 0.55, -r * 0.65);
		g.stroke({ width: 2, color: 0xffec8b, alpha: 0.7 });
		g.moveTo(r * 0.55, -r * 0.65);
		g.lineTo(r * 0.6, -r * 0.75);
		g.stroke({ width: 2, color: 0xffec8b, alpha: 0.7 });
	}

	/**
	 * Draw sunglasses accessory (faces forward - in direction of movement)
	 * In local coordinates: +X = front, +Y = right side, -Y = left side
	 */
	private drawSunglasses(g: Graphics, r: number): void {
		const faceOffset = r * 0.5; // Position on the front of the head

		// Bridge (connects the two lenses vertically on the front face)
		g.moveTo(faceOffset, -r * 0.2);
		g.lineTo(faceOffset, r * 0.2);
		g.stroke({ width: r * 0.08, color: 0x111111 });

		// Upper lens (left side of worm)
		g.roundRect(faceOffset - r * 0.35, -r * 0.9, r * 0.5, r * 0.7, r * 0.1);
		g.fill({ color: 0x000000, alpha: 0.8 });
		g.stroke({ width: 2, color: 0x111111 });

		// Lower lens (right side of worm)
		g.roundRect(faceOffset - r * 0.35, r * 0.2, r * 0.5, r * 0.7, r * 0.1);
		g.fill({ color: 0x000000, alpha: 0.8 });
		g.stroke({ width: 2, color: 0x111111 });

		// Shine on lenses
		g.moveTo(faceOffset - r * 0.15, -r * 0.7);
		g.lineTo(faceOffset + r * 0.1, -r * 0.6);
		g.stroke({ width: 2, color: 0xffffff, alpha: 0.4 });

		g.moveTo(faceOffset - r * 0.15, r * 0.4);
		g.lineTo(faceOffset + r * 0.1, r * 0.5);
		g.stroke({ width: 2, color: 0xffffff, alpha: 0.4 });
	}

	/**
	 * Draw bow tie accessory (positioned behind head - "neck" area)
	 * In local coordinates: +X = front, -X = back/behind
	 */
	private drawBowTie(g: Graphics, r: number): void {
		const x = -r * 1.2; // Behind the head (neck area)

		// Top wing (left side visually when facing right)
		g.moveTo(x, 0);
		g.lineTo(x - r * 0.3, -r * 0.6);
		g.lineTo(x + r * 0.3, -r * 0.6);
		g.closePath();
		g.fill(0xff0000);

		// Bottom wing (right side visually)
		g.moveTo(x, 0);
		g.lineTo(x - r * 0.3, r * 0.6);
		g.lineTo(x + r * 0.3, r * 0.6);
		g.closePath();
		g.fill(0xff0000);

		// Center knot
		g.circle(x, 0, r * 0.15);
		g.fill(0xcc0000);
	}

	/**
	 * Draw halo accessory - divine golden ring with heavenly glow
	 */
	private drawHalo(g: Graphics, r: number): void {
		// Outer glow effect (multiple fading rings)
		g.ellipse(0, -r * 1.5, r * 1.0, r * 0.3);
		g.stroke({ width: r * 0.08, color: 0xffd700, alpha: 0.2 });
		g.ellipse(0, -r * 1.5, r * 0.95, r * 0.27);
		g.stroke({ width: r * 0.06, color: 0xffd700, alpha: 0.3 });

		// Main golden halo ring
		g.ellipse(0, -r * 1.5, r * 0.85, r * 0.22);
		g.stroke({ width: r * 0.18, color: 0xffd700 });

		// Bright inner edge
		g.ellipse(0, -r * 1.5, r * 0.7, r * 0.15);
		g.stroke({ width: r * 0.08, color: 0xffec8b });

		// Central glow
		g.ellipse(0, -r * 1.5, r * 0.55, r * 0.1);
		g.stroke({ width: r * 0.05, color: 0xfffff0, alpha: 0.8 });

		// Divine sparkles around halo (Animated shimmer)
		const time = performance.now() / 200;

		// Draw halo particles (sparkles animated around the ring)
		const sparkleOffsets = [0, 2, 4, 1, 5, 3];
		sparkleOffsets.forEach((offset, i) => {
			const sparkleTime = time + offset;
			const scale = 0.5 + Math.sin(sparkleTime) * 0.5;
			if (scale > 0.2) {
				const angle = (i / sparkleOffsets.length) * Math.PI * 2 + time * 0.1;
				const dist = r * 1.6;
				g.circle(Math.cos(angle) * dist, -r * 1.5 + Math.sin(angle) * r * 0.3, r * 0.05 * scale);
				g.fill({ color: 0xffffff, alpha: 0.8 });
			}
		});
	}

	/**
	 * Draw devil horns accessory - menacing curved horns with fiery glow
	 */
	private drawDevilHorns(g: Graphics, r: number): void {
		// Fiery glow behind horns
		g.ellipse(-r * 0.7, -r * 1.3, r * 0.4, r * 0.5);
		g.fill({ color: 0xff4400, alpha: 0.2 });
		g.ellipse(r * 0.7, -r * 1.3, r * 0.4, r * 0.5);
		g.fill({ color: 0xff4400, alpha: 0.2 });

		// Left horn shadow
		g.moveTo(-r * 0.55, -r * 0.45);
		g.quadraticCurveTo(-r * 1.25, -r * 1.75, -r * 0.35, -r * 1.55);
		g.lineTo(-r * 0.55, -r * 0.45);
		g.fill(0x660000);

		// Left horn main
		g.moveTo(-r * 0.5, -r * 0.5);
		g.quadraticCurveTo(-r * 1.2, -r * 1.8, -r * 0.3, -r * 1.6);
		g.lineTo(-r * 0.5, -r * 0.5);
		g.fill(0xcc0000);
		g.stroke({ width: 1, color: 0x990000 });

		// Left horn highlight
		g.moveTo(-r * 0.5, -r * 0.6);
		g.quadraticCurveTo(-r * 0.9, -r * 1.3, -r * 0.35, -r * 1.45);
		g.stroke({ width: 2, color: 0xff3333, alpha: 0.6 });

		// Right horn shadow
		g.moveTo(r * 0.55, -r * 0.45);
		g.quadraticCurveTo(r * 1.25, -r * 1.75, r * 0.35, -r * 1.55);
		g.lineTo(r * 0.55, -r * 0.45);
		g.fill(0x660000);

		// Right horn main
		g.moveTo(r * 0.5, -r * 0.5);
		g.quadraticCurveTo(r * 1.2, -r * 1.8, r * 0.3, -r * 1.6);
		g.lineTo(r * 0.5, -r * 0.5);
		g.fill(0xcc0000);
		g.stroke({ width: 1, color: 0x990000 });

		// Right horn highlight
		g.moveTo(r * 0.5, -r * 0.6);
		g.quadraticCurveTo(r * 0.9, -r * 1.3, r * 0.35, -r * 1.45);
		g.stroke({ width: 2, color: 0xff3333, alpha: 0.6 });

		// Horn tips (sharp and dark with glow)
		g.circle(-r * 0.35, -r * 1.55, r * 0.1);
		g.fill({ color: 0xff2200, alpha: 0.5 });
		g.circle(-r * 0.35, -r * 1.57, r * 0.06);
		g.fill(0x330000);

		g.circle(r * 0.35, -r * 1.55, r * 0.1);
		g.fill({ color: 0xff2200, alpha: 0.5 });
		g.circle(r * 0.35, -r * 1.57, r * 0.06);
		g.fill(0x330000);

		// Horn base rings
		g.arc(-r * 0.52, -r * 0.55, r * 0.15, Math.PI * 0.3, Math.PI * 1.2, false);
		g.stroke({ width: 2, color: 0x440000 });
		g.arc(r * 0.52, -r * 0.55, r * 0.15, Math.PI * 1.8, Math.PI * 0.7, false);
		g.stroke({ width: 2, color: 0x440000 });
	}

	/**
	 * Draw bandana accessory
	 */
	private drawBandana(g: Graphics, r: number): void {
		// Bandana wrap around head
		g.arc(0, 0, r * 1.05, -Math.PI * 0.8, Math.PI * 0.8, false);
		g.stroke({ width: r * 0.25, color: 0xff4444 });

		// Knot at back/top
		g.circle(-r * 0.3, -r * 0.9, r * 0.15);
		g.fill(0xff4444);
		g.circle(r * 0.1, -r * 0.85, r * 0.12);
		g.fill(0xff4444);

		// Trailing ends
		g.moveTo(-r * 0.3, -r * 0.9);
		g.quadraticCurveTo(-r * 0.8, -r * 1.2, -r * 0.6, -r * 1.5);
		g.stroke({ width: r * 0.12, color: 0xff4444 });

		g.moveTo(r * 0.1, -r * 0.85);
		g.quadraticCurveTo(r * 0.5, -r * 1.0, r * 0.4, -r * 1.3);
		g.stroke({ width: r * 0.1, color: 0xff4444 });
	}

	/**
	 * Draw wizard hat accessory - mystical hat with stars and moons
	 */
	private drawWizardHat(g: Graphics, r: number): void {
		// Hat shadow for depth
		g.ellipse(0, -r * 0.25, r * 1.25, r * 0.28);
		g.fill(0x1a0a3e);

		// Brim
		g.ellipse(0, -r * 0.3, r * 1.2, r * 0.25);
		g.fill(0x2a1a5e);
		g.stroke({ width: 2, color: 0x5533aa });

		// Cone shadow
		g.moveTo(-r * 0.75, -r * 0.25);
		g.quadraticCurveTo(-r * 0.55, -r * 1.45, r * 0.05, -r * 2.45);
		g.quadraticCurveTo(r * 0.55, -r * 1.45, r * 0.75, -r * 0.25);
		g.closePath();
		g.fill(0x1a0a3e);

		// Cone main
		g.moveTo(-r * 0.7, -r * 0.3);
		g.quadraticCurveTo(-r * 0.5, -r * 1.5, 0, -r * 2.5);
		g.quadraticCurveTo(r * 0.5, -r * 1.5, r * 0.7, -r * 0.3);
		g.closePath();
		g.fill(0x2a1a5e);
		g.stroke({ width: 2, color: 0x5533aa });

		// Decorative band
		g.moveTo(-r * 0.6, -r * 0.5);
		g.quadraticCurveTo(0, -r * 0.65, r * 0.6, -r * 0.5);
		g.stroke({ width: r * 0.12, color: 0x8844cc });

		// Moon crescent
		g.arc(-r * 0.3, -r * 0.9, r * 0.15, 0.5, Math.PI * 1.5, false);
		g.stroke({ width: 3, color: 0xffeedd });
		g.arc(-r * 0.3, -r * 0.9, r * 0.1, 0.3, Math.PI * 1.3, false);
		g.stroke({ width: 2, color: 0xffffff });

		// Stars decoration with glow effect
		this.drawStar(g, r * 0.15, -r * 1.2, r * 0.16, 0xffd700);
		this.drawStar(g, r * 0.35, -r * 1.0, r * 0.1, 0xffee88);
		this.drawStar(g, -r * 0.15, -r * 1.5, r * 0.14, 0xffd700);
		this.drawStar(g, r * 0.1, -r * 1.85, r * 0.11, 0xffd700);
		this.drawStar(g, -r * 0.25, -r * 1.9, r * 0.08, 0xffee88);

		// Sparkle dots
		g.circle(-r * 0.4, -r * 1.3, r * 0.03);
		g.fill(0xffffff);
		g.circle(r * 0.3, -r * 1.6, r * 0.025);
		g.fill(0xffffff);
		g.circle(-r * 0.05, -r * 2.1, r * 0.03);
		g.fill(0xffffff);

		// Magical tip glow
		g.circle(0, -r * 2.5, r * 0.08);
		g.fill({ color: 0xffd700, alpha: 0.8 });
		g.circle(0, -r * 2.5, r * 0.05);
		g.fill(0xffffff);
	}

	/**
	 * Draw antenna accessory
	 */
	private drawAntenna(g: Graphics, r: number): void {
		// Antenna stalk
		g.moveTo(0, -r * 0.5);
		g.lineTo(0, -r * 2.0);
		g.stroke({ width: r * 0.08, color: 0x666666 });

		// Glowing ball at top
		g.circle(0, -r * 2.0, r * 0.25);
		g.fill(0x00ff00);
		g.circle(0, -r * 2.0, r * 0.18);
		g.fill({ color: 0xaaffaa, alpha: 0.8 });
	}

	/**
	 * Helper to draw a simple star
	 */
	private drawStar(g: Graphics, x: number, y: number, size: number, color: number): void {
		const points = 5;
		const outerRadius = size;
		const innerRadius = size * 0.4;

		g.moveTo(x, y - outerRadius);
		for (let i = 0; i < points * 2; i++) {
			const radius = i % 2 === 0 ? outerRadius : innerRadius;
			const angle = (i * Math.PI) / points - Math.PI / 2;
			g.lineTo(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
		}
		g.closePath();
		g.fill(color);
	}

	/**
	 * Draw top hat accessory - elegant gentleman's top hat
	 */
	private drawTopHat(g: Graphics, r: number): void {
		// Shadow under brim
		g.ellipse(0, -r * 0.45, r * 1.35, r * 0.22);
		g.fill(0x0a0a0a);

		// Brim
		g.ellipse(0, -r * 0.5, r * 1.3, r * 0.2);
		g.fill(0x1a1a1a);
		g.stroke({ width: 2, color: 0x333333 });

		// Brim shine
		g.ellipse(0, -r * 0.52, r * 1.1, r * 0.12);
		g.fill({ color: 0x444444, alpha: 0.3 });

		// Hat body shadow
		g.roundRect(-r * 0.72, -r * 2.15, r * 1.44, r * 1.65, r * 0.1);
		g.fill(0x0a0a0a);

		// Hat body (cylinder)
		g.roundRect(-r * 0.7, -r * 2.2, r * 1.4, r * 1.7, r * 0.1);
		g.fill(0x1a1a1a);
		g.stroke({ width: 2, color: 0x333333 });

		// Hat top edge highlight
		g.moveTo(-r * 0.6, -r * 2.2);
		g.lineTo(r * 0.6, -r * 2.2);
		g.stroke({ width: 3, color: 0x2a2a2a });

		// Satin ribbon band
		g.rect(-r * 0.72, -r * 0.95, r * 1.44, r * 0.28);
		g.fill(0x8b0000);
		g.rect(-r * 0.7, -r * 0.93, r * 1.4, r * 0.24);
		g.fill(0xcc0000);

		// Ribbon bow
		g.ellipse(-r * 0.55, -r * 0.81, r * 0.12, r * 0.08);
		g.fill(0xcc0000);
		g.circle(-r * 0.55, -r * 0.81, r * 0.06);
		g.fill(0x990000);

		// Satin shine on body
		g.rect(-r * 0.45, -r * 2.05, r * 0.12, r * 1.3);
		g.fill({ color: 0xffffff, alpha: 0.12 });
		g.rect(-r * 0.3, -r * 2.05, r * 0.05, r * 1.3);
		g.fill({ color: 0xffffff, alpha: 0.08 });

		// Top surface shine
		g.ellipse(0, -r * 2.15, r * 0.5, r * 0.08);
		g.fill({ color: 0x333333, alpha: 0.5 });
	}

	/**
	 * Draw flower crown accessory
	 */
	private drawFlowerCrown(g: Graphics, r: number): void {
		const flowerPositions = [
			{ x: -r * 0.8, y: -r * 0.6 },
			{ x: -r * 0.4, y: -r * 0.9 },
			{ x: 0, y: -r * 1.0 },
			{ x: r * 0.4, y: -r * 0.9 },
			{ x: r * 0.8, y: -r * 0.6 },
		];
		const flowerColors = [0xff69b4, 0xffb6c1, 0xff1493, 0xffc0cb, 0xff69b4];

		// Green vine base
		g.arc(0, 0, r * 0.95, -Math.PI * 0.75, Math.PI * 0.75, true);
		g.stroke({ width: r * 0.1, color: 0x228b22 });

		// Draw flowers
		flowerPositions.forEach((pos, i) => {
			const flowerColor = flowerColors[i] ?? 0xff69b4;
			// Petals
			for (let p = 0; p < 5; p++) {
				const angle = (p / 5) * Math.PI * 2;
				const px = pos.x + Math.cos(angle) * r * 0.15;
				const py = pos.y + Math.sin(angle) * r * 0.15;
				g.circle(px, py, r * 0.12);
				g.fill(flowerColor);
			}
			// Center
			g.circle(pos.x, pos.y, r * 0.08);
			g.fill(0xffff00);
		});
	}

	/**
	 * Draw pirate hat accessory - detailed tricorn with skull and crossbones
	 */
	private drawPirateHat(g: Graphics, r: number): void {
		// Hat shadow
		g.moveTo(-r * 1.25, -r * 0.25);
		g.quadraticCurveTo(-r * 0.85, -r * 1.45, 0, -r * 1.75);
		g.quadraticCurveTo(r * 0.85, -r * 1.45, r * 1.25, -r * 0.25);
		g.quadraticCurveTo(r * 0.55, -r * 0.45, 0, -r * 0.25);
		g.quadraticCurveTo(-r * 0.55, -r * 0.45, -r * 1.25, -r * 0.25);
		g.fill(0x0a0a0a);

		// Main hat body (tricorn shape)
		g.moveTo(-r * 1.2, -r * 0.3);
		g.quadraticCurveTo(-r * 0.8, -r * 1.5, 0, -r * 1.8);
		g.quadraticCurveTo(r * 0.8, -r * 1.5, r * 1.2, -r * 0.3);
		g.quadraticCurveTo(r * 0.5, -r * 0.5, 0, -r * 0.3);
		g.quadraticCurveTo(-r * 0.5, -r * 0.5, -r * 1.2, -r * 0.3);
		g.fill(0x1a1a1a);
		g.stroke({ width: 2, color: 0x2a2a2a });

		// Leather texture lines
		g.moveTo(-r * 0.5, -r * 1.0);
		g.quadraticCurveTo(-r * 0.2, -r * 1.3, 0, -r * 1.5);
		g.stroke({ width: 1, color: 0x2a2a2a, alpha: 0.5 });
		g.moveTo(r * 0.5, -r * 1.0);
		g.quadraticCurveTo(r * 0.2, -r * 1.3, 0, -r * 1.5);
		g.stroke({ width: 1, color: 0x2a2a2a, alpha: 0.5 });

		// Gold trim (ornate)
		g.moveTo(-r * 1.05, -r * 0.38);
		g.quadraticCurveTo(-r * 0.65, -r * 1.15, 0, -r * 1.48);
		g.quadraticCurveTo(r * 0.65, -r * 1.15, r * 1.05, -r * 0.38);
		g.stroke({ width: r * 0.1, color: 0xdaa520 });
		g.moveTo(-r * 1.0, -r * 0.4);
		g.quadraticCurveTo(-r * 0.6, -r * 1.12, 0, -r * 1.45);
		g.quadraticCurveTo(r * 0.6, -r * 1.12, r * 1.0, -r * 0.4);
		g.stroke({ width: r * 0.06, color: 0xffd700 });

		// Skull
		g.circle(0, -r * 0.85, r * 0.22);
		g.fill(0xf5f5dc);
		g.stroke({ width: 1, color: 0xccccaa });
		// Skull jaw
		g.roundRect(-r * 0.12, -r * 0.72, r * 0.24, r * 0.12, r * 0.03);
		g.fill(0xf5f5dc);

		// Eye sockets
		g.circle(-r * 0.08, -r * 0.9, r * 0.06);
		g.fill(0x1a1a1a);
		g.circle(r * 0.08, -r * 0.9, r * 0.06);
		g.fill(0x1a1a1a);
		// Nose hole
		g.moveTo(0, -r * 0.82);
		g.lineTo(-r * 0.03, -r * 0.76);
		g.lineTo(r * 0.03, -r * 0.76);
		g.closePath();
		g.fill(0x1a1a1a);

		// Crossbones with rounded ends
		g.moveTo(-r * 0.28, -r * 0.62);
		g.lineTo(r * 0.28, -r * 0.52);
		g.stroke({ width: r * 0.06, color: 0xf5f5dc });
		g.moveTo(r * 0.28, -r * 0.62);
		g.lineTo(-r * 0.28, -r * 0.52);
		g.stroke({ width: r * 0.06, color: 0xf5f5dc });
		// Bone ends
		g.circle(-r * 0.28, -r * 0.62, r * 0.04);
		g.fill(0xf5f5dc);
		g.circle(r * 0.28, -r * 0.62, r * 0.04);
		g.fill(0xf5f5dc);
		g.circle(-r * 0.28, -r * 0.52, r * 0.04);
		g.fill(0xf5f5dc);
		g.circle(r * 0.28, -r * 0.52, r * 0.04);
		g.fill(0xf5f5dc);

		// Red feather
		g.moveTo(-r * 0.3, -r * 1.5);
		g.quadraticCurveTo(-r * 0.6, -r * 2.2, -r * 0.2, -r * 2.4);
		g.quadraticCurveTo(-r * 0.4, -r * 2.0, -r * 0.2, -r * 1.5);
		g.fill(0xcc0000);
		g.stroke({ width: 1, color: 0x990000 });
	}

	/**
	 * Draw viking helmet accessory - epic Norse warrior helmet with horns
	 */
	private drawVikingHelmet(g: Graphics, r: number): void {
		// Left horn shadow
		g.moveTo(-r * 0.95, -r * 0.45);
		g.quadraticCurveTo(-r * 1.85, -r * 0.95, -r * 1.45, -r * 1.95);
		g.quadraticCurveTo(-r * 1.55, -r * 1.15, -r * 0.9, -r * 0.6);
		g.fill(0xb8a67a);

		// Right horn shadow
		g.moveTo(r * 0.95, -r * 0.45);
		g.quadraticCurveTo(r * 1.85, -r * 0.95, r * 1.45, -r * 1.95);
		g.quadraticCurveTo(r * 1.55, -r * 1.15, r * 0.9, -r * 0.6);
		g.fill(0xb8a67a);

		// Left horn main
		g.moveTo(-r * 0.9, -r * 0.5);
		g.quadraticCurveTo(-r * 1.8, -r * 1.0, -r * 1.4, -r * 2.0);
		g.quadraticCurveTo(-r * 1.5, -r * 1.2, -r * 0.85, -r * 0.65);
		g.fill(0xf5f0d8);
		g.stroke({ width: 1, color: 0xd4c9a0 });

		// Left horn ridges
		g.moveTo(-r * 1.0, -r * 0.7);
		g.quadraticCurveTo(-r * 1.4, -r * 1.0, -r * 1.35, -r * 1.5);
		g.stroke({ width: 2, color: 0xd4c9a0, alpha: 0.5 });
		g.moveTo(-r * 1.1, -r * 0.8);
		g.quadraticCurveTo(-r * 1.5, -r * 1.1, -r * 1.38, -r * 1.7);
		g.stroke({ width: 1, color: 0xd4c9a0, alpha: 0.3 });

		// Right horn main
		g.moveTo(r * 0.9, -r * 0.5);
		g.quadraticCurveTo(r * 1.8, -r * 1.0, r * 1.4, -r * 2.0);
		g.quadraticCurveTo(r * 1.5, -r * 1.2, r * 0.85, -r * 0.65);
		g.fill(0xf5f0d8);
		g.stroke({ width: 1, color: 0xd4c9a0 });

		// Right horn ridges
		g.moveTo(r * 1.0, -r * 0.7);
		g.quadraticCurveTo(r * 1.4, -r * 1.0, r * 1.35, -r * 1.5);
		g.stroke({ width: 2, color: 0xd4c9a0, alpha: 0.5 });
		g.moveTo(r * 1.1, -r * 0.8);
		g.quadraticCurveTo(r * 1.5, -r * 1.1, r * 1.38, -r * 1.7);
		g.stroke({ width: 1, color: 0xd4c9a0, alpha: 0.3 });

		// Helmet dome shadow
		g.arc(0, -r * 0.25, r * 1.05, Math.PI, 0, false);
		g.fill(0x5a4a30);

		// Helmet dome (leather/metal)
		g.arc(0, -r * 0.3, r * 1.0, Math.PI, 0, false);
		g.fill(0x8b7355);
		g.stroke({ width: 2, color: 0x6b5535 });

		// Metal band (main)
		g.arc(0, -r * 0.3, r * 1.02, Math.PI, 0, false);
		g.stroke({ width: r * 0.14, color: 0x707070 });

		// Metal band highlight
		g.arc(0, -r * 0.35, r * 0.98, Math.PI * 0.85, Math.PI * 0.15, false);
		g.stroke({ width: r * 0.06, color: 0x999999 });

		// Center ridge (riveted metal)
		g.moveTo(0, -r * 1.28);
		g.lineTo(0, -r * 0.3);
		g.stroke({ width: r * 0.12, color: 0x555555 });
		g.moveTo(0, -r * 1.3);
		g.lineTo(0, -r * 0.35);
		g.stroke({ width: r * 0.08, color: 0x777777 });

		// Rivets on center ridge
		g.circle(0, -r * 1.1, r * 0.04);
		g.fill(0x888888);
		g.circle(0, -r * 0.85, r * 0.04);
		g.fill(0x888888);
		g.circle(0, -r * 0.6, r * 0.04);
		g.fill(0x888888);

		// Nose guard with detail
		g.moveTo(-r * 0.04, -r * 0.3);
		g.lineTo(-r * 0.03, r * 0.25);
		g.lineTo(r * 0.03, r * 0.25);
		g.lineTo(r * 0.04, -r * 0.3);
		g.fill(0x666666);
		g.stroke({ width: 1, color: 0x555555 });

		// Nose guard shine
		g.moveTo(-r * 0.01, -r * 0.2);
		g.lineTo(r * 0.01, r * 0.1);
		g.stroke({ width: 2, color: 0x888888, alpha: 0.5 });

		// Horn base metal rings
		g.arc(-r * 0.9, -r * 0.55, r * 0.12, Math.PI * 0.2, Math.PI * 1.3, false);
		g.stroke({ width: 3, color: 0x888888 });
		g.arc(r * 0.9, -r * 0.55, r * 0.12, Math.PI * 1.7, Math.PI * 0.8, false);
		g.stroke({ width: 3, color: 0x888888 });
	}

	/**
	 * Draw cat ears accessory
	 */
	private drawCatEars(g: Graphics, r: number): void {
		// Left ear outer
		g.moveTo(-r * 0.9, -r * 0.2);
		g.lineTo(-r * 0.7, -r * 1.4);
		g.lineTo(-r * 0.3, -r * 0.4);
		g.closePath();
		g.fill(0x444444);

		// Left ear inner
		g.moveTo(-r * 0.8, -r * 0.35);
		g.lineTo(-r * 0.7, -r * 1.1);
		g.lineTo(-r * 0.45, -r * 0.45);
		g.closePath();
		g.fill(0xffb6c1);

		// Right ear outer
		g.moveTo(r * 0.9, -r * 0.2);
		g.lineTo(r * 0.7, -r * 1.4);
		g.lineTo(r * 0.3, -r * 0.4);
		g.closePath();
		g.fill(0x444444);

		// Right ear inner
		g.moveTo(r * 0.8, -r * 0.35);
		g.lineTo(r * 0.7, -r * 1.1);
		g.lineTo(r * 0.45, -r * 0.45);
		g.closePath();
		g.fill(0xffb6c1);
	}

	/**
	 * Draw bunny ears accessory
	 */
	private drawBunnyEars(g: Graphics, r: number): void {
		// Left ear
		g.ellipse(-r * 0.6, -r * 1.3, r * 0.25, r * 0.8);
		g.fill(0xffffff);
		g.ellipse(-r * 0.6, -r * 1.3, r * 0.15, r * 0.5);
		g.fill(0xffb6c1);

		// Right ear
		g.ellipse(r * 0.6, -r * 1.3, r * 0.25, r * 0.8);
		g.fill(0xffffff);
		g.ellipse(r * 0.6, -r * 1.3, r * 0.15, r * 0.5);
		g.fill(0xffb6c1);
	}

	/**
	 * Draw cowboy hat accessory
	 */
	private drawCowboyHat(g: Graphics, r: number): void {
		// Wide brim
		g.ellipse(0, -r * 0.3, r * 1.5, r * 0.35);
		g.fill(0x8b4513);
		g.stroke({ width: 2, color: 0x654321 });

		// Crown of hat
		g.roundRect(-r * 0.7, -r * 1.2, r * 1.4, r * 0.9, r * 0.2);
		g.fill(0x8b4513);
		g.stroke({ width: 2, color: 0x654321 });

		// Top dent
		g.ellipse(0, -r * 1.2, r * 0.5, r * 0.15);
		g.fill(0x6b3510);

		// Hat band
		g.rect(-r * 0.65, -r * 0.5, r * 1.3, r * 0.12);
		g.fill(0x444444);
	}

	/**
	 * Draw star accessory (floating above head)
	 */
	private drawStarAccessory(g: Graphics, r: number): void {
		// Glow effect
		g.circle(0, -r * 1.5, r * 0.7);
		g.fill({ color: 0xffd700, alpha: 0.3 });

		// Main star
		this.drawStar(g, 0, -r * 1.5, r * 0.5, 0xffd700);

		// Inner bright core
		g.circle(0, -r * 1.5, r * 0.15);
		g.fill(0xffff99);
	}

	/**
	 * Draw ninja mask accessory
	 * Fixed: Spans across vertical width (Y) instead of length (X)
	 */
	private drawNinjaMask(g: Graphics, r: number): void {
		// Black mask covering most of face (front of head)
		// Wide in Y (1.1), thin in X (0.5)
		g.ellipse(r * 0.2, 0, r * 0.5, r * 1.05);
		g.fill(0x222222);

		// Knot behind head
		g.circle(-r * 0.8, -r * 0.4, r * 0.15);
		g.fill(0x222222);
		g.circle(-r * 0.8, r * 0.4, r * 0.15);
		g.fill(0x222222);

		// Eye opening (skin color reveal - assuming generic skin color or just dark hole?)
		// Actually usually ninja masks show the eyes.
		// Let's create a slit that reveals the eyes underneath
		g.roundRect(0, -r * 0.35, r * 0.6, r * 0.7, r * 0.1);
		// Masking hack: We can't easily "erase" to show eyes behind in just one Graphics layer ordering.
		// Instead we draw the "hole" as the skin color? No, we don't know skin color easily here.
		// Better: Draw the mask as top and bottom bands.

		// Clear previous ellipse
		g.clear();

		// Top band (forehead)
		g.moveTo(r * 0.6, -r * 0.35);
		g.bezierCurveTo(r * 0.8, -r * 1.2, -r * 0.5, -r * 1.1, -r * 0.5, -r * 0.5);
		g.lineTo(-r * 0.5, -r * 0.35);
		g.closePath();
		g.fill(0x222222);

		// Bottom band (nose/mouth)
		g.moveTo(r * 0.6, r * 0.35);
		g.bezierCurveTo(r * 0.8, r * 1.2, -r * 0.5, r * 1.1, -r * 0.5, r * 0.5);
		g.lineTo(-r * 0.5, r * 0.35);
		g.closePath();
		g.fill(0x222222);

		// Side connections
		g.rect(-r * 0.6, -r * 1.0, r * 0.4, r * 2.0); // wrap around back
		g.fill(0x222222);

		// Since this is complex to mask, let's just draw the "mask" with a hole by drawing two big blobs top/bottom
		// Simplified: Draw big mask, draw "hole" in dark color (shadowed skin)
		g.clear();

		// Main mask wrap
		g.arc(0, 0, r * 1.05, -Math.PI * 0.6, Math.PI * 0.6, false); // Front arc? No

		// Simple approach: Band across face with hole
		g.roundRect(-r * 0.2, -r * 1.0, r * 1.2, r * 2.0, r * 0.5); // Covers whole face
		g.fill(0x222222);

		// "Hole" for eyes - dark skin tone approximation or just open
		g.roundRect(r * 0.2, -r * 0.4, r * 0.7, r * 0.8, r * 0.1);
		g.fill(0xccaa88); // Generic skin tone... not perfect but works
	}

	/**
	 * Draw chef hat accessory
	 */
	private drawChefHat(g: Graphics, r: number): void {
		// Hat base
		g.rect(-r * 0.8, -r * 0.5, r * 1.6, r * 0.4);
		g.fill(0xffffff);
		g.stroke({ width: 2, color: 0xcccccc });

		// Puffy top - multiple overlapping circles
		g.circle(-r * 0.4, -r * 1.2, r * 0.5);
		g.fill(0xffffff);
		g.circle(r * 0.4, -r * 1.2, r * 0.5);
		g.fill(0xffffff);
		g.circle(0, -r * 1.4, r * 0.55);
		g.fill(0xffffff);
		g.circle(-r * 0.2, -r * 0.9, r * 0.4);
		g.fill(0xffffff);
		g.circle(r * 0.2, -r * 0.9, r * 0.4);
		g.fill(0xffffff);

		// Subtle shadows for depth
		g.circle(-r * 0.4, -r * 1.15, r * 0.45);
		g.stroke({ width: 1, color: 0xdddddd });
		g.circle(r * 0.4, -r * 1.15, r * 0.45);
		g.stroke({ width: 1, color: 0xdddddd });
	}

	/**
	 * Draw heart accessory (floating above head)
	 */
	private drawHeartAccessory(g: Graphics, r: number): void {
		// Glow effect
		g.circle(0, -r * 1.4, r * 0.6);
		g.fill({ color: 0xff69b4, alpha: 0.3 });

		// Heart shape using bezier curves
		const hx = 0;
		const hy = -r * 1.3;
		const size = r * 0.5;

		g.moveTo(hx, hy + size * 0.3);
		g.bezierCurveTo(
			hx - size * 0.5, hy - size * 0.3,
			hx - size, hy + size * 0.3,
			hx, hy + size
		);
		g.bezierCurveTo(
			hx + size, hy + size * 0.3,
			hx + size * 0.5, hy - size * 0.3,
			hx, hy + size * 0.3
		);
		g.fill(0xff69b4);

		// Highlight
		g.circle(hx - size * 0.25, hy + size * 0.1, size * 0.15);
		g.fill({ color: 0xffffff, alpha: 0.5 });
	}

	// ==========================================
	// NEW ACCESSORIES IMPLEMENTATION
	// ==========================================

	// --- EYES GROUP ---

	private drawGlasses3D(g: Graphics, r: number): void {
		const faceX = r * 0.4;
		// Frames
		g.rect(faceX, -r * 0.8, r * 0.1, r * 1.6); // Center vertical? No, bridge
		// Left lens frame (Cyan)
		g.rect(faceX, -r * 0.8, r * 0.2, r * 0.7);
		g.fill(0xffffff);
		// Right lens frame (Red)
		g.rect(faceX, r * 0.1, r * 0.2, r * 0.7);
		g.fill(0xffffff);

		// Lenses
		g.rect(faceX + r * 0.05, -r * 0.75, r * 0.15, r * 0.6);
		g.fill({ color: 0x00ffff, alpha: 0.6 }); // Cyan
		g.rect(faceX + r * 0.05, r * 0.15, r * 0.15, r * 0.6);
		g.fill({ color: 0xff0000, alpha: 0.6 }); // Red

		// Bridge
		g.rect(faceX + r * 0.05, -r * 0.1, r * 0.1, r * 0.2);
		g.fill(0xffffff);
	}

	private drawMonocle(g: Graphics, r: number): void {
		// Single lens on right eye (+Y)
		g.circle(r * 0.5, r * 0.45, r * 0.3);
		g.stroke({ width: r * 0.05, color: 0xffd700 }); // Gold rim
		g.fill({ color: 0xaaddff, alpha: 0.3 }); // Glass

		// Chain
		g.moveTo(r * 0.5, r * 0.75);
		g.quadraticCurveTo(r * 0.2, r * 0.9, -r * 0.5, r * 0.8);
		g.stroke({ width: r * 0.03, color: 0xffd700 });
	}

	private drawVRHeadset(g: Graphics, r: number): void {
		// Boxy headset cover
		g.roundRect(r * 0.4, -r * 0.7, r * 0.6, r * 1.4, r * 0.1);
		g.fill(0x111111); // Black body
		g.stroke({ width: 2, color: 0x333333 });

		// Front lights (cyberpunk style)
		g.rect(r * 1.0, -r * 0.6, r * 0.05, r * 1.2);
		g.fill(0x00ff00); // Power light strip

		// Strap
		g.moveTo(r * 0.4, -r * 0.6);
		g.lineTo(-r * 0.5, -r * 0.8); // Side strap
		g.stroke({ width: r * 0.1, color: 0x222222 });
		g.moveTo(r * 0.4, r * 0.6);
		g.lineTo(-r * 0.5, r * 0.8);
		g.stroke({ width: r * 0.1, color: 0x222222 });
	}

	private drawSkiGoggles(g: Graphics, r: number): void {
		// Large single visor wrap
		g.roundRect(r * 0.3, -r * 0.8, r * 0.5, r * 1.6, r * 0.2);
		g.fill({ color: 0xffaa00, alpha: 0.8 }); // Orange/Gold reflective visor
		g.stroke({ width: r * 0.1, color: 0x333333 }); // Frame

		// Reflection stripe
		g.moveTo(r * 0.4, -r * 0.6);
		g.lineTo(r * 0.5, r * 0.6);
		g.stroke({ width: r * 0.15, color: 0xffffff, alpha: 0.4 });
	}

	private drawAviators(g: Graphics, r: number): void {
		// Thin wire frame
		const wireColor = 0xdddddd;
		const lensColor = 0x222222;

		// Left lens (-Y) teardrop shape
		g.moveTo(r * 0.5, -r * 0.1);
		g.bezierCurveTo(r * 0.8, -r * 0.1, r * 0.8, -r * 0.8, r * 0.5, -r * 0.8);
		g.bezierCurveTo(r * 0.3, -r * 0.8, r * 0.3, -r * 0.4, r * 0.5, -r * 0.1);
		g.fill({ color: lensColor, alpha: 0.9 });
		g.stroke({ width: r * 0.03, color: wireColor });

		// Right lens (+Y)
		g.moveTo(r * 0.5, r * 0.1);
		g.bezierCurveTo(r * 0.8, r * 0.1, r * 0.8, r * 0.8, r * 0.5, r * 0.8);
		g.bezierCurveTo(r * 0.3, r * 0.8, r * 0.3, r * 0.4, r * 0.5, r * 0.1);
		g.fill({ color: lensColor, alpha: 0.9 });
		g.stroke({ width: r * 0.03, color: wireColor });

		// Double bridge
		g.moveTo(r * 0.5, -r * 0.1);
		g.lineTo(r * 0.5, r * 0.1);
		g.stroke({ width: r * 0.03, color: wireColor });
		g.moveTo(r * 0.55, -r * 0.1);
		g.lineTo(r * 0.55, r * 0.1);
		g.stroke({ width: r * 0.03, color: wireColor });
	}

	private drawCyclopsVisor(g: Graphics, r: number): void {
		// X-Men style visor
		g.rect(r * 0.4, -r * 0.85, r * 0.4, r * 1.7);
		g.fill(0xddbb00); // Gold metal

		// Red laser slit
		g.rect(r * 0.55, -r * 0.8, r * 0.1, r * 1.6);
		// Pulsing alpha
		const laserAlpha = 0.6 + Math.sin(performance.now() / 150) * 0.4;
		g.fill({ color: 0xff0000, alpha: laserAlpha });

		// Glow - expands and contracts
		const glowWidth = r * 0.05 * (1 + Math.sin(performance.now() / 100) * 0.5);
		g.rect(r * 0.65, -r * 0.8, glowWidth, r * 1.6);
		g.fill({ color: 0xff8888, alpha: 0.5 });
	}

	private drawEyePatch(g: Graphics, r: number): void {
		// Patch on Left Eye (-Y)
		g.circle(r * 0.55, -r * 0.45, r * 0.35);
		g.fill(0x111111);

		// Strap
		g.moveTo(r * 0.55, -r * 0.45);
		g.lineTo(-r * 0.5, r * 0.5); // Diagonally across head
		g.stroke({ width: r * 0.08, color: 0x111111 });
	}

	private drawCucumberSlices(g: Graphics, r: number): void {
		// Slices on both eyes
		const drawSlice = (y: number) => {
			g.circle(r * 0.6, y, r * 0.35);
			g.fill(0x90ee90); // Light green flesh
			g.stroke({ width: r * 0.05, color: 0x228b22 }); // Dark green skin
			// Seeds
			g.circle(r * 0.6, y, r * 0.1);
			g.fill({ color: 0xffffff, alpha: 0.5 });
		};

		drawSlice(-r * 0.45);
		drawSlice(r * 0.45);
	}

	private drawAnimeEyes(g: Graphics, r: number): void {
		// Big sparkly eyes - overlaid on top of normal eyes
		const drawEye = (y: number) => {
			g.ellipse(r * 0.6, y, r * 0.3, r * 0.4);
			g.fill(0x000000); // Black pupil base

			// Top shine (large)
			g.circle(r * 0.65, y - r * 0.15, r * 0.12);
			g.fill(0xffffff);

			// Bottom shine (small)
			g.circle(r * 0.58, y + r * 0.15, r * 0.08);
			g.fill(0xffffff);

			// Blushing cheeks
			g.ellipse(r * 0.5, y + r * 0.5 * (y > 0 ? 1 : -1), r * 0.15, r * 0.08);
			g.fill({ color: 0xffaaaa, alpha: 0.5 });
		};

		drawEye(-r * 0.45);
		drawEye(r * 0.45);
	}

	// --- MOUTH GROUP ---

	private drawVampireFangs(g: Graphics, r: number): void {
		// Mouth line
		g.moveTo(r * 0.5, -r * 0.3);
		g.quadraticCurveTo(r * 0.6, 0, r * 0.5, r * 0.3);
		g.stroke({ width: r * 0.05, color: 0x000000 });

		// Fangs - protruding down/out
		g.beginPath();
		g.moveTo(r * 0.55, -r * 0.2);
		g.lineTo(r * 0.8, -r * 0.2);
		g.lineTo(r * 0.6, -r * 0.1);
		g.fill(0xffffff); // Left fang

		g.beginPath();
		g.moveTo(r * 0.55, r * 0.2);
		g.lineTo(r * 0.8, r * 0.2);
		g.lineTo(r * 0.6, r * 0.1);
		g.fill(0xffffff); // Right fang
	}

	private drawBuckTeeth(g: Graphics, r: number): void {
		// Two big rectangular teeth
		g.rect(r * 0.5, -r * 0.15, r * 0.25, r * 0.14);
		g.fill(0xffffff);
		g.stroke({ width: 1, color: 0xcccccc });

		g.rect(r * 0.5, 0.01, r * 0.25, r * 0.14);
		g.fill(0xffffff);
		g.stroke({ width: 1, color: 0xcccccc });
	}

	private drawMustache(g: Graphics, r: number): void {
		// Handlebar mustache
		g.moveTo(r * 0.5, 0);
		// Left side
		g.bezierCurveTo(r * 0.6, -r * 0.1, r * 0.7, -r * 0.5, r * 0.4, -r * 0.6);
		g.quadraticCurveTo(r * 0.5, -r * 0.4, r * 0.5, -r * 0.1);
		// Right side
		g.moveTo(r * 0.5, 0);
		g.bezierCurveTo(r * 0.6, r * 0.1, r * 0.7, r * 0.5, r * 0.4, r * 0.6);
		g.quadraticCurveTo(r * 0.5, r * 0.4, r * 0.5, r * 0.1);

		g.fill(0x332211); // Dark brown
	}

	private drawBeard(g: Graphics, r: number): void {
		// Santa/Wizard beard
		g.moveTo(r * 0.4, -r * 0.5); // Start at cheek
		g.quadraticCurveTo(r * 0.8, -r * 0.3, r * 0.9, 0); // Chin
		g.quadraticCurveTo(r * 0.8, r * 0.3, r * 0.4, r * 0.5); // Other cheek

		g.quadraticCurveTo(0, r * 0.4, -r * 0.2, 0); // Bottom curve
		g.quadraticCurveTo(0, -r * 0.4, r * 0.4, -r * 0.5); // Back to start

		g.fill(0xeeeeee);
	}

	private drawMedicalMask(g: Graphics, r: number): void {
		// Blue mask covering mouth area
		g.roundRect(r * 0.4, -r * 0.5, r * 0.4, r * 1.0, r * 0.1);
		g.fill(0x88ccff);

		// Straps
		g.moveTo(r * 0.4, -r * 0.4);
		g.lineTo(-r * 0.2, -r * 0.6);
		g.stroke({ width: 2, color: 0xffffff });
		g.moveTo(r * 0.4, r * 0.4);
		g.lineTo(-r * 0.2, r * 0.6);
		g.stroke({ width: 2, color: 0xffffff });
	}

	private drawClownNose(g: Graphics, r: number): void {
		// Big red nose at tip
		g.circle(r * 0.9, 0, r * 0.25);
		g.fill(0xff0000);
		// Shine
		g.circle(r * 0.95, -r * 0.08, r * 0.08);
		g.fill({ color: 0xffffff, alpha: 0.6 });
	}

	private drawBubblegum(g: Graphics, r: number): void {
		// Pink bubble expanding from mouth
		// Only expand positively: 0 to 1, slow expand, fast pop
		// Sawtooth-ish wave
		const t = (performance.now() % 2000) / 2000; // 0 to 1 over 2 seconds
		let size = t;
		// Quick pop at end
		if (size > 0.9) size = 1 - (size - 0.9) * 10; // 1 down to 0
		if (size < 0) size = 0;

		const radius = size * r * 0.6;

		if (radius > 0.1) {
			g.circle(r * 0.8 + radius * 0.5, 0, radius);
			g.fill({ color: 0xff69b4, alpha: 0.8 });
			g.stroke({ width: 1, color: 0xff1493 });
			// Shine
			g.circle(r * 0.9 + radius * 0.5, -radius * 0.3, radius * 0.3);
			g.fill({ color: 0xffffff, alpha: 0.5 });
		}
	}

	private drawPipe(g: Graphics, r: number): void {
		// Pipe stem
		g.moveTo(r * 0.5, r * 0.1);
		g.lineTo(r * 0.9, r * 0.4);
		g.stroke({ width: r * 0.08, color: 0x654321 });
		// Bowl
		g.circle(r * 0.9, r * 0.4, r * 0.15);
		g.fill(0x331100);
		// Smoke
		const time = performance.now() / 1000;
		g.circle(r * 1.0 + Math.sin(time) * 5, r * 0.4 - time % 1 * 20, r * 0.05);
		g.fill({ color: 0xaaaaaa, alpha: 0.5 });
	}

	private drawZipperMouth(g: Graphics, r: number): void {
		// Zipper line
		g.moveTo(r * 0.5, -r * 0.3);
		g.lineTo(r * 0.5, r * 0.3);
		g.stroke({ width: r * 0.08, color: 0x333333 });
		// Zipper pull
		g.rect(r * 0.45, 0, r * 0.05, r * 0.2);
		g.fill(0x888888);
		// Teeth marks
		for (let i = -3; i <= 3; i++) {
			g.moveTo(r * 0.45, i * r * 0.08);
			g.lineTo(r * 0.55, i * r * 0.08);
			g.stroke({ width: 1, color: 0xaaaaaa });
		}
	}

	private drawDogTongue(g: Graphics, r: number): void {
		// Tongue hanging out
		g.moveTo(r * 0.5, 0);
		g.quadraticCurveTo(r * 0.8, 0, r * 0.8, r * 0.3); // Out and down
		g.quadraticCurveTo(r * 0.8, r * 0.5, r * 0.6, r * 0.5); // Tip
		g.quadraticCurveTo(r * 0.5, r * 0.4, r * 0.5, r * 0.2); // Back
		g.fill(0xff9999);
		g.stroke({ width: 1, color: 0xcc6666 });
	}

	/**
	 * Set visibility
	 */
	setVisible(visible: boolean): void {
		this.container.visible = visible;
	}

	/**
	 * Set spawn protection visual effect (ghost/transparent appearance)
	 * Protected worms appear at 50% opacity with a subtle glow effect
	 * @param protected Whether spawn protection is active
	 */
	setSpawnProtection(isProtected: boolean): void {
		if (this.hasSpawnProtection === isProtected) return;

		this.hasSpawnProtection = isProtected;

		// Apply transparency effect - 50% opacity when protected
		// Use SPAWN_PROTECTION.OPACITY from shared/constants (0.5)
		this.container.alpha = isProtected ? 0.5 : 1.0;
	}

	/**
	 * Set segment count - grows or shrinks the worm naturally
	 * Uses queue system for growth to prevent visual explosion from batch eating
	 * Shrinking is immediate, growing is queued (max 4 segments per frame)
	 */
	setSegmentCount(count: number): void {
		const currentActualCount = this.bodyPoints.length;
		const targetCount = count;

		// Update config to target count
		this.config.segmentCount = targetCount;

		if (targetCount > currentActualCount + this.pendingGrowth) {
			// GROWING: Queue new segments (don't add immediately)
			const segmentsToQueue = targetCount - currentActualCount - this.pendingGrowth;
			this.pendingGrowth += segmentsToQueue;
		} else if (targetCount < currentActualCount) {
			// SHRINKING: Immediate removal from tail (no queue needed)
			// Also clear any pending growth that would exceed target
			this.pendingGrowth = 0;

			this.bodyPoints.splice(targetCount);
			this.segmentVelocities.splice(targetCount);
			this.segmentRotations.splice(targetCount);
			this.segmentGrowthProgress.splice(targetCount);

			// Recalculate ALL segment radii with proper tapering
			this.recalculateSegmentRadii();

			// Redraw body segments
			this.drawBodySegments();
		}
	}

	/**
	 * Process growth queue - called each frame to add queued segments
	 * Adds up to MAX_SEGMENTS_PER_FRAME segments per call for smooth growth
	 *
	 * CLASSIC SNAKE BEHAVIOR: New segments spawn AT the tail position.
	 * They will naturally follow the path as the head moves forward.
	 * This is how traditional snake/worms games work - the body follows the head's trail.
	 */
	private processGrowthQueue(): void {
		if (this.pendingGrowth <= 0) return;

		const currentCount = this.bodyPoints.length;
		const segmentsToAdd = Math.min(this.pendingGrowth, this.MAX_SEGMENTS_PER_FRAME);

		// Get current tail position - new segments spawn HERE
		const tailPoint = this.bodyPoints[currentCount - 1];
		const tailX = tailPoint?.x ?? 0;
		const tailY = tailPoint?.y ?? 0;

		// Get tail rotation to inherit for new segments
		const tailRotation = this.segmentRotations[currentCount - 1] ?? (this.headRotation + Math.PI);

		// Inherit velocity from tail
		const tailVelocity = this.segmentVelocities[currentCount - 1];

		for (let i = 0; i < segmentsToAdd; i++) {
			// All new segments spawn at the SAME position as current tail
			// They will spread out naturally as the head moves and path-following repositions them
			this.bodyPoints.push(new Point(tailX, tailY));
			this.segmentVelocities.push({
				vx: tailVelocity?.vx ?? 0,
				vy: tailVelocity?.vy ?? 0,
			});
			this.segmentRotations.push(tailRotation);
			// Start new segment at 0 growth - will animate to full size
			this.segmentGrowthProgress.push(0);
		}

		// Deduct from queue
		this.pendingGrowth -= segmentsToAdd;

		// Recalculate segment radii
		this.recalculateSegmentRadii();

		// Mark body as dirty for redraw
		this.bodyDirty = true;
	}

	/**
	 * Recalculate segment radii with proper tapering (head=1.0, tail=0.7)
	 */
	private recalculateSegmentRadii(): void {
		const count = this.bodyPoints.length;
		const baseRadius = this.config.radius;
		this.segmentRadii = [];
		for (let i = 0; i < count; i++) {
			const progress = i / Math.max(1, count - 1);
			const segRadius = baseRadius * (1.0 - progress * 0.3); // Head=1.0, tail=0.7
			this.segmentRadii.push(segRadius);
		}
	}

	/**
	 * Add a boost/sprint visual effect
	 * When boosting:
	 * - Orange glow around front segments
	 * - Speed lines trail behind
	 * - Color shift toward orange/yellow
	 * Effects smoothly fade in/out for playful arcade feel
	 */
	setBoostEffect(boosting: boolean): void {
		const wasBoosting = this.isBoosting;
		this.isBoosting = boosting;

		// Create sprint graphics containers if needed
		if (boosting && !this.sprintGlow) {
			this.createSprintGraphics();
		}

		// Keep sprint graphics visible during fade-out (they'll hide when intensity reaches 0)
		// Only show them when starting to boost or already have intensity
		if (this.sprintGlow) {
			this.sprintGlow.visible = boosting || this.sprintIntensity > 0.01;
		}
		if (this.speedLines) {
			this.speedLines.visible = boosting || this.sprintIntensity > 0.01;
		}

		// Reset intensity when starting boost (from fully stopped state)
		if (boosting && !wasBoosting && this.sprintIntensity < 0.01) {
			this.sprintIntensity = 0;
		}

		// Scale pulse effect on head when boosting
		if (boosting) {
			const scale = 1 + Math.sin(performance.now() / 100) * 0.05;
			this.headGraphics.scale.set(scale);
		} else {
			this.headGraphics.scale.set(1);
			// Note: sprintIntensity fade-out handled in updateSprintFadeOut
		}

		// Force body redraw to apply/remove color shift
		this.forceBodyRedraw();
	}

	/**
	 * Update eye expression based on game state
	 * Priority: Happy (powerup) > Angry (long sprint) > Scared (low score) > Normal
	 *
	 * @param sprintDuration - How long the worm has been sprinting (seconds)
	 * @param score - Current worm score (segment count)
	 * @param hasPowerup - Whether the worm has any active powerups
	 */
	setExpressionState(sprintDuration: number, score: number, hasPowerup: boolean): void {
		// Store the state (for debugging/future use)
		this._expressionState = { sprintDuration, score, hasPowerup };

		// Determine target expression based on game state
		// Priority: Happy > Angry > Scared > Normal
		let newExpression = EyeExpression.NORMAL;

		if (hasPowerup) {
			// Highest priority: Happy when powered up
			newExpression = EyeExpression.HAPPY;
		} else if (sprintDuration >= EXPRESSION_THRESHOLDS.ANGRY_SPRINT_DURATION) {
			// Second priority: Angry when sprinting hard (5+ seconds)
			newExpression = EyeExpression.ANGRY;
		} else if (score < EXPRESSION_THRESHOLDS.SCARED_SCORE_THRESHOLD) {
			// Third priority: Scared when score is low (under 20)
			newExpression = EyeExpression.SCARED;
		}

		// Update target expression if changed
		if (newExpression !== this.targetExpression) {
			this.targetExpression = newExpression;
			this.expressionTransition = 0; // Start transition
		}
	}

	/**
	 * Update expression transition for smooth animation
	 * Called each frame during skin animation update
	 */
	private updateExpressionTransition(dt: number): void {
		// If transitioning to a new expression
		if (this.expressionTransition < 1) {
			this.expressionTransition = Math.min(1, this.expressionTransition + this.EXPRESSION_TRANSITION_SPEED * dt);

			// When transition completes, apply the new expression
			if (this.expressionTransition >= 1 && this.currentExpression !== this.targetExpression) {
				this.currentExpression = this.targetExpression;
				// Redraw eyes with new expression
				if (this.config.showEyes) {
					this.drawEyes();
				}
			}
		}
	}

	/**
	 * Get the current eye expression
	 */
	getExpression(): EyeExpression {
		return this.currentExpression;
	}

	/**
	 * Get the current expression state (useful for debugging)
	 */
	getExpressionState(): { sprintDuration: number; score: number; hasPowerup: boolean } {
		return { ...this._expressionState };
	}

	/**
	 * Create sprint visual effect graphics (glow and speed lines)
	 */
	private createSprintGraphics(): void {
		// Create sprint glow graphics (rendered behind body)
		if (!this.sprintGlow) {
			this.sprintGlow = new Graphics();
			// Insert at the beginning of container (behind everything else)
			this.container.addChildAt(this.sprintGlow, 0);
		}

		// Create speed lines graphics (rendered behind body)
		if (!this.speedLines) {
			this.speedLines = new Graphics();
			// Insert at the beginning of container (behind glow)
			this.container.addChildAt(this.speedLines, 0);
		}
	}

	/**
	 * Update sprint visual effects (call each frame when boosting)
	 */
	private updateSprintEffects(dt: number): void {
		if (!this.isBoosting) return;

		// Ramp up sprint intensity over time (takes ~0.5 seconds to reach full)
		this.sprintIntensity = Math.min(1, this.sprintIntensity + dt * 2);

		// Update sprint glow around front segments
		this.drawSprintGlow();

		// Update speed line trail
		this.updateSpeedLines(dt);
	}

	/**
	 * Update sprint fade-out effects (call each frame when NOT boosting but intensity > 0)
	 * Smoothly fades out the orange glow and color shift over ~0.3 seconds
	 */
	private updateSprintFadeOut(dt: number): void {
		// Fade out intensity over ~0.3 seconds (faster than fade-in for snappy feel)
		this.sprintIntensity = Math.max(0, this.sprintIntensity - dt * 3.5);

		// Update visuals during fade-out
		if (this.sprintIntensity > 0.01) {
			// Keep drawing glow with reduced intensity
			this.drawSprintGlow();
			// Update existing speed lines (don't spawn new ones)
			this.updateSpeedLinesFadeOut(dt);
		} else {
			// Fully faded out - hide graphics
			this.sprintIntensity = 0;
			if (this.sprintGlow) {
				this.sprintGlow.visible = false;
				this.sprintGlow.clear();
			}
			if (this.speedLines) {
				this.speedLines.visible = false;
				this.speedLines.clear();
			}
			// Clear remaining particles
			this.speedLineParticles = [];
		}
	}

	/**
	 * Update speed lines during fade-out (no new spawns, just animate existing)
	 */
	private updateSpeedLinesFadeOut(dt: number): void {
		if (!this.speedLines) return;

		const g = this.speedLines;
		g.clear();

		const intensity = this.sprintIntensity;

		// Update and draw existing particles (no new spawns)
		for (let i = this.speedLineParticles.length - 1; i >= 0; i--) {
			const p = this.speedLineParticles[i];
			if (!p) continue;

			// Update position and life
			p.x += p.vx * dt;
			p.y += p.vy * dt;
			p.life -= dt;

			// Remove dead particles
			if (p.life <= 0) {
				this.speedLineParticles.splice(i, 1);
				continue;
			}

			// Per-particle culling
			if (this.cullingBounds) {
				if (p.x < this.cullingBounds.left - 50 ||
					p.x > this.cullingBounds.right + 50 ||
					p.y < this.cullingBounds.top - 50 ||
					p.y > this.cullingBounds.bottom + 50) {
					continue;
				}
			}

			// Calculate fade based on life remaining and overall intensity
			const lifeRatio = p.life / p.maxLife;
			const alpha = p.alpha * lifeRatio * intensity;

			// Normalize velocity for line direction
			const vLen = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
			const nvx = vLen > 0 ? p.vx / vLen : 0;
			const nvy = vLen > 0 ? p.vy / vLen : 0;

			// Draw speed line (short line in direction of movement)
			const lineLength = p.length * lifeRatio;
			g.moveTo(p.x, p.y);
			g.lineTo(p.x + nvx * lineLength, p.y + nvy * lineLength);
			g.stroke({ color: WormRenderer.SPRINT_LINE_COLOR, width: 2, alpha: alpha });
		}
	}

	/**
	 * Draw subtle white glow effect around front segments
	 */
	private drawSprintGlow(): void {
		if (!this.sprintGlow) return;

		const g = this.sprintGlow;
		g.clear();

		const segmentCount = this.bodyPoints.length;
		const { radius } = this.config;
		const intensity = this.sprintIntensity;

		// Only draw glow for first 10-15% of segments (front of worm)
		const glowSegmentCount = Math.min(Math.max(3, Math.floor(segmentCount * 0.15)), 20);

		// Pulsing effect for exciting arcade feel
		const pulse = 0.8 + Math.sin(performance.now() / 80) * 0.2;
		const baseAlpha = 0.35 * intensity * pulse;

		// Draw glow circles from back of glow region to front (so front renders on top)
		for (let i = glowSegmentCount - 1; i >= 0; i--) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			// Per-segment culling
			if (this.cullingBounds) {
				const segRadius = (this.segmentRadii[i] ?? radius) * 2;
				if (point.x + segRadius < this.cullingBounds.left ||
					point.x - segRadius > this.cullingBounds.right ||
					point.y + segRadius < this.cullingBounds.top ||
					point.y - segRadius > this.cullingBounds.bottom) {
					continue;
				}
			}

			// Falloff: brightest at head, fading toward back
			const falloff = 1 - (i / glowSegmentCount);
			const segRadius = this.segmentRadii[i] ?? radius;

			// Outer glow layer (larger, more transparent white)
			const outerGlowRadius = segRadius * (1.6 + falloff * 0.4) * pulse;
			const outerAlpha = baseAlpha * falloff * 0.3; // Reduced from 0.4 for subtlety
			g.circle(point.x, point.y, outerGlowRadius);
			g.fill({ color: WormRenderer.SPRINT_GLOW_COLOR, alpha: outerAlpha });

			// Inner glow layer (smaller, slightly brighter white)
			const innerGlowRadius = segRadius * (1.25 + falloff * 0.25);
			const innerAlpha = baseAlpha * falloff * 0.5; // Reduced from 0.7 for subtlety
			g.circle(point.x, point.y, innerGlowRadius);
			g.fill({ color: WormRenderer.SPRINT_GLOW_COLOR, alpha: innerAlpha });
		}
	}

	/**
	 * Update speed line trail effect
	 */
	private updateSpeedLines(dt: number): void {
		if (!this.speedLines) return;

		const g = this.speedLines;
		g.clear();

		const intensity = this.sprintIntensity;
		const headPoint = this.bodyPoints[0];
		if (!headPoint || intensity < 0.1) return;

		// Get head direction (opposite to movement for trail)
		const secondPoint = this.bodyPoints[1];
		let dirX = 0, dirY = -1;
		if (secondPoint) {
			const dx = secondPoint.x - headPoint.x;
			const dy = secondPoint.y - headPoint.y;
			const len = Math.sqrt(dx * dx + dy * dy);
			if (len > 0.001) {
				dirX = dx / len;
				dirY = dy / len;
			}
		}

		// Spawn new speed line particles
		const spawnRate = 8; // particles per second
		const spawnCount = Math.floor(spawnRate * dt * intensity);
		const { radius } = this.config;

		for (let i = 0; i < spawnCount; i++) {
			// Spawn from random position along front segments
			const spawnIndex = Math.floor(Math.random() * Math.min(5, this.bodyPoints.length));
			const spawnPoint = this.bodyPoints[spawnIndex];
			if (!spawnPoint) continue;

			// Add some perpendicular spread
			const perpX = -dirY;
			const perpY = dirX;
			const spread = (Math.random() - 0.5) * radius * 2;

			this.speedLineParticles.push({
				x: spawnPoint.x + perpX * spread,
				y: spawnPoint.y + perpY * spread,
				vx: dirX * (150 + Math.random() * 100), // Move in trail direction
				vy: dirY * (150 + Math.random() * 100),
				life: 0.3 + Math.random() * 0.2, // 0.3-0.5 seconds
				maxLife: 0.3 + Math.random() * 0.2,
				length: 15 + Math.random() * 25, // Line length
				alpha: 0.4 + Math.random() * 0.3,
			});
		}

		// Limit particle count
		const maxParticles = 30;
		while (this.speedLineParticles.length > maxParticles) {
			this.speedLineParticles.shift();
		}

		// Update and draw particles
		for (let i = this.speedLineParticles.length - 1; i >= 0; i--) {
			const p = this.speedLineParticles[i];
			if (!p) continue;

			// Update position and life
			p.x += p.vx * dt;
			p.y += p.vy * dt;
			p.life -= dt;

			// Remove dead particles
			if (p.life <= 0) {
				this.speedLineParticles.splice(i, 1);
				continue;
			}

			// Per-particle culling
			if (this.cullingBounds) {
				if (p.x < this.cullingBounds.left - 50 ||
					p.x > this.cullingBounds.right + 50 ||
					p.y < this.cullingBounds.top - 50 ||
					p.y > this.cullingBounds.bottom + 50) {
					continue;
				}
			}

			// Calculate fade based on life remaining
			const lifeRatio = p.life / p.maxLife;
			const alpha = p.alpha * lifeRatio * intensity;

			// Normalize velocity for line direction
			const vLen = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
			const nvx = vLen > 0 ? p.vx / vLen : 0;
			const nvy = vLen > 0 ? p.vy / vLen : 0;

			// Draw speed line (short line in direction of movement)
			const lineLength = p.length * lifeRatio;
			g.moveTo(p.x, p.y);
			g.lineTo(p.x + nvx * lineLength, p.y + nvy * lineLength);
			g.stroke({ color: WormRenderer.SPRINT_LINE_COLOR, width: 2, alpha: alpha });
		}
	}

	/**
	 * Get boosted color with subtle brightness increase for sprint effect
	 * Uses brightness boost instead of color shift to preserve skin colors
	 * @param baseColor Original segment color
	 * @param segmentIndex Index of the segment (0 = head)
	 * @returns Slightly brightened color when boosting
	 */
	private getSprintAdjustedColor(baseColor: number, segmentIndex: number): number {
		if (!this.isBoosting || this.sprintIntensity < 0.01) {
			return baseColor;
		}

		const segmentCount = this.bodyPoints.length;
		// Front segments get more brightness boost
		const frontRatio = Math.max(0, 1 - (segmentIndex / Math.max(1, segmentCount * 0.3)));
		const boostIntensity = this.sprintIntensity * frontRatio * 0.15; // Max 15% brightness boost (subtle)

		if (boostIntensity < 0.01) return baseColor;

		// Extract RGB from base color
		const r = (baseColor >> 16) & 0xff;
		const g = (baseColor >> 8) & 0xff;
		const b = baseColor & 0xff;

		// Boost brightness toward white (preserves hue, just lightens)
		const newR = Math.min(255, Math.round(r + (255 - r) * boostIntensity));
		const newG = Math.min(255, Math.round(g + (255 - g) * boostIntensity));
		const newB = Math.min(255, Math.round(b + (255 - b) * boostIntensity));

		return (newR << 16) | (newG << 8) | newB;
	}

	/**
	 * Set whether this worm is the local player
	 * Note: Visual effects (crown, aura) removed as per requirements.
	 * The flag is kept for potential future use (e.g., same-skin enemy indicator)
	 */
	setIsLocalPlayer(isLocal: boolean): void {
		if (this.isLocalPlayer === isLocal) return;

		this.isLocalPlayer = isLocal;

		// Enable path-following for ALL worms (smooth ribbon motion)
		// This ensures body segments trace the exact path the head traveled
		// regardless of whether it's a local player, remote player, or AI worm
		this.setPathFollowing(true);

		// Visual effects removed - no crown or aura for local player
		// The distinction is now only used for gameplay logic
		if (!isLocal) {
			this.removeLocalPlayerEffects();
		}
	}

	/**
	 * Get whether this worm is marked as local player
	 */
	getIsLocalPlayer(): boolean {
		return this.isLocalPlayer;
	}

	/**
	 * Set whether this enemy has the same skin as the local player
	 * Shows a red glow/stroke around enemies with matching skin
	 */
	setSameSkinEnemy(isSameSkin: boolean): void {
		if (this.hasSameSkin === isSameSkin) return;
		this.hasSameSkin = isSameSkin;

		if (isSameSkin && !this.isLocalPlayer) {
			this.createSameSkinIndicator();
		} else {
			this.removeSameSkinIndicator();
		}
	}

	/**
	 * Set the LOD (Level of Detail) level for this worm
	 * Used by CullingManager to reduce rendering for distant worms
	 * @param lod 0 = close (full detail), 1 = medium, 2 = far (minimal)
	 */
	setLOD(lod: 0 | 1 | 2): void {
		if (this.lodLevel !== lod) {
			this.lodLevel = lod;
			// Force redraw with new LOD settings
			this.forceBodyRedraw();
		}
	}

	/**
	 * Get current LOD level
	 */
	getLOD(): 0 | 1 | 2 {
		return this.lodLevel;
	}

	/**
	 * Create red glow indicator for same-skin enemies
	 */
	private createSameSkinIndicator(): void {
		if (this.sameSkinIndicator) return;

		const { radius } = this.config;
		const g = new Graphics();

		// Draw red pulsing outline/glow around the head
		// Outer glow ring
		g.circle(0, 0, radius * 1.8);
		g.stroke({ width: 3, color: 0xff3333, alpha: 0.6 });

		// Inner glow ring
		g.circle(0, 0, radius * 1.5);
		g.stroke({ width: 2, color: 0xff0000, alpha: 0.8 });

		this.sameSkinIndicator = g;
		// Add behind head but in front of body
		this.container.addChildAt(g, this.container.getChildIndex(this.headGraphics));
	}

	/**
	 * Remove same-skin indicator
	 */
	private removeSameSkinIndicator(): void {
		if (this.sameSkinIndicator) {
			this.container.removeChild(this.sameSkinIndicator);
			this.sameSkinIndicator.destroy();
			this.sameSkinIndicator = null;
		}
	}

	/**
	 * Check if this worm has the same skin indicator active
	 */
	hasSameSkinIndicator(): boolean {
		return this.hasSameSkin;
	}

	/**
	 * Remove local player visual effects
	 */
	private removeLocalPlayerEffects(): void {
		if (this.localPlayerMarker) {
			this.container.removeChild(this.localPlayerMarker);
			this.localPlayerMarker.destroy();
			this.localPlayerMarker = null;
		}

		if (this.localPlayerAura) {
			this.container.removeChild(this.localPlayerAura);
			this.localPlayerAura.destroy();
			this.localPlayerAura = null;
		}
	}

	/**
 * Check if an accessory type requires frame-by-frame animation
 */
	private isAnimatedAccessory(type: AccessoryType): boolean {
		const animatedTypes = [
			AccessoryType.PROPELLER_HAT,
			AccessoryType.HALO,
			AccessoryType.CYCLOPS_VISOR,
			AccessoryType.BUBBLEGUM,
			AccessoryType.PIPE, // Smoke
			AccessoryType.ANTENNA, // Glowing ball
			AccessoryType.AFRO, // Disco shine? (Maybe later)
		];
		return animatedTypes.includes(type);
	}

	/**
	 * Draw propeller hat accessory with animation
	 */
	private drawPropellerHat(g: Graphics, r: number): void {
		// Cap
		g.arc(0, 0, r * 0.95, Math.PI, 0, false);
		g.fill(0xffcc00); // Yellow cap
		g.stroke({ width: 2, color: 0xffaa00 });

		// Brim
		g.rect(-r * 1.0, -r * 0.1, r * 2.0, r * 0.2);
		g.fill(0xffaa00);

		// Propeller shaft
		g.rect(-r * 0.1, -r * 1.2, r * 0.2, r * 0.6);
		g.fill(0xcccccc);

		// Propeller blades (Animated)
		const time = performance.now() / 100;
		const bladeLength = r * 1.2;
		const bladeWidth = r * 0.3;

		// Blade 1
		const angle1 = time;
		g.moveTo(0, -r * 1.2);
		g.lineTo(Math.cos(angle1) * bladeLength, -r * 1.2 + Math.sin(angle1) * bladeWidth);
		g.stroke({ width: r * 0.15, color: 0xff4444 });

		// Blade 2
		const angle2 = time + Math.PI;
		g.moveTo(0, -r * 1.2);
		g.lineTo(Math.cos(angle2) * bladeLength, -r * 1.2 + Math.sin(angle2) * bladeWidth);
		g.stroke({ width: r * 0.15, color: 0x4444ff });

		// Center nut
		g.circle(0, -r * 1.2, r * 0.15);
		g.fill(0xdddddd);
	}
	/**
	 * Update the position of local player effects and same-skin indicator
	 * Uses server-provided radius for head (score-based scaling)
	 */
	private updateLocalPlayerEffects(): void {
		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		// Use server-provided head radius if available, otherwise use config radius
		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		// Update local player effects (if any exist)
		if (this.isLocalPlayer) {
			// Position the crown above the head
			if (this.localPlayerMarker) {
				const markerOffset = headRadius * 2.2; // Above the head
				this.localPlayerMarker.position.set(
					headPoint.x - Math.sin(this.headRotation) * markerOffset,
					headPoint.y + Math.cos(this.headRotation) * markerOffset - headRadius
				);

				// Make it bob slightly for visibility
				const bob = Math.sin(performance.now() / 300) * 3;
				this.localPlayerMarker.y += bob;
			}

			// Position the aura at the head
			if (this.localPlayerAura) {
				this.localPlayerAura.position.set(headPoint.x, headPoint.y);

				// Subtle pulse effect
				const pulse = 1 + Math.sin(performance.now() / 500) * 0.1;
				this.localPlayerAura.scale.set(pulse);
			}
		}

		// Update same-skin enemy indicator
		if (this.sameSkinIndicator && this.hasSameSkin) {
			this.sameSkinIndicator.position.set(headPoint.x, headPoint.y);

			// Pulsing effect to make it noticeable
			const pulse = 1 + Math.sin(performance.now() / 200) * 0.15;
			this.sameSkinIndicator.scale.set(pulse);
		}
	}

	/**
	 * Start death animation - fades out the worm over time
	 * @param onComplete - Callback when animation finishes
	 * @param duration - Animation duration in ms (default 1000ms)
	 */
	startDeathAnimation(onComplete?: () => void, duration: number = 1000): void {
		if (this.deathAnimation.active) return;

		this.deathAnimation = {
			active: true,
			elapsed: 0,
			duration,
			onComplete: onComplete ?? null,
		};

		// Initial flash effect - make worm briefly brighter before fading
		this.container.alpha = 1.2;
	}

	/**
	 * Check if death animation is currently playing
	 */
	isDeathAnimationActive(): boolean {
		return this.deathAnimation.active;
	}

	/**
	 * Update death animation (call each frame with delta time in seconds)
	 * Returns true when animation is complete
	 */
	updateDeathAnimation(dt: number): boolean {
		if (!this.deathAnimation.active) return false;

		this.deathAnimation.elapsed += dt * 1000; // Convert to ms
		const progress = Math.min(1, this.deathAnimation.elapsed / this.deathAnimation.duration);

		// Easing function for smooth fade-out (ease-out cubic)
		const easedProgress = 1 - Math.pow(1 - progress, 3);

		// Fade out alpha
		this.container.alpha = 1 - easedProgress;

		// Scale down slightly for "shrinking death" effect
		const shrinkScale = 1 - easedProgress * 0.3;
		this.container.scale.set(shrinkScale);

		// Add a subtle red tint/color shift as it fades (visual death cue)
		// This affects all children in the container
		if (progress > 0.3) {
			// Wobble effect during fade
			const wobble = Math.sin(this.deathAnimation.elapsed * 0.02) * (1 - progress) * 5;
			this.container.rotation = wobble * 0.02;
		}

		// Animation complete
		if (progress >= 1) {
			this.deathAnimation.active = false;
			this.container.visible = false;

			if (this.deathAnimation.onComplete) {
				this.deathAnimation.onComplete();
			}
			return true;
		}

		return false;
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// LEVIATHAN (LEGENDARY) VISUAL EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Set whether this worm is LEVIATHAN (legendary AI)
	 * Applies special visual effects: dark purple/gold colors, crown, particle trail, menacing eyes
	 */
	setLegendary(legendary: boolean): void {
		if (this.isLegendary === legendary) return;
		this.isLegendary = legendary;

		if (legendary) {
			this.createLeviathanEffects();
			// Override colors for LEVIATHAN
			this.config.color = WormRenderer.LEVIATHAN_PRIMARY;
			this.config.pupilColor = WormRenderer.LEVIATHAN_EYE;
			// Redraw with new colors
			this.cachedColors = [];
			this.drawHead();
			this.drawEyes();
			this.forceBodyRedraw();
		} else {
			this.removeLeviathanEffects();
		}
	}

	/**
	 * Get whether this worm is LEVIATHAN
	 */
	getLegendary(): boolean {
		return this.isLegendary;
	}

	/**
	 * Create LEVIATHAN visual effects (crown, aura, particle layer)
	 */
	private createLeviathanEffects(): void {
		const { radius } = this.config;

		// Create purple/gold aura glow (behind body)
		if (!this.leviathanAura) {
			this.leviathanAura = new Graphics();
			// Add at the very back of the container
			this.container.addChildAt(this.leviathanAura, 0);
		}

		// Create particle trail layer
		if (!this.leviathanParticles) {
			this.leviathanParticles = new Graphics();
			// Add behind body but in front of aura
			this.container.addChildAt(this.leviathanParticles, 1);
		}

		// Create crown (above head)
		if (!this.leviathanCrown) {
			this.leviathanCrown = new Graphics();
			this.container.addChild(this.leviathanCrown);
		}

		// Initial draw
		this.drawLeviathanCrown(radius);
	}

	/**
	 * Remove LEVIATHAN visual effects
	 */
	private removeLeviathanEffects(): void {
		if (this.leviathanCrown) {
			this.container.removeChild(this.leviathanCrown);
			this.leviathanCrown.destroy();
			this.leviathanCrown = null;
		}

		if (this.leviathanAura) {
			this.container.removeChild(this.leviathanAura);
			this.leviathanAura.destroy();
			this.leviathanAura = null;
		}

		if (this.leviathanParticles) {
			this.container.removeChild(this.leviathanParticles);
			this.leviathanParticles.destroy();
			this.leviathanParticles = null;
		}

		this.leviathanParticleData = [];
	}

	/**
	 * Draw LEVIATHAN crown above head
	 */
	private drawLeviathanCrown(headRadius: number): void {
		if (!this.leviathanCrown) return;

		const g = this.leviathanCrown;
		g.clear();

		const r = headRadius * 0.6; // Scale relative to head

		// Crown glow
		g.circle(0, -r * 1.8, r * 1.2);
		g.fill({ color: WormRenderer.LEVIATHAN_SECONDARY, alpha: 0.3 });

		// Crown main body - pointed style
		g.moveTo(-r * 0.8, -r * 0.3);
		g.lineTo(-r * 0.8, -r * 0.9);
		g.lineTo(-r * 0.5, -r * 0.6);
		g.lineTo(-r * 0.25, -r * 1.2);
		g.lineTo(0, -r * 0.75);
		g.lineTo(r * 0.25, -r * 1.2);
		g.lineTo(r * 0.5, -r * 0.6);
		g.lineTo(r * 0.8, -r * 0.9);
		g.lineTo(r * 0.8, -r * 0.3);
		g.closePath();
		g.fill(WormRenderer.LEVIATHAN_SECONDARY);
		g.stroke({ width: 2, color: 0xb8860b }); // Darker gold outline

		// Crown band at base
		g.rect(-r * 0.75, -r * 0.42, r * 1.5, r * 0.12);
		g.fill(WormRenderer.LEVIATHAN_PRIMARY);

		// Crown jewels (purple gems)
		g.circle(0, -r * 0.6, r * 0.12);
		g.fill(WormRenderer.LEVIATHAN_PRIMARY);
		g.stroke({ width: 1, color: 0x2a0050 });

		g.circle(-r * 0.45, -r * 0.55, r * 0.08);
		g.fill(WormRenderer.LEVIATHAN_PRIMARY);

		g.circle(r * 0.45, -r * 0.55, r * 0.08);
		g.fill(WormRenderer.LEVIATHAN_PRIMARY);

		// Diamond sparkles on points
		g.circle(-r * 0.25, -r * 1.1, r * 0.05);
		g.fill(0xffffff);
		g.circle(r * 0.25, -r * 1.1, r * 0.05);
		g.fill(0xffffff);
	}

	/**
	 * Update LEVIATHAN visual effects (call each frame)
	 */
	private updateLeviathanEffects(dt: number): void {
		if (!this.isLegendary) return;

		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		// Update crown position (above head)
		if (this.leviathanCrown) {
			const crownOffset = headRadius * 1.8;
			const cosR = Math.cos(this.headRotation);
			const sinR = Math.sin(this.headRotation);

			this.leviathanCrown.position.set(
				headPoint.x - sinR * crownOffset,
				headPoint.y + cosR * crownOffset - headRadius * 0.5
			);
			this.leviathanCrown.rotation = this.headRotation;

			// Subtle floating animation
			const float = Math.sin(this.animationTime * 0.003) * 3;
			this.leviathanCrown.y += float;

			// Redraw crown if radius changed significantly
			if (Math.abs(headRadius - this.lastHeadRadius) > 1) {
				this.drawLeviathanCrown(headRadius);
			}
		}

		// Update aura (ominous purple/gold pulsing glow around body)
		if (this.leviathanAura) {
			this.drawLeviathanAura(dt);
		}

		// Update particle trail
		if (this.leviathanParticles) {
			this.updateLeviathanParticles(dt);
		}
	}

	/**
	 * Draw LEVIATHAN aura (ominous glow around body)
	 */
	private drawLeviathanAura(_dt: number): void {
		if (!this.leviathanAura) return;

		const g = this.leviathanAura;
		g.clear();

		const pulse = Math.sin(this.animationTime * 0.004) * 0.15 + 0.85;
		const fastPulse = Math.sin(this.animationTime * 0.008) * 0.1 + 0.9;

		// Draw aura around each visible segment (with decreasing intensity toward tail)
		const segmentsToRender = Math.min(this.bodyPoints.length, 30); // Only first 30 segments for performance

		for (let i = 0; i < segmentsToRender; i += 2) { // Skip every other segment for performance
			const point = this.bodyPoints[i];
			if (!point) continue;

			const radius = this.segmentRadii[i] ?? this.config.radius;
			const alpha = 0.15 * (1 - i / segmentsToRender) * pulse;

			// Outer purple glow
			g.circle(point.x, point.y, radius * 2.5);
			g.fill({ color: WormRenderer.LEVIATHAN_GLOW, alpha: alpha * 0.5 });

			// Inner gold glow (head only)
			if (i === 0) {
				g.circle(point.x, point.y, radius * 1.8);
				g.fill({ color: WormRenderer.LEVIATHAN_SECONDARY, alpha: alpha * 0.3 * fastPulse });
			}
		}
	}

	/**
	 * Update LEVIATHAN particle trail
	 */
	private updateLeviathanParticles(dt: number): void {
		if (!this.leviathanParticles) return;

		const dtMs = dt * 1000;

		// Spawn new particles from body
		if (this.bodyPoints.length > 5) {
			// Spawn from head and some body segments
			const spawnChance = 0.3; // 30% chance per frame per spawn point
			const spawnPoints = [0, 5, 10, 15, 20]; // Segment indices to spawn from

			for (const idx of spawnPoints) {
				if (idx >= this.bodyPoints.length) continue;
				if (Math.random() > spawnChance) continue;

				const point = this.bodyPoints[idx];
				if (!point) continue;

				const radius = this.segmentRadii[idx] ?? this.config.radius;

				// Create particle
				this.leviathanParticleData.push({
					x: point.x + (Math.random() - 0.5) * radius * 2,
					y: point.y + (Math.random() - 0.5) * radius * 2,
					vx: (Math.random() - 0.5) * 30,
					vy: (Math.random() - 0.5) * 30 - 20, // Slight upward drift
					life: 0,
					maxLife: 800 + Math.random() * 400,
					size: 2 + Math.random() * 4,
					color: Math.random() > 0.3 ? WormRenderer.LEVIATHAN_GLOW : WormRenderer.LEVIATHAN_SECONDARY,
				});
			}
		}

		// Limit particle count
		const maxParticles = 50;
		if (this.leviathanParticleData.length > maxParticles) {
			this.leviathanParticleData.splice(0, this.leviathanParticleData.length - maxParticles);
		}

		// Update particles
		for (let i = this.leviathanParticleData.length - 1; i >= 0; i--) {
			const p = this.leviathanParticleData[i]!;
			p.life += dtMs;

			// Physics
			p.x += p.vx * dt;
			p.y += p.vy * dt;

			// Fade and slow down
			p.vx *= 0.98;
			p.vy *= 0.98;

			// Remove dead particles
			if (p.life >= p.maxLife) {
				this.leviathanParticleData.splice(i, 1);
			}
		}

		// Draw particles
		const g = this.leviathanParticles;
		g.clear();

		for (const p of this.leviathanParticleData) {
			const alpha = 0.6 * (1 - p.life / p.maxLife);
			const size = p.size * (1 - p.life / p.maxLife * 0.5);

			g.circle(p.x, p.y, size);
			g.fill({ color: p.color, alpha });
		}
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// BOUNTY SYSTEM (LEADERBOARD RANK) VISUAL EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Set the worm's leaderboard rank for bounty visual indicators
	 * Rank 1-3: Crown (gold/silver/bronze)
	 * Rank 4-5: Small star marker
	 * Rank 0 or >5: No bounty indicator
	 */
	setLeaderboardRank(rank: number): void {
		const previousRank = this.leaderboardRank;
		this.leaderboardRank = rank;

		// Only update visuals if rank changed
		if (previousRank === rank) return;

		// Remove existing bounty crown if rank is out of bounty range
		if (rank < 1 || rank > 5) {
			this.removeBountyCrown();
			return;
		}

		// Create or update bounty crown/star
		this.createBountyCrown();
	}

	/**
	 * Get the current leaderboard rank
	 */
	getLeaderboardRank(): number {
		return this.leaderboardRank;
	}

	/**
	 * Create the bounty crown/star graphics based on current rank
	 */
	private createBountyCrown(): void {
		// Create graphics if not exists
		if (!this.bountyCrown) {
			this.bountyCrown = new Graphics();
			// Add above other elements (on top of head)
			this.container.addChild(this.bountyCrown);
		}

		// Draw the appropriate indicator based on rank
		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		this.drawBountyCrown(headRadius);
	}

	/**
	 * Remove bounty crown/star graphics
	 */
	private removeBountyCrown(): void {
		if (this.bountyCrown) {
			this.container.removeChild(this.bountyCrown);
			this.bountyCrown.destroy();
			this.bountyCrown = null;
		}
	}

	/**
	 * Draw bounty crown or star based on rank
	 * Rank 1: Golden crown with gems
	 * Rank 2: Silver crown
	 * Rank 3: Bronze crown
	 * Rank 4-5: Small yellow star
	 */
	private drawBountyCrown(headRadius: number): void {
		if (!this.bountyCrown) return;

		const g = this.bountyCrown;
		g.clear();

		const rank = this.leaderboardRank;

		if (rank >= 1 && rank <= 3) {
			// Draw crown for top 3
			this.drawRankedCrown(g, headRadius, rank);
		} else if (rank >= 4 && rank <= 5) {
			// Draw star for rank 4-5
			this.drawBountyStar(g, headRadius);
		}
	}

	/**
	 * Draw a crown for ranks 1-3 (gold/silver/bronze)
	 */
	private drawRankedCrown(g: Graphics, headRadius: number, rank: number): void {
		const r = headRadius * 0.5; // Scale relative to head

		// Get crown color based on rank
		let crownColor: number;
		let darkOutline: number;
		let gemColor: number;

		switch (rank) {
			case 1:
				crownColor = WormRenderer.BOUNTY_GOLD;
				darkOutline = 0xb8860b; // Dark gold
				gemColor = 0xff0000; // Ruby red
				break;
			case 2:
				crownColor = WormRenderer.BOUNTY_SILVER;
				darkOutline = 0x808080; // Gray
				gemColor = 0x00bfff; // Light blue sapphire
				break;
			case 3:
				crownColor = WormRenderer.BOUNTY_BRONZE;
				darkOutline = 0x8b4513; // Saddle brown
				gemColor = 0x32cd32; // Lime green emerald
				break;
			default:
				return;
		}

		// Crown glow (subtle, matching crown color)
		g.circle(0, -r * 1.2, r * 1.0);
		g.fill({ color: crownColor, alpha: 0.25 });

		// Crown main body - pointed style (3 points)
		g.moveTo(-r * 0.7, -r * 0.2);
		g.lineTo(-r * 0.7, -r * 0.8);
		g.lineTo(-r * 0.35, -r * 0.5);
		g.lineTo(0, -r * 1.0);
		g.lineTo(r * 0.35, -r * 0.5);
		g.lineTo(r * 0.7, -r * 0.8);
		g.lineTo(r * 0.7, -r * 0.2);
		g.closePath();
		g.fill(crownColor);
		g.stroke({ width: 1.5, color: darkOutline });

		// Crown band at base (darker stripe)
		g.rect(-r * 0.65, -r * 0.32, r * 1.3, r * 0.12);
		g.fill(darkOutline);

		// Center gem
		g.circle(0, -r * 0.55, r * 0.1);
		g.fill(gemColor);
		g.stroke({ width: 1, color: 0xffffff, alpha: 0.5 });

		// Small gems on side points (for gold crown only)
		if (rank === 1) {
			g.circle(-r * 0.35, -r * 0.65, r * 0.06);
			g.fill(0xffffff);
			g.circle(r * 0.35, -r * 0.65, r * 0.06);
			g.fill(0xffffff);
		}

		// Sparkle on top point (for gold crown)
		if (rank === 1) {
			const sparkle = Math.sin(this.animationTime * 0.005) * 0.3 + 0.7;
			g.circle(0, -r * 0.95, r * 0.04);
			g.fill({ color: 0xffffff, alpha: sparkle });
		}
	}

	/**
	 * Draw a star marker for ranks 4-5
	 */
	private drawBountyStar(g: Graphics, headRadius: number): void {
		const r = headRadius * 0.35; // Smaller than crown

		// Star glow
		g.circle(0, -r * 0.5, r * 1.2);
		g.fill({ color: WormRenderer.BOUNTY_STAR, alpha: 0.2 });

		// Draw 5-pointed star
		const points = 5;
		const outerRadius = r;
		const innerRadius = r * 0.4;

		g.moveTo(0, -outerRadius);
		for (let i = 0; i < points * 2; i++) {
			const radius = i % 2 === 0 ? outerRadius : innerRadius;
			const angle = (i * Math.PI) / points - Math.PI / 2;
			const x = Math.cos(angle) * radius;
			const y = Math.sin(angle) * radius;
			g.lineTo(x, y);
		}
		g.closePath();
		g.fill(WormRenderer.BOUNTY_STAR);
		g.stroke({ width: 1, color: 0xffa500 }); // Orange outline

		// Center sparkle
		const sparkle = Math.sin(this.animationTime * 0.006) * 0.3 + 0.7;
		g.circle(0, 0, r * 0.15);
		g.fill({ color: 0xffffff, alpha: sparkle });
	}

	/**
	 * Update bounty crown position (called each frame when bounty is active)
	 */
	private updateBountyCrown(): void {
		if (!this.bountyCrown) return;

		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		const headRadius = (this.segmentRadii[0] !== undefined && this.segmentRadii[0] > 0)
			? this.segmentRadii[0]
			: this.config.radius;

		// Position above head (similar to LEVIATHAN crown positioning)
		const crownOffset = headRadius * 1.6;
		const cosR = Math.cos(this.headRotation);
		const sinR = Math.sin(this.headRotation);

		this.bountyCrown.position.set(
			headPoint.x - sinR * crownOffset,
			headPoint.y + cosR * crownOffset - headRadius * 0.3
		);
		this.bountyCrown.rotation = this.headRotation;

		// Subtle floating animation (bob up and down)
		const float = Math.sin(this.animationTime * 0.004) * 2;
		this.bountyCrown.y += float;

		// Redraw crown if radius changed significantly
		if (Math.abs(headRadius - this.lastHeadRadius) > 1) {
			this.drawBountyCrown(headRadius);
		}
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// KILL STREAK VISUAL EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Set the kill streak tier for visual effects
	 * Tiers: '' (none), 'RAMPAGE' (3+), 'UNSTOPPABLE' (5+), 'GODLIKE' (7+), 'LEGENDARY' (10+)
	 *
	 * Visual effects by tier:
	 * - RAMPAGE (3+): Orange flame trail
	 * - UNSTOPPABLE (5+): Flame trail + pulsing glow
	 * - GODLIKE (7+): Intense flames + screen edge glow effect
	 * - LEGENDARY (10+): Golden aura + particle storm
	 */
	setKillStreakTier(tier: string): void {
		if (this.killStreakTier === tier) return;

		const previousTier = this.killStreakTier;
		this.killStreakTier = tier;

		if (tier) {
			this.createKillStreakEffects(tier);
		} else {
			this.removeKillStreakEffects();
		}

		// Force body redraw if transitioning to/from streak for color effects
		if (previousTier !== tier) {
			this.forceBodyRedraw();
		}
	}

	/**
	 * Get the current kill streak tier
	 */
	getKillStreakTier(): string {
		return this.killStreakTier;
	}

	/**
	 * Create kill streak visual effect graphics based on tier
	 */
	private createKillStreakEffects(tier: string): void {
		// Always create flame trail for any streak tier
		if (!this.killStreakFlames) {
			this.killStreakFlames = new Graphics();
			// Add behind body
			this.container.addChildAt(this.killStreakFlames, 0);
		}

		// UNSTOPPABLE (5+) and above: Add pulsing glow
		if (tier === 'UNSTOPPABLE' || tier === 'GODLIKE' || tier === 'LEGENDARY') {
			if (!this.killStreakGlow) {
				this.killStreakGlow = new Graphics();
				// Add behind flames
				this.container.addChildAt(this.killStreakGlow, 0);
			}
		}

		// LEGENDARY (10+): Add golden aura and particle storm
		if (tier === 'LEGENDARY') {
			if (!this.killStreakAura) {
				this.killStreakAura = new Graphics();
				// Add at the very back
				this.container.addChildAt(this.killStreakAura, 0);
			}
		}
	}

	/**
	 * Remove all kill streak visual effects
	 */
	private removeKillStreakEffects(): void {
		if (this.killStreakFlames) {
			this.container.removeChild(this.killStreakFlames);
			this.killStreakFlames.destroy();
			this.killStreakFlames = null;
		}

		if (this.killStreakGlow) {
			this.container.removeChild(this.killStreakGlow);
			this.killStreakGlow.destroy();
			this.killStreakGlow = null;
		}

		if (this.killStreakAura) {
			this.container.removeChild(this.killStreakAura);
			this.killStreakAura.destroy();
			this.killStreakAura = null;
		}

		this.killStreakParticles = [];
	}

	/**
	 * Update kill streak visual effects (called each frame when streak is active)
	 */
	private updateKillStreakEffects(dt: number): void {
		if (!this.killStreakTier) return;

		const tier = this.killStreakTier;

		// Draw flame trail (all tiers)
		if (this.killStreakFlames) {
			this.drawKillStreakFlames(tier);
		}

		// Draw pulsing glow (UNSTOPPABLE+)
		if (this.killStreakGlow && (tier === 'UNSTOPPABLE' || tier === 'GODLIKE' || tier === 'LEGENDARY')) {
			this.drawKillStreakGlow(tier);
		}

		// Draw golden aura and particle storm (LEGENDARY only)
		if (tier === 'LEGENDARY') {
			if (this.killStreakAura) {
				this.drawKillStreakAura();
			}
			this.updateKillStreakParticles(dt);
		}
	}

	/**
	 * Draw flame trail effect behind worm body
	 * Color varies by tier: orange -> red-orange -> gold -> magenta
	 */
	private drawKillStreakFlames(tier: string): void {
		if (!this.killStreakFlames) return;

		const g = this.killStreakFlames;
		g.clear();

		// Get flame color based on tier
		let flameColor = WormRenderer.STREAK_RAMPAGE;
		let intensity = 0.6;
		if (tier === 'UNSTOPPABLE') {
			flameColor = WormRenderer.STREAK_UNSTOPPABLE;
			intensity = 0.7;
		} else if (tier === 'GODLIKE') {
			flameColor = WormRenderer.STREAK_GODLIKE;
			intensity = 0.8;
		} else if (tier === 'LEGENDARY') {
			flameColor = WormRenderer.STREAK_LEGENDARY;
			intensity = 0.9;
		}

		// Pulsing animation
		const pulse = 0.8 + Math.sin(performance.now() / 100) * 0.2;
		const fastPulse = 0.7 + Math.sin(performance.now() / 50) * 0.3;

		// Draw flames along body (more segments for higher tiers)
		const segmentCount = this.bodyPoints.length;
		let flameSegments = Math.min(15, segmentCount);
		if (tier === 'UNSTOPPABLE') flameSegments = Math.min(25, segmentCount);
		if (tier === 'GODLIKE') flameSegments = Math.min(35, segmentCount);
		if (tier === 'LEGENDARY') flameSegments = Math.min(50, segmentCount);

		for (let i = 0; i < flameSegments; i += 2) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			// Per-segment culling
			const segRadius = (this.segmentRadii[i] ?? this.config.radius);
			if (this.cullingBounds) {
				const checkRadius = segRadius * 3;
				if (point.x + checkRadius < this.cullingBounds.left ||
					point.x - checkRadius > this.cullingBounds.right ||
					point.y + checkRadius < this.cullingBounds.top ||
					point.y - checkRadius > this.cullingBounds.bottom) {
					continue;
				}
			}

			// Flame falloff from head to tail
			const falloff = 1 - (i / flameSegments);
			const alpha = intensity * falloff * pulse * 0.5;

			// Flickering flame shape (multiple overlapping circles with offset)
			const flicker1 = Math.sin(performance.now() / 80 + i * 0.5) * segRadius * 0.3;
			const flicker2 = Math.cos(performance.now() / 60 + i * 0.3) * segRadius * 0.2;

			// Outer flame glow
			const outerRadius = segRadius * (1.8 + falloff * 0.5) * pulse;
			g.circle(point.x + flicker1, point.y + flicker2, outerRadius);
			g.fill({ color: flameColor, alpha: alpha * 0.4 });

			// Inner flame core (brighter)
			const innerRadius = segRadius * (1.3 + falloff * 0.3) * fastPulse;
			g.circle(point.x + flicker2 * 0.5, point.y - flicker1 * 0.5, innerRadius);
			g.fill({ color: 0xffff00, alpha: alpha * 0.6 }); // Yellow core

			// Hot white center (head only)
			if (i < 4) {
				const coreRadius = segRadius * 1.1;
				g.circle(point.x, point.y, coreRadius);
				g.fill({ color: 0xffffff, alpha: alpha * 0.3 });
			}
		}
	}

	/**
	 * Draw pulsing glow effect around worm (UNSTOPPABLE+ tiers)
	 */
	private drawKillStreakGlow(tier: string): void {
		if (!this.killStreakGlow) return;

		const g = this.killStreakGlow;
		g.clear();

		// Get glow color and intensity based on tier
		let glowColor = WormRenderer.STREAK_UNSTOPPABLE;
		let glowIntensity = 0.4;
		if (tier === 'GODLIKE') {
			glowColor = WormRenderer.STREAK_GODLIKE;
			glowIntensity = 0.5;
		} else if (tier === 'LEGENDARY') {
			glowColor = WormRenderer.STREAK_LEGENDARY;
			glowIntensity = 0.6;
		}

		// Strong pulsing effect
		const pulse = 0.6 + Math.sin(performance.now() / 150) * 0.4;
		const slowPulse = 0.8 + Math.sin(performance.now() / 300) * 0.2;

		// Draw expanding/contracting glow rings around head
		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		const headRadius = this.segmentRadii[0] ?? this.config.radius;

		// Multiple glow rings with different phases
		const rings = tier === 'LEGENDARY' ? 4 : tier === 'GODLIKE' ? 3 : 2;
		for (let r = 0; r < rings; r++) {
			const phase = (performance.now() / 200 + r * 0.5) % (Math.PI * 2);
			const ringPulse = 0.8 + Math.sin(phase) * 0.3;
			const ringRadius = headRadius * (2 + r * 0.8) * ringPulse * slowPulse;
			const ringAlpha = glowIntensity * (1 - r * 0.2) * pulse;

			g.circle(headPoint.x, headPoint.y, ringRadius);
			g.fill({ color: glowColor, alpha: ringAlpha * 0.3 });
		}

		// Body glow trail (thinner than flames)
		const glowSegments = Math.min(20, this.bodyPoints.length);
		for (let i = 0; i < glowSegments; i += 3) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			const segRadius = this.segmentRadii[i] ?? this.config.radius;
			const falloff = 1 - (i / glowSegments);
			const alpha = glowIntensity * falloff * pulse * 0.25;

			g.circle(point.x, point.y, segRadius * 2.5 * slowPulse);
			g.fill({ color: glowColor, alpha });
		}
	}

	/**
	 * Draw golden aura effect for LEGENDARY tier (10+ kills)
	 */
	private drawKillStreakAura(): void {
		if (!this.killStreakAura) return;

		const g = this.killStreakAura;
		g.clear();

		// Majestic golden aura with rainbow shimmer
		const pulse = 0.7 + Math.sin(performance.now() / 200) * 0.3;
		const shimmerPhase = (performance.now() / 100) % (Math.PI * 2);

		// Rainbow color cycling for legendary effect
		const hue = (performance.now() / 20) % 360;
		const shimmerColor = this.hslToHex(hue, 100, 50);

		// Draw aura around entire body
		const auraSegments = Math.min(40, this.bodyPoints.length);
		for (let i = 0; i < auraSegments; i += 2) {
			const point = this.bodyPoints[i];
			if (!point) continue;

			const segRadius = this.segmentRadii[i] ?? this.config.radius;
			const falloff = 1 - (i / auraSegments);

			// Golden outer aura
			const auraRadius = segRadius * (3.5 + Math.sin(shimmerPhase + i * 0.2) * 0.5) * pulse;
			g.circle(point.x, point.y, auraRadius);
			g.fill({ color: WormRenderer.STREAK_GODLIKE, alpha: 0.15 * falloff * pulse });

			// Rainbow shimmer layer
			const shimmerRadius = segRadius * (2.5 + Math.cos(shimmerPhase + i * 0.3) * 0.3);
			g.circle(point.x, point.y, shimmerRadius);
			g.fill({ color: shimmerColor, alpha: 0.1 * falloff * pulse });
		}
	}

	/**
	 * Update particle storm for LEGENDARY tier
	 */
	private updateKillStreakParticles(dt: number): void {
		const dtMs = dt * 1000;
		const headPoint = this.bodyPoints[0];
		if (!headPoint) return;

		// Spawn new particles
		const spawnRate = 0.5; // 50% chance per frame
		if (Math.random() < spawnRate) {
			// Spawn from random body segment
			const spawnIndex = Math.floor(Math.random() * Math.min(20, this.bodyPoints.length));
			const spawnPoint = this.bodyPoints[spawnIndex];
			if (spawnPoint) {
				const radius = this.segmentRadii[spawnIndex] ?? this.config.radius;

				// Particle colors: gold, white, magenta
				const colors = [WormRenderer.STREAK_GODLIKE, 0xffffff, WormRenderer.STREAK_LEGENDARY];
				const color = colors[Math.floor(Math.random() * colors.length)]!;

				this.killStreakParticles.push({
					x: spawnPoint.x + (Math.random() - 0.5) * radius * 2,
					y: spawnPoint.y + (Math.random() - 0.5) * radius * 2,
					vx: (Math.random() - 0.5) * 60,
					vy: (Math.random() - 0.5) * 60 - 30, // Slight upward drift
					life: 0,
					maxLife: 600 + Math.random() * 400,
					size: 2 + Math.random() * 5,
					color,
				});
			}
		}

		// Limit particle count
		const maxParticles = 60;
		if (this.killStreakParticles.length > maxParticles) {
			this.killStreakParticles.splice(0, this.killStreakParticles.length - maxParticles);
		}

		// Update particles
		for (let i = this.killStreakParticles.length - 1; i >= 0; i--) {
			const p = this.killStreakParticles[i]!;
			p.life += dtMs;

			// Physics
			p.x += p.vx * dt;
			p.y += p.vy * dt;

			// Slow down
			p.vx *= 0.97;
			p.vy *= 0.97;

			// Remove dead particles
			if (p.life >= p.maxLife) {
				this.killStreakParticles.splice(i, 1);
			}
		}

		// Draw particles (use flames graphics if no dedicated particle graphics)
		if (this.killStreakFlames) {
			const g = this.killStreakFlames;
			// Don't clear - flames already drew, we add particles on top
			for (const p of this.killStreakParticles) {
				const alpha = 0.8 * (1 - p.life / p.maxLife);
				const size = p.size * (1 - p.life / p.maxLife * 0.5);

				// Sparkle effect
				const sparkle = Math.sin(p.life / 50) * 0.5 + 0.5;
				g.circle(p.x, p.y, size * (1 + sparkle * 0.3));
				g.fill({ color: p.color, alpha });
			}
		}
	}

	/**
	 * Convert HSL to hex color
	 */
	private hslToHex(h: number, s: number, l: number): number {
		s /= 100;
		l /= 100;
		const a = s * Math.min(l, 1 - l);
		const f = (n: number) => {
			const k = (n + h / 30) % 12;
			const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
			return Math.round(255 * color);
		};
		return (f(0) << 16) | (f(8) << 8) | f(4);
	}

	/**
	 * Reset the renderer for reuse from object pool
	 * Reinitializes all state with new configuration
	 */
	reset(config: Partial<WormRendererConfig>): void {
		// Clear existing effects
		this.removeLocalPlayerEffects();
		this.removeSameSkinIndicator();
		this.removeLeviathanEffects();
		this.removeBountyCrown();
		this.removeKillStreakEffects();
		this.removeSprintEffects();
		this.removeEyebrows();
		if (this.accessoryGraphics) {
			this.container.removeChild(this.accessoryGraphics);
			this.accessoryGraphics.destroy();
			this.accessoryGraphics = null;
		}

		// Clear graphics
		this.bodySegments.clear();
		this.bodySegmentsBuffer.clear();
		this.headGraphics.clear();
		this.leftEye.clear();
		this.rightEye.clear();

		// Reset config
		this.config = { ...DEFAULT_CONFIG, ...config };

		// Update skin data
		if (config.skin) {
			this.skinData = config.skin;
			if (!config.color && config.skin.primaryColor) {
				this.config.color = hexToNumber(config.skin.primaryColor);
			}
		} else {
			this.skinData = null;
		}

		// Reset state variables
		this.isLocalPlayer = false;
		this.hasSameSkin = false;
		this.isLegendary = false;
		this.leaderboardRank = 0;
		this.killStreakTier = '';
		this.isBoosting = false;
		this.sprintIntensity = 0;
		this.animationTime = 0;
		this.headRotation = 0;
		this.prevHeadRotation = 0;
		this.currentTurnRate = 0;
		this.lodLevel = 0;
		this.bodyDirty = true;
		this.lastHeadRadius = 0;
		this.timeSinceBodyRedraw = 0;
		this.eyeAnimationFrame = 0;
		this.activeBodyBuffer = 0;

		// Reset path following
		this.pathTotalDistance = 0;
		this.usePathFollowing = true;

		// Reset eye state
		this.pupilOffsetX = 0.3;
		this.pupilOffsetY = 0;
		this.targetPupilOffsetX = 0.3;
		this.targetPupilOffsetY = 0;
		this.lookAroundTimer = 0;
		this.nextLookChange = 2000;

		// Reset expression
		this.currentExpression = EyeExpression.NORMAL;
		this.targetExpression = EyeExpression.NORMAL;
		this.expressionTransition = 1;
		this._expressionState = { sprintDuration: 0, score: 0, hasPowerup: false };

		// Reset death animation
		this.deathAnimation = {
			active: false,
			elapsed: 0,
			duration: 1000,
			onComplete: null,
		};

		// Clear pending growth
		this.pendingGrowth = 0;

		// Reset cached colors
		this.cachedColors = [];
		this.colorCacheTime = 0;

		// Reset accessory
		this.accessoryType = AccessoryType.NONE;

		// Clear and reinitialize leviathan particles
		this.leviathanParticleData = [];
		this.speedLineParticles = [];

		// Reset culling bounds
		this.cullingBounds = null;

		// Reinitialize body points - body extends BEHIND the head
		this.bodyPoints = [];
		this.segmentVelocities = [];
		this.segmentRotations = [];
		this.segmentGrowthProgress = [];
		this.segmentRadii = [];
		for (let i = 0; i < this.config.segmentCount; i++) {
			// Body extends BEHIND the head (negative x direction for worm facing right)
			this.bodyPoints.push(new Point(-i * this.config.segmentSpacing, 0));
			this.segmentVelocities.push({ vx: 0, vy: 0 });
			this.segmentRotations.push(0);
			this.segmentGrowthProgress.push(1.0);
		}

		// Reinitialize path history
		this.pathHistory.clear();
		this.initializePathHistory();

		// Redraw head and eyes
		this.drawHead();
		if (this.config.showEyes) {
			this.drawEyes();
		}

		// Force body redraw
		this.forceBodyRedraw();
	}

	/**
	 * Clean up resources
	 */
	destroy(): void {
		this.bodySegments.destroy();
		this.bodySegmentsBuffer.destroy();
		this.headGraphics.destroy();
		this.leftEye.destroy();
		this.rightEye.destroy();
		this.removeLocalPlayerEffects();
		this.removeSameSkinIndicator();
		this.removeLeviathanEffects();
		this.removeBountyCrown();
		this.removeKillStreakEffects();
		this.removeSprintEffects();
		this.removeEyebrows();
		this.container.destroy({ children: true });
	}

	/**
	 * Remove eyebrow graphics
	 */
	private removeEyebrows(): void {
		if (this.leftEyebrow) {
			this.container.removeChild(this.leftEyebrow);
			this.leftEyebrow.destroy();
			this.leftEyebrow = null;
		}
		if (this.rightEyebrow) {
			this.container.removeChild(this.rightEyebrow);
			this.rightEyebrow.destroy();
			this.rightEyebrow = null;
		}
	}

	/**
	 * Remove sprint visual effects (glow and speed lines)
	 */
	private removeSprintEffects(): void {
		if (this.sprintGlow) {
			this.container.removeChild(this.sprintGlow);
			this.sprintGlow.destroy();
			this.sprintGlow = null;
		}

		if (this.speedLines) {
			this.container.removeChild(this.speedLines);
			this.speedLines.destroy();
			this.speedLines = null;
		}

		this.speedLineParticles = [];
		this.sprintIntensity = 0;
	}
}
